<script module>
  // Pages edited in this tab. Written by an effect, so only ever in the browser: a block that
  // mounts again after a save or a cancel must not play the page intro a second time.
  const edited_pages = new Set();
</script>

<script>
  import TextField from '#lib/TextField.svelte';

  import {
    determine_orientation,
    render_micro_markdown,
    find_closest_aspect_ratio_for_brand,
    get_visibility_icon,
    entry_url,
    ACCEPTED_IMAGE_FILE_TYPES
  } from '#lib/util.js';
  import { add_media, get_jobs, retry as retry_upload, remove as remove_upload } from '#lib/upload_manager.svelte.js';
  import { TITLE_LEVEL_PARAGRAPH } from '#lib/StoryBlockModel.svelte.js';
  import { story_layouts, get_default_block_spacing, get_layout_orientation, has_fullscreen_previews, get_effective_track, get_effective_track_align, has_auto_advancing_carousel } from '#lib/StoryLayouts.js';
  // import BlockFocusTrap from '#lib/BlockFocusTrap.svelte';
  import { page } from '$app/state';
  import ZoomableImage from '#lib/ZoomableImage.svelte';
  import ButtonLabel from '#lib/ButtonLabel.svelte';
  import Icon from '#lib/Icon.svelte';
  import Popover from '#lib/Popover.svelte';
  import { lazy } from '#lib/lazy.svelte.js';
  import t from '#lib/i18n.svelte.js';
  import inview from '#lib/inview.js';
  import carousel_auto_advance from '#lib/carousel_auto_advance.js';
  import Image from '#lib/Image.svelte';
  import ProgramRow from '#lib/ProgramRow.svelte';
  import CalendarRegular from '#lib/CalendarRegular.svelte';
  import CategoryLabel from '#lib/CategoryLabel.svelte';
  import { get_entry_category } from '#lib/entry_category.js';

  import { getContext } from 'svelte';
  // Quick hack to set the button styles for the links
  // TODO: find a more elegant solution and remove this from here
  let site_settings = getContext('site_settings');
  let entry_api = getContext('entry_api');

  let {
    block,
    is_editing,
    ref = $bindable(),
  } = $props();

  let link_editor_ref = $state({
    primary: null,
    secondary: null,
  });
  let link_container_ref = $state({
    primary: null,
    secondary: null,
  });

  // const layout = {
  //   primary_field: 'image',    
  // }

  // TODOS: 
  // 1. Introduce constraints (recommendations) for layout selection
  //    → e.g. max_images = 2
  //    → e.g. max_title_length = 100 characters

  /** When preamble uses uppercase, .-caps in body text should inherit preamble typography */
  let preamble_is_uppercase = $derived(site_settings.typography?.styles?.preamble?.letter_case === 'uppercase');
  /**
   * A description short enough to sit on one line (or none at all). Layouts that set the
   * description apart from the title may align such a line to the far edge; a paragraph
   * keeps its reading edge. Measured on the visible text, so link targets do not count.
   */
  const SHORT_DESCRIPTION_MAX_CHARACTERS = 40;
  let description_is_short = $derived.by(() => {
    const visible = (block.description ?? '').replace(/\[([^\]]*)\]\([^)]*\)/g, '$1').trim();
    return visible.length <= SHORT_DESCRIPTION_MAX_CHARACTERS && !visible.includes('\n');
  });

  // No icons on the canvas: the placeholders now name each field, so the badges
  // were saying the same thing twice while sitting on top of the layout.
  let text_fields = $derived([
      { field_name: 'preamble', text_variant: 'preamble', placeholder: t('placeholder_preamble') },
    block.title_level !== 0 ?
      { field_name: 'title', text_variant: block.title_level === TITLE_LEVEL_PARAGRAPH ? 'body' : `heading${block.title_level}`, placeholder: t('placeholder_title') } : null,
      { field_name: 'description', text_variant: 'body', placeholder: t('placeholder_description') },
  ]
    .filter(Boolean)
    .filter(field => (is_editing && block[`show_${field.field_name}`]) || (block[field.field_name] && block[`show_${field.field_name}`]))
  );

  let show_links = $derived((is_editing && block.show_links) || (block.show_links && (block.primary_link_href || block.secondary_link_href)));

  // Calculate the number of TextField components
  let text_fields_count = $derived.by(() => {
    let count = text_fields.length;
    // plus one if links are active
    if(show_links){
      count++
    }
    return count
  });

  /**
   * Pinned ratios win. Collage layouts otherwise keep each image's own ratio;
   * other layouts use the closest brand ratio to the preview images' average.
   */
  let common_aspect_ratio = $derived.by(() => {
    if (block.aspect_ratio) return block.aspect_ratio;
    if (layout_config?.preserve_preview_aspect_ratio) return undefined;
    const aspect_ratios = (block.entry_previews || [])
      .map(entry_preview => entry_preview.images[0]?.width / entry_preview.images[0]?.height)
      .filter(ratio => Number.isFinite(ratio) && ratio > 0);
    return find_closest_aspect_ratio_for_brand(aspect_ratios, site_settings.aspect_ratios);
  });

  /** Per-placement presentation overrides for one slot (sparse; absent = entry defaults) */
  function slot_settings_for(entry_preview) {
    return block.entry_preview_settings?.[String(entry_preview.entry_id)];
  }

  let layout_config = $derived(story_layouts.find(layout => layout.value === block.layout));

  // Editing-only, behind a popover / a sticker click: split out of the reader bundle and
  // fetched as soon as edit mode starts, so they are here before anyone can open them
  const link_editor = lazy(() => import('#lib/LinkEditor.svelte'));
  const sticker_editor = lazy(() => import('#lib/StickerEditor.svelte'));
  $effect(() => {
    if (is_editing) { link_editor.load(); sticker_editor.load(); }
  });
  let is_auto_carousel = $derived(has_auto_advancing_carousel(layout_config) && block.entry_previews.length > 1 && !is_editing);
  /** Width of the block: the block's own track when the layout allows it, else the layout's */
  let effective_track = $derived(get_effective_track(block));
  /** Where that width sits on the page; always 'center' once the track leaves no slack */
  let effective_track_align = $derived(get_effective_track_align(block));
  
  // Layout orientation for entry preview move buttons (horizontal = left/right, vertical = up/down)
  let layout_orientation = $derived(get_layout_orientation(layout_config));
  
  // Check if this layout has fullscreen entry previews (for unified toolbar)
  let is_fullscreen_layout = $derived(has_fullscreen_previews(layout_config));
  
  // Track hovered preview index for unified toolbar (all layouts with entry previews)
  let hovered_preview_index = $state(null);
  
  // Generate unique anchor name for the currently hovered preview card
  let active_preview_anchor = $derived(
    hovered_preview_index !== null 
      ? `--preview-${block.block_id}-${hovered_preview_index}` 
      : null
  );

  // One anchor name per preview card, in card order. Block.svelte pins a persistent
  // "more actions" button to each of them; the naming lives here because the cards
  // themselves set the matching anchor-name below.
  let preview_anchors = $derived(
    (block.entry_previews || []).map((_, i) => `--preview-${block.block_id}-${i}`)
  );
  
  // Export methods and state for parent Block.svelte to access via ref
  // Note: We assign values directly (not getters) so the ref object changes when values change,
  // which triggers reactivity in Block.svelte's $derived expressions
  $effect(() => {
    ref = {
      move_entry_preview,
      remove_entry_preview,
      hovered_preview_index,
      active_preview_anchor,
      preview_anchors,
    };
  });

  /**
   * Move an entry preview in the multiref array.
   * Updates both multiref and entry_previews locally to avoid a redundant network fetch.
   * @param {number} from_index
   * @param {number} to_index
   */
  function move_entry_preview(from_index, to_index) {
    if (to_index < 0 || to_index >= block.entry_previews.length) return;

    const multiref = [...(block.entry_previews_multiref || [])];
    const [moved_ref] = multiref.splice(from_index, 1);
    multiref.splice(to_index, 0, moved_ref);

    const previews = [...(block.entry_previews || [])];
    const [moved_preview] = previews.splice(from_index, 1);
    previews.splice(to_index, 0, moved_preview);

    block.entry_previews_multiref = multiref;
    block.entry_previews = previews;
  }

  /**
   * Remove an entry preview from the multiref array.
   * Updates both multiref and entry_previews locally to avoid a redundant network fetch.
   * @param {number} index
   */
  function remove_entry_preview(index) {
    block.entry_previews_multiref = (block.entry_previews_multiref || []).filter((_, i) => i !== index);
    block.entry_previews = (block.entry_previews || []).filter((_, i) => i !== index);
  }

  // Drag-and-drop state for entry previews
  let dragging_index = $state(null);
  let drop_target_index = $state(null);
  let drop_position = $state(null); // 'before' or 'after'

  /**
   * Handle drag start on preview card
   * @param {DragEvent} event
   * @param {number} index
   */
  function handle_drag_start(event, index) {
    if (!is_editing) return;
    
    const target = event.currentTarget;
    
    // Temporarily remove outline for clean drag image
    const original_outline = target.style.outline;
    target.style.outline = 'none';
    
    const rect = target.getBoundingClientRect();
    event.dataTransfer.setDragImage(target, event.clientX - rect.left, event.clientY - rect.top);
    
    // Restore outline after drag image is captured
    requestAnimationFrame(() => {
      target.style.outline = original_outline;
    });
    
    dragging_index = index;
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.dropEffect = 'move';
    // Store the block_id to ensure we only accept drops from the same block
    event.dataTransfer.setData('application/x-storyblock-entry', `${block.block_id}:${index}`);
  }

  /**
   * Handle drag over preview card
   * @param {DragEvent} event
   * @param {number} index
   */
  function handle_drag_over(event, index) {
    if (!is_editing || dragging_index === null) return;
    
    // Verify this is a valid drag from our block
    const data = event.dataTransfer.types.includes('application/x-storyblock-entry');
    if (!data) return;
    
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    
    const rect = event.currentTarget.getBoundingClientRect();
    const is_horizontal = layout_orientation === 'horizontal';
    
    if (is_horizontal) {
      const mid = rect.left + rect.width / 2;
      drop_position = event.clientX < mid ? 'before' : 'after';
    } else {
      const mid = rect.top + rect.height / 2;
      drop_position = event.clientY < mid ? 'before' : 'after';
    }
    
    drop_target_index = index;
  }

  /**
   * Handle drag leave
   * @param {DragEvent} event
   */
  function handle_drag_leave(event) {
    // Only clear if actually leaving the element, not entering a child
    const related = event.relatedTarget;
    if (related && event.currentTarget.contains(related)) return;
    
    drop_target_index = null;
    drop_position = null;
  }

  /**
   * Handle drop on preview card
   * @param {DragEvent} event
   * @param {number} target_index
   */
  function handle_drop(event, target_index) {
    event.preventDefault();
    
    // Verify this is a valid drop from our block
    const data = event.dataTransfer.getData('application/x-storyblock-entry');
    if (!data || !data.startsWith(`${block.block_id}:`)) {
      handle_drag_end();
      return;
    }
    
    if (dragging_index === null) return;
    
    let new_index = target_index;
    if (drop_position === 'after') {
      new_index = target_index + 1;
    }
    
    // Adjust for the removal of the dragged item
    if (dragging_index < new_index) {
      new_index -= 1;
    }
    
    if (dragging_index !== new_index) {
      move_entry_preview(dragging_index, new_index);
    }
    
    handle_drag_end();
  }

  /**
   * Handle drag end (cleanup)
   */
  function handle_drag_end() {
    dragging_index = null;
    drop_target_index = null;
    drop_position = null;
  }

  /*
    Safari skips the content of a closed <details> (content-visibility: hidden) when a web font
    finishes loading, so an answer keeps the layout it got with the fallback font. Opened later,
    it paints in the web font with the fallback's widths: link pills came out narrower than their
    labels, the arrow sticking out over the border. Rebuilding the answer's boxes on open lays it
    out again with the font that is there now.
  */
  function relayout_opened_answer(event) {
    const details = event.currentTarget;
    const answer = details.open && details.querySelector(':scope > .faq-answer');
    if (!answer) return;
    answer.style.display = 'none';
    answer.offsetWidth; // apply the removal - restoring display in the same style pass would be a no-op
    answer.style.removeProperty('display');
  }

  // ——— File drop: dropping images onto the block uploads them, creates media
  // entries (the commitment model — media commits at upload completion) and
  // appends them to this block. Placeholders render inline while in flight.
  let file_drop_active = $state(false);

  /** Upload jobs targeted at this block that don't have a rendered preview card yet.
      The placeholder hides the moment the real preview card renders — even while
      still optimizing — otherwise the image shows twice until optimization ends. */
  let pending_media_jobs = $derived(
    get_jobs().filter(job =>
      job.target === block.block_id &&
      !(job.entry_id && (block.entry_previews || []).some(p => p.entry_id === job.entry_id))
    )
  );

  function is_file_drag(event) {
    return is_editing
      && page.data.current_user?.role === 'admin'
      && Array.from(event.dataTransfer?.types || []).includes('Files');
  }

  function handle_block_file_dragover(event) {
    if (!is_file_drag(event)) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'copy';
    file_drop_active = true;
  }

  function handle_block_file_dragleave(event) {
    if (event.relatedTarget && event.currentTarget.contains(event.relatedTarget)) return;
    file_drop_active = false;
  }

  function handle_block_file_drop(event) {
    if (!is_file_drag(event)) return;
    event.preventDefault();
    event.stopPropagation(); // the window-level fallback must not double-handle this drop
    file_drop_active = false;
    const files = Array.from(event.dataTransfer.files || [])
      .filter(file => ACCEPTED_IMAGE_FILE_TYPES.includes(file.type));
    if (files.length === 0) return;
    add_media(files, {
      target: block.block_id,
      on_entry: (entry) => {
        block.entry_previews_multiref = [...(block.entry_previews_multiref || []), entry.entry_id];
      },
    });
  }

  // Effective block spacing: user override or layout default
  // Supports symmetric mode (both use block_spacing) and independent mode (bottom uses block_spacing_bottom)
  let default_block_spacing = $derived(get_default_block_spacing(layout_config));
  let effective_block_spacing_top = $derived(block.block_spacing ?? default_block_spacing);
  let effective_block_spacing_bottom = $derived(block.block_spacing_bottom ?? block.block_spacing ?? default_block_spacing);

  const CAROUSEL_SPLIT_LAYOUTS = [111,113,114,115,117,118,119,120];
  const FULLSCREEN_SCROLL_LAYOUTS = [301,311];
  const disable_zoom_for_layout_variants = [4,5,6,7,9,20,27,28,64,67,72,73,74,77,78,79,81,82,83,85,86,88,89,90,91,92,93,95,96,98,99,100,101,102,103,104,105,106,107,108,109,144,...CAROUSEL_SPLIT_LAYOUTS,...FULLSCREEN_SCROLL_LAYOUTS];
  const layout_with_full_screen_images = [4,13,20];
  const layout_with_full_height_images = [4,5,28,70,71,67,72,73,74,78,79,81,82,83,85,86,88,89,90,91,92,93,95,96,98,99,100,101,102,103,104,105,106,107,108,109,...CAROUSEL_SPLIT_LAYOUTS,...FULLSCREEN_SCROLL_LAYOUTS];
  const layout_preview_image_count = {
    25: 3,
    68: 2
  };
  const layouts_where_images_link_to_primary_link = [76];
  const FAQ_LAYOUTS = [201, 202, 203, 204, 205, 206, 207, 208];
  let is_faq_layout = $derived(FAQ_LAYOUTS.includes(block.layout));

  /*
    Text settle-in (styles: TEXT SETTLE-IN). Fields that start out below the window are held
    back - invisible, a little low - and rise in on a clock once they come into view, a step
    apart when several arrive together. Fields already on screen when the page appears are
    never touched, so nothing visible blinks out, and without JS everything simply shows.

    Played on a clock, not linked to the scroll: tied to the scroll, a rise is over within one
    turn of the wheel and nobody sees it. Classified by an IntersectionObserver, not by reading
    positions here: one read per block, each after the previous block's writes, forced a full
    layout per block at mount. Only data- attributes change, the cheap path for restyling.
  */
  /*
    Page intro (styles: PAGE INTRO). The texts on screen when a page appears rise in, the title
    reading in word by word. Pure CSS from the first paint, so nothing waits for this script -
    which only takes the class away for good once editing starts: leaving edit mode must not
    replay it.
  */
  // svelte-ignore state_referenced_locally (how the page first appears decides)
  let intro = $state(!is_editing && !edited_pages.has(page.url.pathname));
  $effect(() => {
    if (!is_editing) return;
    intro = false;
    edited_pages.add(page.url.pathname);
  });

  const SETTLE_GROUPS = ['single-column', 'two-column', 'grid', 'editorial'];
  let settles_text = $derived(SETTLE_GROUPS.includes(layout_config?.group) || is_faq_layout);
  // Not `ref`: that prop is handed back to Block.svelte as an object of methods
  let text_fields_element = $state();
  $effect(() => {
    if (!text_fields_element || is_editing || !settles_text || !(site_settings.typography.animation_intensity > 0)) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const fields = text_fields_element.querySelectorAll(':scope > .field');
    if (fields.length === 0) return;
    const held = new Set();
    let first_report = true;
    function settle(entries, observer) {
      let order = 0;
      for (const { target, isIntersecting, intersectionRatio, boundingClientRect } of entries) {
        if (observer === crossing && first_report) {
          // Hold back only what is still below the window; whatever shows, even in the band
          // under the trigger line, is left alone
          if (!isIntersecting && boundingClientRect.top > innerHeight) {
            held.add(target);
            target.dataset.settle = 'held';
          } else {
            crossing.unobserve(target);
            whole.unobserve(target);
          }
          continue;
        }
        const arrived = observer === crossing ? isIntersecting : intersectionRatio >= 1;
        if (arrived && held.has(target)) {
          held.delete(target);
          target.style.setProperty('--settle-order', String(order++));
          target.dataset.settle = 'in';
          crossing.unobserve(target);
          whole.unobserve(target);
        }
      }
      if (observer === crossing) first_report = false;
    }
    // In once its top passes 88% of the window ...
    const crossing = new IntersectionObserver(settle, { rootMargin: '0px 0px -12% 0px' });
    // ... or once it is whole on screen: near the end of a page a field may never scroll up
    // as far as that line, and must not stay invisible
    const whole = new IntersectionObserver(settle, { threshold: 1 });
    for (const field of fields) {
      crossing.observe(field);
      whole.observe(field);
    }
    return () => {
      crossing.disconnect();
      whole.disconnect();
      for (const field of fields) {
        delete field.dataset.settle;
        field.style.removeProperty('--settle-order');
      }
    };
  });

  /*
    FAQ text column (styles: FAQ Layout). It stays pinned under the menu while the questions
    scroll past - only when they run on well beyond it: a quarter of the window at least. Next to
    a list barely taller than the text, the column moved a few pixels against it and stopped,
    which looked broken. Measured on the closed questions (their summaries), so opening or
    closing an answer never pins or releases the column while someone is reading.
  */
  let faq_list_element = $state();
  let faq_text_pinned = $state(false);
  $effect(() => {
    if (!text_fields_element || !faq_list_element) return;
    function measure() {
      let questions_height = 0;
      for (const summary of faq_list_element.querySelectorAll(':scope > .faq-item > .faq-summary')) {
        questions_height += summary.offsetHeight;
      }
      faq_text_pinned = questions_height - text_fields_element.offsetHeight >= innerHeight / 4;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(text_fields_element);
    observer.observe(faq_list_element);
    addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      removeEventListener('resize', measure);
    };
  });

  /*
    Program (148): event previews as rows. Visitors see only events that have not ended yet,
    earliest first, so a homepage program does not go stale between edits. Editors see every
    entry in the stored order (that is the order drag and drop works on), past ones dimmed.
  */
  const PROGRAM_LAYOUT = 148;
  let is_program_layout = $derived(block.layout === PROGRAM_LAYOUT);
  // Regular dates (149): the calendar's regular series as a weekly plan, resolved by the server
  const REGULAR_DATES_LAYOUT = 149;
  let is_regular_dates_layout = $derived(block.layout === REGULAR_DATES_LAYOUT);
  const program_now = new Date();
  function program_end(entry_preview) {
    return new Date(entry_preview.ends_at || entry_preview.starts_at);
  }
  function is_past_program_entry(entry_preview) {
    return entry_preview.type === 'event' && entry_preview.starts_at && program_end(entry_preview) < program_now;
  }
  let program_rows = $derived.by(() => {
    if (!is_program_layout) return [];
    // Automatic: the server already chose what is on and coming up, in order
    if (block.program_automatic) {
      return (block.program_entry_previews ?? []).map((entry_preview, index) => ({ entry_preview, index }));
    }
    const rows = (block.entry_previews ?? []).map((entry_preview, index) => ({ entry_preview, index }));
    if (is_editing) return rows;
    const events = rows
      .filter(({ entry_preview }) => entry_preview.type === 'event' && entry_preview.starts_at && !is_past_program_entry(entry_preview))
      .sort((a, b) => new Date(a.entry_preview.starts_at).getTime() - new Date(b.entry_preview.starts_at).getTime());
    const others = rows.filter(({ entry_preview }) => entry_preview.type !== 'event' || !entry_preview.starts_at);
    return [...events, ...others];
  });

  // Sticker editing state
  let selected_sticker_id = $state(null);
  /** @type {Object.<string, HTMLElement>} sticker id -> element (popover anchor) */
  let sticker_elements = $state({});
  let selected_sticker = $derived(block.stickers?.find(s => s.id === selected_sticker_id));

  /**
   * Resolve the anchor name for a sticker, normalizing invalid preview indices
   * (e.g. after a preview was removed) to the closest valid one.
   * NOTE: 'text' only works in layouts where .text-fields is a real box
   * (many layouts use display: contents) — 'block' is the robust fallback.
   * @param {Object} sticker
   * @returns {string} CSS anchor name
   */
  function sticker_anchor_name(sticker) {
    if (sticker.anchor === 'block') return `--layout-${block.block_id}`;
    if (sticker.anchor === 'text') return `--text-${block.block_id}`;
    if (!(block.entry_previews?.length > 0)) return `--layout-${block.block_id}`;
    const index = Math.min(Math.max(0, sticker.anchor), block.entry_previews.length - 1);
    return `--preview-${block.block_id}-${index}`;
  }

</script>

{#snippet drop_indicator_line(orientation)}
  {#if dragging_index !== null && drop_target_index !== null && dragging_index !== drop_target_index}
    {@const is_after_last = drop_position === 'after' && drop_target_index === block.entry_previews.length - 1}
    {@const indicator_index = drop_position === 'after' && !is_after_last ? drop_target_index + 1 : drop_target_index}
    <div 
      class="drop-indicator {orientation}"
      class:before={!is_after_last}
      class:after={is_after_last}
      style:--target-anchor="--preview-{block.block_id}-{indicator_index}"
    ></div>
  {/if}
{/snippet}

{#if (block.has_content && block.is_visible(page.data.current_user)) || is_editing}
  <div
    bind:this={ref}
    class='story-layout variant-{block.layout}'
    class:group-single-column={layout_config?.group === 'single-column'}
    class:group-two-column={layout_config?.group === 'two-column'}
    class:group-grid={layout_config?.group === 'grid'}
    class:group-cover={layout_config?.group === 'cover'}
    class:group-bento={layout_config?.group === 'bento'}
    class:group-editorial={layout_config?.group === 'editorial'}
    class:group-special={layout_config?.group === 'special'}
    class:group-fullscreen-scroll={layout_config?.value === 301 || layout_config?.value === 311}
    class:inset-carousel={layout_config?.inset_carousel}
    class:track-inner={effective_track === 'inner'}
    class:track-extended-1={effective_track === 'extended-1'}
    class:track-extended-2={effective_track === 'extended-2'}
    class:track-flex={effective_track === 'flex'}
    class:track-full={effective_track === 'full'}
    class:track-align-left={effective_track_align === 'left'}
    class:track-align-right={effective_track_align === 'right'}
    class:align-left={layout_config?.alignment === 'left'}
    class:align-center={layout_config?.alignment === 'center'}
    class:align-right={layout_config?.alignment === 'right'}
    class:columns-2={layout_config?.columns === 2}
    class:columns-3={layout_config?.columns === 3}
    class:columns-4={layout_config?.columns === 4}
    class:columns-5={layout_config?.columns === 5}
    class:columns-6={layout_config?.columns === 6}
    class:vertical-top={layout_config?.vertical === 'top'}
    class:vertical-center={layout_config?.vertical === 'center'}
    class:vertical-bottom={layout_config?.vertical === 'bottom'}
    class:horizontal-left={layout_config?.horizontal === 'left'}
    class:horizontal-center={layout_config?.horizontal === 'center'}
    class:horizontal-right={layout_config?.horizontal === 'right'}
    class:has-text-fields={text_fields_count > 0}
    class:full-screen-images={layout_with_full_screen_images.includes(block.layout)}
    class:full-height-images={layout_with_full_height_images.includes(block.layout)}
    class:use-animations={site_settings.typography.animation_intensity > 0}
    class:intro
    class:faq-text-pinned={faq_text_pinned}
    class:preamble-is-uppercase={preamble_is_uppercase}
    class:short-description={description_is_short}
    class:spacing-top-0={effective_block_spacing_top === '0'}
    class:spacing-top-xs={effective_block_spacing_top === 'xs'}
    class:spacing-top-s={effective_block_spacing_top === 's'}
    class:spacing-top-m={effective_block_spacing_top === 'm'}
    class:spacing-top-l={effective_block_spacing_top === 'l'}
    class:spacing-bottom-0={effective_block_spacing_bottom === '0'}
    class:spacing-bottom-xs={effective_block_spacing_bottom === 'xs'}
    class:spacing-bottom-s={effective_block_spacing_bottom === 's'}
    class:spacing-bottom-m={effective_block_spacing_bottom === 'm'}
    class:spacing-bottom-l={effective_block_spacing_bottom === 'l'}
    style:--text-fields-count={text_fields_count}
    style:--animation-play-state={is_editing ? 'paused' : 'running'}
    style:--common-aspect-ratio={common_aspect_ratio}
    style:--block-spacing-top={effective_block_spacing_top === '0' ? '0px' : `var(--block-spacing-${effective_block_spacing_top})`}
    style:--block-spacing-bottom={effective_block_spacing_bottom === '0' ? '0px' : `var(--block-spacing-${effective_block_spacing_bottom})`}
    style:anchor-name={block.stickers?.length > 0 ? `--layout-${block.block_id}` : undefined}
    class:file-drop-active={file_drop_active}
    ondragover={handle_block_file_dragover}
    ondragleave={handle_block_file_dragleave}
    ondrop={handle_block_file_drop}
  >

  <!-- Zero-height span of the block's content track. Block.svelte measures this to place the
       width drag handles and to mask the selection stripes: the block element itself always
       spans the whole page grid, so both would otherwise sit on the page margins regardless
       of how wide the block actually is. Edit mode only - it is pure editing chrome. -->
  {#if is_editing}
    <div class='track-anchor' aria-hidden='true'></div>
  {/if}

  {#if text_fields.length > 0 || show_links}
    <hgroup bind:this={text_fields_element} class='text-fields' style:anchor-name={`--text-${block.block_id}`}>
      {#each text_fields as {field_name, text_variant, placeholder}, index}
        <TextField
          class='field {field_name} {index === 0 ? 'first' : ''} {text_fields.length - 1 === index && !show_links ? 'last' : ''}'
          {block}
          {is_editing}
          field_name={field_name}
          variant={text_variant}
          {placeholder}
        />
      {/each}

      {#if (show_links) }
        <div class='field {text_fields.length === 0 ? 'first' : ''} last links flex-row flex-wrap'>
          {#each ['primary','secondary'] as link_variant}
            {@const resolved_link = block[`${link_variant}_link`]}
            {#if (block[`${link_variant}_link_href`] && block[`${link_variant}_link_title`]) || is_editing}
              <!-- IMPORTANT: Sveltekit bug when using target="_self" -> on back navigation the page is empty and does a full reload -->
              <a 
                bind:this={link_container_ref[link_variant]}
                class='text link-container {link_variant}-link {resolved_link.type}' 
                {...!is_editing ? { href: resolved_link.href } : {}}
                {...(resolved_link.type === 'external' ? {target: '_blank'} : {})}
              >
                <!-- TODO: we need to get primary_control_surface and secondary_control_surface from site_settings here -->
                <!-- But it wouldn't be very efficient to query this for every Block -->
                <!-- Ideally we have a css class shortcut for "primary" and "secondary" -->
                <!-- Also we shouldn't ship all the css of the unused button styles -->
                <!-- So ideally we create the css for the primary, secondary and sytem primary/ secondary button server side -->
                <!-- But then again we don't have the flexibility to use the variants such as underline, ghosted, outline, etc. internally for the editing UI...  -->
                <!-- should maybe instead give rather more control over the look of a "stroke" in the broadest sense -->
                <ButtonLabel 
                  variant={'brand' + link_variant + ' ' + site_settings[`${link_variant}_control_surface`]}
                  align={site_settings.control_surface_align}
                  editable={is_editing}
                  bind:text={block[`${link_variant}_link_title`]}
                  field_name='{link_variant}_link_title'
                  placeholder={t(`placeholder_${link_variant}_link`)}
                  on_end_icon_click={() => {
                    link_editor_ref[link_variant].showPopover();
                    setTimeout(() => {
                      document.getElementById(`link-editor-${link_variant}`)?.focus();
                    }, 100);
                  }}
                />
              </a>
              <Popover 
                class='link-editor-container'
                bind:ref={link_editor_ref[link_variant]}
                placement='bottom'
                anchor={link_container_ref[link_variant]}
                style='min-width: 242px; height: 310px;'
                offset={8}
              >
                {#if is_editing && link_editor.component}
                <link_editor.component
                  search_input_id={`link-editor-${link_variant}`}
                  exclude_block_id={block.block_id}
                  on_blur={() => {
                    link_editor_ref[link_variant].hidePopover();
                  }}
                  value={block[`${link_variant}_link_href`]}
                  on_select={(option) => {
                    block[`${link_variant}_link_href`] = option.value;
                    if(option.title && !block[`${link_variant}_link_title`] || ['email', 'phone'].includes(option.type)) {
                      block[`${link_variant}_link_title`] = option.title;
                    }
                    link_editor_ref[link_variant].hidePopover();
                  }}
                  container='inline'
                />
                {/if}
              </Popover>
            {/if}
          {/each}
        </div>
      {/if}
    </hgroup>
  {/if}

  {#if is_regular_dates_layout}
    <div class='field regular-dates'>
      <CalendarRegular regular={block.regular_series ?? []} />
    </div>
  {/if}

  {#if !is_regular_dates_layout && (block.entry_previews?.length > 0 || pending_media_jobs.length > 0 || (is_program_layout && block.program_automatic))}
    {#if is_program_layout}
      <!-- Program: event rows with a date column -->
      <div class="field program-list" role={is_editing ? 'list' : undefined}>
        {#each program_rows as { entry_preview, index } (`${index}-${entry_preview.entry_id}`)}
          <div
            class="program-item"
            class:is-past={is_editing && is_past_program_entry(entry_preview)}
            class:is-dragging={dragging_index === index}
            role={is_editing ? 'listitem' : undefined}
            draggable={is_editing && !block.program_automatic && page.data.current_user?.role === 'admin' && block.entry_previews.length > 1}
            ondragstart={(e) => handle_drag_start(e, index)}
            ondragover={(e) => handle_drag_over(e, index)}
            ondragleave={(e) => handle_drag_leave(e)}
            ondrop={(e) => handle_drop(e, index)}
            ondragend={handle_drag_end}
            onmouseenter={() => hovered_preview_index = index}
            onmouseleave={() => { if (hovered_preview_index === index) hovered_preview_index = null; }}
            style:anchor-name={`--preview-${block.block_id}-${index}`}
            ondblclick={(e) => {
              if (!entry_api.is_editing) return;
              if (!entry_api.is_contextual_editing) {
                e.preventDefault();
                entry_api.start_contextual_editing(entry_preview.entry_id);
              }
            }}
          >
            <ProgramRow
              {entry_preview}
              href={block.link_entry_previews && !is_editing ? entry_url(entry_preview) : undefined}
              show_image={block.show_entry_preview_images}
              now={program_now}
            />
            {#if page.data.current_user?.role === 'admin' && is_editing}
              <Icon name={get_visibility_icon(entry_preview.visibility)} class='visibility-icon' />
            {/if}
          </div>
        {/each}

        {#if block.program_automatic && is_editing}
          <p class='program-note text-caption'>{t('program_automatic_hint')}</p>
        {/if}
        {@render drop_indicator_line('vertical')}
      </div>
    {:else if is_faq_layout}
      <!-- FAQ Accordion Layout -->
      <div bind:this={faq_list_element} class="field faq-list" role={is_editing ? 'list' : undefined}>
        {#each block.entry_previews as entry_preview, i}
          {@const { type, title, description, preamble, visibility, images } = entry_preview}
          {@const has_content = description}
          {@const has_image = images?.length > 0}
          <details 
            class="faq-item"
            class:no-content={!has_content}
            class:has-image={has_image}
            draggable={is_editing && page.data.current_user?.role === 'admin' && block.entry_previews.length > 1}
            ondragstart={(e) => handle_drag_start(e, i)}
            ondragover={(e) => handle_drag_over(e, i)}
            ondragleave={(e) => handle_drag_leave(e)}
            ondrop={(e) => handle_drop(e, i)}
            ondragend={handle_drag_end}
            ontoggle={relayout_opened_answer}
            onmouseenter={() => hovered_preview_index = i}
            onmouseleave={() => { if (hovered_preview_index === i) hovered_preview_index = null; }}
            style:anchor-name={`--preview-${block.block_id}-${i}`}
            class:is-dragging={dragging_index === i}
            ondblclick={(e) => {
              if (!entry_api.is_editing) return;
              if (!entry_api.is_contextual_editing) {
                e.preventDefault();
                entry_api.start_contextual_editing(entry_preview.entry_id);
              }
            }}
          >
            <summary 
              class="faq-summary"
              onclick={(e) => {
                // Prevent opening if no content
                if (!has_content) e.preventDefault();
              }}
            >
              {#if has_image}
                <Image 
                  class="faq-image"
                  {...images[0]}
                  sizes="80px"
                  aspect_ratio="1/1"
                  loading="lazy"
                />
              {/if}
              <div class="faq-text">
                {#if preamble}
                  <span class="faq-preamble text-preamble text leading-trim">{preamble}</span>
                {/if}
                <span class="faq-question text-heading3 text leading-trim">{title || t('untitled')}</span>
              </div>
              {#if has_content}
                <Icon name="arrow-down" class="faq-arrow" />
              {/if}
              {#if page.data.current_user?.role === 'admin' && is_editing}
                <Icon name={get_visibility_icon(visibility)} class='visibility-icon' />
              {/if}
            </summary>
            {#if has_content}
              <div class="faq-answer text-body text">
                {#if description}
                  {@html render_micro_markdown(description, [], 'text-body')}
                {/if}
              </div>
            {/if}
          </details>
        {/each}
        
        {@render drop_indicator_line('vertical')}
      </div>
    {:else}
    <!-- Standard Entry Previews -->
    <div
      class='field entry-previews count-{block.entry_previews.length}'
      style:--entry-previews-count={block.entry_previews.length}
      role={is_editing ? 'list' : is_auto_carousel ? 'region' : undefined}
      aria-label={is_auto_carousel ? t('slideshow') : undefined}
      tabindex={is_auto_carousel ? 0 : undefined}
      use:carousel_auto_advance={is_auto_carousel}
    >
      <!-- {#if is_editing}
        <BlockFocusTrap />
      {/if} -->
      <!-- Key includes the index: the same media entry may legitimately appear
           twice in one block (e.g. pasting a deduped image again) -->
      {#each block.entry_previews as entry_preview, i (`${i}-${entry_preview.entry_id}`)}
        {@const { type,title,description,preamble,images,visibility } = entry_preview}
        {@const category = get_entry_category(entry_preview.tag_keys, page.data.entry_tag_colors)}
        <!-- Images and videos both use the image entry type. -->
        {@const show_preview_button = block.show_entry_preview_button && type !== 'image'}
        <!--  n: "one" based (not zero based) -->
        {@const n = i + 1}
        {@const href = entry_url(entry_preview)}
        <!-- svelte-ignore a11y_no_static_element_interactions -->


        <!-- Utility for nth-child selectors:
         https://css-tip.com/quantity-queries/
        -->

        <svelte:element 
          this={
            (block.link_entry_previews 
            && type !== 'image' 
            && !is_editing) 
            || 
            (
              layouts_where_images_link_to_primary_link.includes(block.layout) 
              && !is_editing
              && block.primary_link_href
              && type === 'image'
            ) ? 'a' : 'div'
          }
          use:inview={{
            active: block.layout !== 144 && site_settings.typography.animation_intensity > 0,
            once: true,
            threshold: [0, 0.2],
            root_margin: "0px 0px -10% 0px"
          }}
          draggable={is_editing && page.data.current_user?.role === 'admin' && !is_fullscreen_layout && block.entry_previews.length > 1}
          ondragstart={(e) => handle_drag_start(e, i)}
          ondragover={(e) => handle_drag_over(e, i)}
          ondragleave={(e) => handle_drag_leave(e)}
          ondrop={(e) => handle_drop(e, i)}
          ondragend={handle_drag_end}
          onmouseenter={() => hovered_preview_index = i}
          onmouseleave={() => { if (hovered_preview_index === i) hovered_preview_index = null; }}
          ondblclick={(e) => {
            if (!entry_api.is_editing) return; // double click only works in edit mode
            // NOTE: We only support one level of contextual editing
            if (!entry_api.is_contextual_editing) {
              entry_api.start_contextual_editing(entry_preview.entry_id);
            } else {
              console.warn('Contextual editing is only possible one level deep');
            }
          }}
          {...(
            block.link_entry_previews 
            && type !== 'image' 
            && !is_editing) ? 
          {
            href
          } :
          (
            layouts_where_images_link_to_primary_link.includes(block.layout) 
            && block.primary_link_href
            && type === 'image'
          ) ? {
            href: block.primary_link_href
          } : 
          {}}
          style:--preview-card-index={n}
          style:--preview-card-count={block.entry_previews.length}
          style:anchor-name={`--preview-${block.block_id}-${i}`}
          class="preview-card preview-card-{n}"
          class:text-only={!(block.show_entry_preview_images && images?.length > 0)}
          class:odd={n % 2 === 1}
          class:even={n % 2 === 0}
          class:first={n === 1}
          class:second={n === 2}
          class:last={n === block.entry_previews.length}
          class:second-last={n === block.entry_previews.length - 1}

          class:every-3-starting-1={n % 3 === 1}
          class:every-3-starting-2={n % 3 === 2}
          class:every-3-starting-3={n % 3 === 0}        

          class:every-4-starting-1={n % 4 === 1}
          class:every-4-starting-3={n % 4 === 3}
          class:every-4-starting-2={n % 4 === 2}
          class:every-4-starting-4={n % 4 === 0}            

          class:every-7-starting-1={n % 7 === 1}
          class:every-7-starting-2={n % 7 === 2}
          class:every-7-starting-3={n % 7 === 3}
          class:every-7-starting-4={n % 7 === 4}
          class:every-7-starting-5={n % 7 === 5}
          class:every-7-starting-6={n % 7 === 6}
          class:every-7-starting-7={n % 7 === 0}

          class:every-8-starting-1={n % 8 === 0}
          class:every-8-starting-2={n % 8 === 1}
          class:every-8-starting-3={n % 8 === 2}
          class:every-8-starting-4={n % 8 === 3}
          class:every-8-starting-5={n % 8 === 4}
          class:every-8-starting-6={n % 8 === 5}
          class:every-8-starting-7={n % 8 === 6}
          class:every-8-starting-8={n % 8 === 7}
          class:is-dragging={dragging_index === i}
        > 
          <!-- QUESTION: should we link those entries then? (= clicking on an item will take you to the full entry page?) -->
          {#if block.show_entry_preview_images && images && images.length > 0}
            {@const slot_settings = slot_settings_for(entry_preview)}
            <div class='preview-images'>
              {#each {length: Math.min(layout_preview_image_count[block.layout] || 1, images.length)} as _, i}
                <ZoomableImage
                  zoom={
                    type === 'image'
                    && !is_editing
                    && !disable_zoom_for_layout_variants.includes(block.layout)
                    && site_settings.image_zoom
                    && !images[i]?.src?.toLowerCase().endsWith('.svg')
                    && !(
                      layouts_where_images_link_to_primary_link.includes(block.layout)
                      && block.primary_link_href
                    )
                  }
                  {...images[i]}
                  {href}
                  {title}
                  {description}
                  {preamble}
                  {is_editing}
                  {...layout_config?.image_sizes ? {sizes: layout_config.image_sizes} : {}}
                  aspect_ratio={common_aspect_ratio}
                  fit_to_frame={slot_settings?.fit != null ? slot_settings.fit === 'contain' : images[i]?.fit_to_frame}
                  focal_point_x={slot_settings?.focal?.[0] ?? images[i]?.focal_point_x}
                  focal_point_y={slot_settings?.focal?.[1] ?? images[i]?.focal_point_y}
                  show_playback_control={true}
                  class={determine_orientation(images[i]?.width ?? images[0].width, images[i]?.height ?? images[0].height)}
                  loading={i === 0 ? 'eager' : 'lazy'}
                />
              {/each}
            </div>
          {/if}

          {#if 
            block.show_entry_preview_preamble && (preamble || category)
            || block.show_entry_preview_title && title
            || block.show_entry_preview_description && description
            || show_preview_button
          }
            <div class='preview-texts'>
              {#if block.show_entry_preview_preamble && (preamble || category)}
                <p class='preview-preamble text-preamble text leading-trim line-clamp'>
                  <!-- The category (first tag with a colour) leads the eyebrow: "■ Ausstellung · Do., 17. Sept." -->
                  {#if category}<CategoryLabel {category} text_style='text-preamble' />{#if preamble}<span class='preamble-separator' aria-hidden='true'> · </span>{/if}{/if}
                  <!-- Render markdown links if the entry itself is not linked -->
                  {@html render_micro_markdown(
                    preamble ?? '',
                    ([block.link_entry_previews ? 'link' : null].filter(Boolean)),
                    'text-preamble'
                  )}
                </p>
              {/if}
              {#if block.show_entry_preview_title && title}
                <!-- Opionated: -->
                <!-- Use "smaller" preamble text style in case there's no preamble and description -->
                <h3 class='preview-title {!block.show_entry_preview_preamble && !block.show_entry_preview_description ? 'text-preamble' : 'text-heading3'} text leading-trim line-clamp'>
                  {@html render_micro_markdown(
                    title,
                    ([block.link_entry_previews ? 'link' : null].filter(Boolean)),
                    (!block.show_entry_preview_preamble && !block.show_entry_preview_description ? 'text-preamble' : 'text-heading3')
                  )}
                </h3>
              {/if}
              {#if block.show_entry_preview_description && description}
                <p class='preview-description text-caption text leading-trim line-clamp'>
                  {@html render_micro_markdown(
                    description,
                    ([block.link_entry_previews ? 'link' : null].filter(Boolean)),
                    'text-caption'
                  )}
                </p>
              {/if}
              {#if show_preview_button}
                <ButtonLabel
                  class='preview-button'
                  href={!block.link_entry_previews ? href : undefined}
                  variant='brand {site_settings.primary_control_surface}'
                  align={site_settings.control_surface_align}
                  editable={is_editing}
                  bind:text={block.entry_preview_button_label}
                  field_name='entry_preview_button_label'
                  end_icon=' '
                >
                  {#if !is_editing && !block.entry_preview_button_label}
                    {t('read_more')}
                  {/if}
                </ButtonLabel>
              {/if}
            </div>
          {/if}
        </svelte:element>
      {/each}

      <!-- In-flight upload placeholders: local preview + state, replaced by the real
           preview card once the created entry resolves into entry_previews -->
      {#each pending_media_jobs as job, job_index (job.id)}
        <!-- Each placeholder continues the card numbering; sharing one index
             stacked concurrent uploads in the grid-positioned layouts. -->
        <div
          class='preview-card media-upload-placeholder'
          class:failed={job.status === 'failed'}
          style:--preview-card-index={block.entry_previews.length + job_index + 1}
          style:--preview-card-count={block.entry_previews.length + pending_media_jobs.length}
        >
          {#if job.preview_url}
            <img class='placeholder-preview' src={job.preview_url} alt='' />
          {/if}
          <div class='placeholder-status text-caption'>
            {#if job.status === 'failed'}
              <span class='status-text'>{t('upload_failed')}</span>
              <span class='placeholder-actions'>
                <button onclick={() => retry_upload(job.id)}><ButtonLabel variant='outlined text-compact'>{t('retry')}</ButtonLabel></button>
                <button onclick={() => remove_upload(job.id)}><ButtonLabel variant='ghostly text-compact'>{t('remove')}</ButtonLabel></button>
              </span>
            {:else if job.status === 'uploading'}
              <progress class='placeholder-progress' max='100' value={job.progress}></progress>
              <span class='status-text'>{t('uploading')} {job.progress}%</span>
            {:else if job.status === 'optimizing' || job.status === 'ready'}
              <span class='status-text optimizing'>{t('optimizing_safe_to_leave')}</span>
            {:else}
              <span class='status-text'>{t('uploading')}…</span>
            {/if}
          </div>
        </div>
      {/each}

      {#if FULLSCREEN_SCROLL_LAYOUTS.includes(block.layout) && block.entry_previews.length > 1 && !is_editing}
        <nav class='fss-pagination' aria-label='Scroll progress'>
          {#each block.entry_previews as _, i}
            <span
              class='fss-dot'
              style:--dot-index={i + 1}
            ></span>
          {/each}
        </nav>
      {/if}

      {@render drop_indicator_line(layout_orientation)}
    </div>
    {/if}
  {/if}

  <!-- Decorative stickers (stamps/badges) anchored to entry previews or the text fields -->
  <!-- Uses CSS anchor positioning; hidden in browsers without support (purely decorative) -->
  {#if block.stickers?.length > 0}
    {#each block.stickers as sticker (sticker.id)}
      <!-- NOTE: no spread attributes here: mixing spreads with event handlers on
           svelte:element can detach the delegated handlers on attribute updates -->
      <svelte:element
        this={sticker.href && !is_editing ? 'a' : 'div'}
        bind:this={sticker_elements[sticker.id]}
        class="sticker area-{sticker.area ?? 'bottom-right'}"
        class:is-editing={is_editing}
        class:is-selected={is_editing && selected_sticker_id === sticker.id}
        style:--sticker-anchor={sticker_anchor_name(sticker)}
        style:--sticker-size="{sticker.size ?? 96}px"
        style:--sticker-aspect-ratio={sticker.width && sticker.height ? sticker.width / sticker.height : 1}
        style:--sticker-rotate="{sticker.rotate ?? 0}deg"
        style:--sticker-offset-x="{sticker.offset_x ?? 0}%"
        style:--sticker-offset-y="{sticker.offset_y ?? 0}%"
        href={sticker.href && !is_editing ? sticker.href : undefined}
        role={is_editing ? 'button' : undefined}
        tabindex={is_editing ? 0 : undefined}
        title={is_editing ? t('sticker_edit') : undefined}
        onclick={(e) => {
          if (!is_editing) return;
          e.stopPropagation();
          selected_sticker_id = sticker.id;
        }}
        onkeydown={(e) => {
          if (!is_editing) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            selected_sticker_id = sticker.id;
          }
        }}
        aria-hidden={sticker.label || is_editing ? undefined : 'true'}
      >
        <img class="sticker-image" src={sticker.src} alt="" loading="lazy" draggable="false" />
        {#if sticker.label}
          <span class="sticker-label text-preamble">{sticker.label}</span>
        {/if}
      </svelte:element>
    {/each}

    {#if is_editing && selected_sticker && sticker_elements[selected_sticker_id] && sticker_editor.component}
      <sticker_editor.component
        {block}
        sticker={selected_sticker}
        anchor_element={sticker_elements[selected_sticker_id]}
        onclose={() => selected_sticker_id = null}
      />
    {/if}
  {/if}

  </div>
{/if}

<!-- {JSON.stringify(block,null,2)} -->

<style lang='postcss'>

  /* 
    When preamble uses uppercase text-transform, .-caps elements in body text
    should inherit preamble typography for visual consistency.
    This applies to ++text++ markdown syntax within description fields.
  */
  :global(.story-block .preamble-is-uppercase .text-body .-caps) {
    font-family: var(--preamble-font-family);
    font-size: var(--preamble-font-size);
    font-weight: var(--preamble-font-weight);
    font-style: var(--preamble-slant);
    line-height: var(--preamble-line-scale);
    letter-spacing: var(--preamble-letter-spacing);
    word-spacing: var(--preamble-word-spacing);
    font-optical-sizing: var(--preamble-optical-sizing);
    font-feature-settings: var(--preamble-font-features);
    font-variation-settings: var(--preamble-font-variations);
  }

  /* fade opacity only once, not anymore after block has been in view once */
  /* :global(.block.at-0:not(.seen-0):not(.is_editing) .story-layout.use-animations .entry-previews .preview-images .media-item:not(.fit-to-frame)) {
    animation-name: parallax-scale-full-opacity;
  } */
  /* Final state after animation or when editing */
  /* :global(:is(.block.seen-0, .block.is_editing) .story-layout.use-animations .entry-previews .preview-images .media-item:not(.fit-to-frame)) {
    opacity: 1;
    transform: translateY(0) scale(1);
  } */

  /* --block-spacing is now set via inline style from layout config or user override */
  /* apply font smoothing for blocks that have a dark color set, so text appears less bold */
  :global(.block.has-color-set) {
    -webkit-font-smoothing: var(--font-smoothing, var(--canvas-font-smoothing));
  }

  /* ═══════════════════════════════════════════════════════════════════════════════════════════════
   * STICKERS (decorative stamps/badges)
   * Positioned with CSS anchor positioning relative to a preview card (--preview-{block_id}-{i})
   * or the text fields (--text-{block_id}). The sticker is centered on the chosen anchor point
   * (e.g. 'bottom-right' = the bottom-right corner of the anchor), then shifted by the optional
   * offsets (in % of the sticker size). Browsers without anchor positioning hide stickers.
   * ═══════════════════════════════════════════════════════════════════════════════════════════════ */
  .sticker {
    display: none;
  }

  /* Clip horizontal sticker overhang at the page edge so it never causes horizontal page scroll
     (vertical overhang stays visible). The block spans the full page width, so this only clips
     at the viewport edge, not at the content track. */
  :global(.story-block:has(.sticker)) {
    overflow-x: clip;
    overflow-y: visible;
  }

  /* Same treatment for the collage layouts, for the same reason: every card is rotated, and a
     rotated box is wider than its layout box, so a card in the outermost column overhangs the
     page edge and produces horizontal scroll. Most visible on narrow screens, where the cards sit
     hard against the page inline padding. Vertical overhang stays visible. */
  :global(.story-block:has(.variant-140, .variant-141, .variant-142, .variant-145, .variant-146)) {
    overflow-x: clip;
    overflow-y: visible;
  }

  @supports (anchor-name: --a) {
    .sticker {
      display: grid;
      place-items: center;
      position: absolute;
      position-anchor: var(--sticker-anchor);
      /* scale down on small viewports so stickers never dominate */
      width: min(var(--sticker-size), 24vw);
      aspect-ratio: var(--sticker-aspect-ratio);
      rotate: var(--sticker-rotate);
      translate: calc(-50% + var(--sticker-offset-x)) calc(-50% + var(--sticker-offset-y));
      z-index: 3;
      text-decoration: none;

      /* anchor point (sticker center sits on this point via translate) */
      &.area-top-left      { top: anchor(top);    left: anchor(left);   }
      &.area-top           { top: anchor(top);    left: anchor(center); }
      &.area-top-right     { top: anchor(top);    left: anchor(right);  }
      &.area-left          { top: anchor(center); left: anchor(left);   }
      &.area-center        { top: anchor(center); left: anchor(center); }
      &.area-right         { top: anchor(center); left: anchor(right);  }
      &.area-bottom-left   { top: anchor(bottom); left: anchor(left);   }
      &.area-bottom        { top: anchor(bottom); left: anchor(center); }
      &.area-bottom-right  { top: anchor(bottom); left: anchor(right);  }

      /* In edit mode stickers are clickable and open the sticker editor */
      &.is-editing {
        cursor: pointer;

        @media (hover: hover) {
          &:hover {
            outline: 1px dashed var(--editing);
            outline-offset: 2px;
          }
        }

        &.is-selected {
          outline: 1px solid var(--editing);
          outline-offset: 2px;
        }
      }

      .sticker-image {
        grid-area: 1 / 1;
        width: 100%;
        height: 100%;
        object-fit: contain;
        border: none; /* undo global img border */
        background: none;
        user-select: none;
        -webkit-user-drag: none;
      }

      .sticker-label {
        grid-area: 1 / 1;
        text-align: center;
        white-space: pre-line;
        color: var(--color-default-background);
        /* scale the label with the sticker itself */
        font-size: clamp(9px, calc(min(var(--sticker-size), 24vw) * 0.115), 22px);
        line-height: 1.3;
        padding: 12%;
      }

      @media (hover: hover) {
        &[href] {
          transition: scale 0.2s ease;
          &:hover {
            scale: 1.06;
          }
        }
      }
    }
  }

  /* ═══════════════════════════════════════════════════════════════════════════════════════════════
   * FAQ ACCORDION LAYOUTS (201, 202)
   * ═══════════════════════════════════════════════════════════════════════════════════════════════ */
  .faq-list {
    display: flex;
    flex-direction: column;
    position: relative; /* For drop indicator positioning */
  }
  
  .faq-item {
    --faq-arrow-width: var(--icon-size, 20px);
    --faq-image-width: 48px;
    --faq-gap: var(--size-3);
    --faq-padding-inline: 0px;
    --faq-padding-block: var(--size-7);
    overflow: hidden;
    &:not(:last-child) {
      border-bottom: 1px solid var(--color-default-stroke);
    }
      
    @media (hover: hover) {
      &:hover:has(.faq-answer) {
        border-color: oklch(from var(--color-default-stroke) calc(l * 0.3) c h);
        
        :global(.faq-arrow) {
          opacity: 1;
        }
      }
      
      :global(.faq-arrow) {
        opacity: 0.5;
      }
    }

    /* Animated expand/collapse using grid-template-rows hack */
    --faq-transition: 0.15s cubic-bezier(0.25, 0.1, 0.25, 1);
    
    &::details-content {
      display: grid;
      grid-template-rows: 0fr;
      transition: grid-template-rows var(--faq-transition), content-visibility var(--faq-transition);
      transition-behavior: allow-discrete;
    }

    &[open]::details-content {
      grid-template-rows: 1fr;
    }
    
    /* Starting styles for entry animation */
    @starting-style {
      &[open]::details-content {
        grid-template-rows: 0fr;
      }
    }

    &[open] :global(.faq-arrow) {
      transform: rotate(180deg);
    }
    
    &.no-content .faq-summary {
      cursor: default;
    }
    
    &.has-image .faq-summary {
      grid-template-columns: auto 1fr auto auto;
    }
    
    &.has-image .faq-answer {
      padding-inline-start: calc(var(--faq-padding-inline) + var(--faq-image-width) + var(--faq-gap));
    }
    
    /* Drag-and-drop support */
    &[draggable="true"] {
      cursor: grab;
      user-select: none;
      
      &:active {
        cursor: grabbing;
      }
    }
    
    &.is-dragging {
      opacity: 0.5;
      outline: 1px dashed var(--editing);
      outline-offset: 2px;
    }

    .faq-summary {
      display: grid;
      grid-template-columns: 1fr auto auto;
      align-items: center;
      gap: var(--faq-gap);
      padding: var(--faq-padding-block) var(--faq-padding-inline);
      cursor: pointer;
      user-select: none;
      list-style: none;
      position: relative;
      
      &::-webkit-details-marker {
        display: none;
      }
      
      &::marker {
        display: none;
      }
      
      &:focus-visible {
        outline: none;
        background-color: oklch(from var(--color-default-background) calc(l * 0.97) c h);
      }
      
      :global(.faq-arrow) {
        transition: transform var(--faq-transition), opacity var(--faq-transition);
      }
      
      :global(.faq-image) {
        width: var(--faq-image-width);
        height: var(--faq-image-width);
        border-radius: var(--radius-xs);
        object-fit: cover;
      }
      
      .faq-text {
        display: flex;
        flex-direction: column;
        min-width: 0;
        
        .text:not(:last-child) {
          margin-block-end: 0.8em;
        }
      }
      
      .faq-preamble {
        margin: 0;
        opacity: 0.7;
      }
      
      .faq-question {
        margin: 0;
      }
      
      :global(.visibility-icon) {
        --icon-color1: var(--color-default-foreground-600);
      }
    }

    .faq-answer {
      /* Required for grid-template-rows animation */
      min-height: 0;
      overflow: hidden;
      /* Align with faq-text */
      padding-inline: var(--faq-padding-inline);

      /*
        Bottom spacing is part of the content, not padding: the 0fr row clips content, but the box
        can never be shorter than its padding. Animating the padding instead jumped in both engines
        (Safari also kept the frozen padding for the whole close, then dropped it).
      */
      &::after {
        content: '';
        display: block;
        height: var(--faq-padding-block);
      }

      /*
        The answer clips (the collapse needs overflow: hidden), so anything wider than the column
        is cut off on the right instead of spilling. Boxes that size to their content - link pills,
        flex/grid items - cannot shrink below their longest word, and Safari never hyphenates
        (the global hyphens rule is gated on hyphenate-limit-chars), so a German compound like
        "Wurzelkanalbehandlung" set the minimum width and got cropped on narrow iPhones.
        `anywhere` makes those words breakable for min-content too. The global `*` rule sets
        break-word on every element, so the descendants need it explicitly.
      */
      &, :global(*) {
        overflow-wrap: anywhere;
      }

      .faq-preamble {
        margin-bottom: var(--size-3);
      }
      
      :global(p:not(:last-child)) {
        margin-bottom: var(--size-3);
      }
    }
  }
  
  /* Drop indicator line - positioned between items */
  .faq-list .drop-indicator {
    position: absolute;
    background: var(--editing);
    border-radius: var(--round);
    z-index: 100;
    pointer-events: none;
    position-anchor: var(--target-anchor);
    
    &.vertical {
      left: anchor(left);
      right: anchor(right);
      height: 4px;
      
      &.before {
        bottom: anchor(top);
        transform: translateY(50%);
      }
      
      &.after {
        top: anchor(bottom);
        transform: translateY(-50%);
      }
    }
  }

  /* FAQ Layout two-column positioning (text on one side, FAQ list on other) */
  /* Uses CSS variables for grid columns to reduce redundancy */
  .variant-201, .variant-202, .variant-203, .variant-204,
  .variant-205, .variant-206, .variant-207, .variant-208 {
    /*
      The block spans its whole track and the two columns divide that span, so the width
      control moves the CONTAINER rather than nudging the inner columns. The old per-variant
      values only travelled 8 columns across all four widths (the text column was 17 wide in
      every one of them), which is why the control looked broken.

      --faq-split is the container's own midline. The columns stop one and three lines short
      of it, giving a constant two-column gutter at any width. These formulas are not new:
      they reproduce the original defaults (6/23 and 25/46) exactly at extended-2.
    */
    --faq-split: calc(var(--track-centred-start) + var(--track-span-cols) / 2);

    --faq-text-start: var(--track-centred-start);
    --faq-text-end: calc(var(--faq-split) - 3);
    --faq-list-start: calc(var(--faq-split) - 1);
    --faq-list-end: var(--track-centred-end);

    /* Mirror: text right, list left (alignment comes from the layout, not the block) */
    &.align-right {
      --faq-text-start: calc(var(--faq-split) + 3);
      --faq-text-end: var(--track-centred-end);
      --faq-list-start: var(--track-centred-start);
      --faq-list-end: calc(var(--faq-split) + 1);
    }

    @media (min-width: 769px) {
      grid-template-rows: auto auto 1fr;
      .text-fields {
        grid-area: 1 / var(--faq-text-start) / 2 / var(--faq-text-end);
        height: fit-content;
        position: relative;
        display: flex;
        flex-direction: column;
      }
      /* Pinned only beside a list that runs on well beyond the text (see faq_text_pinned) */
      &.faq-text-pinned .text-fields {
        position: sticky;
        top: calc(var(--dynamic-island-bottom-edge) + var(--block-spacing-s));
      }
      .faq-list {
        grid-area: 1 / var(--faq-list-start) / 2 / var(--faq-list-end);
      }
    }
    @media (max-width: 768px) {
      .faq-list {
        grid-area: 5 / 2 / 16 / 50;
      }
    }
  }

  /* The eight per-variant column rules are gone: they were hand-tuned copies of the formula
     above, and the four "widths" they produced differed by only one column each. */

  /* For full-screen images: */
  /* apply mix-blend-mode if the dynamic island has no background, so that the text is visible */
  :global(
    body
      :has(.dynamic-island:not(.has-background):not(.is-editing):not(.mobile-drawer-open))
      :has(.story-block:first-child .story-layout.full-screen-images) 
      .dynamic-island 
    ) {
    transition: mix-blend-mode 0.3s ease-in-out;
    mix-blend-mode: difference;
    :global(.row-1 .button-label) {
      --icon-color1: var(--color-default-background);
      :global(.button-text) {
        color: var(--color-default-background);
        mix-blend-mode: difference;
      }
      :global(.button-icon){
        mix-blend-mode: difference;
      }
      :global(.media-item) {
        background-color: var(--color-default-background);
        filter: invert(1) saturate(0);
      }
    }
    :global(.marker){
      background-color: var(--color-default-background);
    }
  }

  .story-layout {
    padding-block-start: var(--block-spacing-top);
    padding-block-end: var(--block-spacing-bottom);
    display: grid;
    align-items: flex-start;
    grid-template-columns: inherit;
    grid-template-rows: repeat(5, auto);
    grid-column: 1 / -1;
    background-color: var(--color-background, transparent);

    /* we use .text instead of e.g. "preamble" as the font size is defined on the "text" element */    
    :global(.description:not(.first.last) .text),
    :global(.links:not(.first.last) .text:not(.editable)) { 
      margin-block-end: var(--font-size);
    }
    :global(.title .text) {
      margin-block-end: max(calc(var(--font-size) * 0.5), calc(var(--body-font-size) * 1.5));
      @media (min-width: 769px) {
        margin-block-end: max(calc(var(--font-size) * 0.5), calc(var(--body-font-size) * 2));
      }
      /* 
       - Currently hanging-punctuation is only supported in Safari 
       - For large titles the hanging punctuation can be cropped by the edge of the screen, as the width of the character can be larger than the padding inline.
       - TODO: We need a cross-browser solution that also considers the padding inline.

       Something like this, that's using negative text-indent to move the text left, so that the hanging punctuation is not cropped.
       But this requires knowledge of whether there's a punctuation mark at the start & end of the text
       blockquote {
          text-indent: -0.45em; 
        }
        @​supports (hanging-punctuation: first) {
          blockquote {
            text-indent: 0;
            hanging-punctuation: first;
          }
        }

      */
      hanging-punctuation: none;
    }
    :global(.description .text) { 
      text-wrap: balance;
      margin-block-end: var(--font-size);
    }
    :global(.preamble:not(.first.last) .text) {
      margin-block-end: min(calc(var(--font-size) * 1.5), calc(var(--body-font-size)));
    }
    :global(.links) {
      row-gap: var(--size-4);
      /* The row's outer edges stay flush with the text column; only the gap between two links
         is spaced (below). Without this, ButtonLabel's generic "buttons in a row" spacing
         insets the first and last link by --size-2. */
      :global(> :is(a, button, span)) {
        --button-outer-inline-spacing: 0;
      }
      /* Positions are counted among the links only (`of a`). The server also renders a hidden
         link-editor popover after every link and hydration removes it, so plain :first-child /
         :last-child matched differently before and after - a layout shift on every page load. */
      :global(a.secondary-link:not(:nth-child(1 of a))){
        --button-outer-inline-spacing-start: calc(var(--grid-gutter) * 0.5);
      }
      :global(a.primary-link:not(:nth-last-child(1 of a))){
        --button-outer-inline-spacing-end: calc(var(--grid-gutter) * 0.5);
      }
      :global(.text:empty) {
        width: 100%;
      }
    }
    :global(.field:has( + .field.links) .text) {
      margin-block-end: calc(var(--font-size) * 1.5);
      @media (min-width: 769px) {
        margin-block-end: calc(var(--font-size) * 2);
      }
    }
    :global(.title:has( + .field.links) .text) {
      margin-block-end: calc(var(--font-size) * 0.5);
      @media (min-width: 769px) {
        margin-block-end: calc(var(--font-size) * 0.5);
      }
    }

    :where(.text-fields) {
      display: contents;
      :global(:where(.field.last) .text) {
        /* the last text field should have no margin at the end */
        @media (min-width: 769px) {
          margin-block-end: 0;
        }
      }
    }

    .entry-previews {
      position: relative; /* For drop indicator positioning */
    }
    :global(.button-label:is(.brandprimary, .brandsecondary, .preview-button)) {
      /* background-position-x: the run of underlined buttons (ButtonLabel), which this list replaces */
      transition: opacity 150ms ease, background-color 150ms ease, scale 100ms ease-out, background-position-x var(--underline-run-duration, 0s) var(--ease-glide);
      @media (prefers-reduced-motion: reduce) {
        transition-duration: 0s;
      }
    }
    .link-container:active > :global(.button-label),
    :global(.preview-button:active) {
      scale: 0.98;
    }
    /* A long link label ("Aufnahmeantrag Mitgliedschaft mit Datenschutzerklärung") wraps inside
       the column instead of pushing the page sideways on phones */
    .link-container {
      max-width: 100%;
      > :global(.button-label) {
        white-space: normal;
        text-wrap: balance;
      }
    }
    .entry-previews a.preview-card * {
      cursor: pointer;
    }
    .entry-previews .preview-card {
      display: flex;
      flex-direction: column;
      &:not(.last) {
        margin-block-end: var(--grid-gutter);
      }
      .preview-images:not(:last-child) { 
        margin-block-end: var(--size-5);
      }
      .preview-images {
        position: relative;
        user-select: none;
        :global(.visibility-icon) {
          --icon-color1: var(--color-default-background);
          position: absolute;
          margin: var(--size-2);
          top: max(calc(0.5 * (var(--card-radius,0px) + var(--icon-default-size))),var(--size-4));
          left: max(calc(0.5 * (var(--card-radius,0px) + var(--icon-default-size))),var(--size-4));
        }
        .edit-indicator {
          position: absolute;
          top: max(calc(0.5 * (var(--card-radius,0px) + var(--icon-default-size))),var(--size-4));
          right: max(calc(0.5 * (var(--card-radius,0px) + var(--icon-default-size))),var(--size-4));
          padding: var(--size-1);
          background: var(--color-default-background);
          border-radius: var(--round);
          box-shadow: 0 1px 3px oklch(0 0 0 / 0.15);
          opacity: 0;
          transition: opacity 0.15s, background 0.1s;
          cursor: pointer;
          z-index: 1;
          --icon-size: 14px;
          --icon-color1: var(--editing);
          &:hover {
            background: var(--editing);
            --icon-color1: var(--color-default-background);
          }
        }
      }
      &:hover .edit-indicator {
        opacity: 1;
      }
      /* Prevent OS image dragging when card is draggable */
      &[draggable="true"] {
        cursor: grab;
        user-select: none;
        
        :global(img), :global(video), :global(picture), :global(.media-item) {
          pointer-events: none;
          user-select: none;
          -webkit-user-drag: none;
        }
        
        &:active {
          cursor: grabbing;
        }
      }
      
      /* Drag-and-drop indicators */
      &.is-dragging {
        opacity: 0.5;
        outline: 1px dashed var(--editing);
        outline-offset: 2px;
      }
      .preview-texts {
        display: flex;
        flex-direction: column;
        .preview-preamble {
          --line-clamp: 2;
        }
        .preview-title {
          --line-clamp: 9;
        }
        .preview-description {
          --line-clamp: 5;
        }
        /*
          One spacing value for the whole stack: picture, 16px, eyebrow, 8px, title, 8px,
          description. The eyebrow (category, date) belongs to the title it introduces and the
          description continues it, so both gaps are tight and equal; the larger gap above
          separates the texts from the picture.
        */
        :is(.preview-preamble, .preview-title, .preview-description):not(:last-child) {
          margin-block-end: var(--size-3);
        }
      }
    }
    
    /* A card without a picture: a rule above, the same text stack, no fill */
    &:is(.group-grid, .variant-140, .variant-141, .variant-142, .variant-143, .variant-145, .variant-146) .entry-previews .preview-card.text-only {
      border-block-start: 1px solid var(--color-foreground);
      padding-block-start: var(--size-5);
    }

    /* Drop indicator line - positioned between items */
    .entry-previews .drop-indicator {
      position: absolute;
      background: var(--editing);
      border-radius: var(--round);
      z-index: 100;
      pointer-events: none;
      
      /* Anchor to target element */
      position-anchor: var(--target-anchor);
      
      /* Vertical orientation */
      &.vertical {
        /* Match target element's width */
        left: anchor(left);
        right: anchor(right);
        height: 4px;
        
        &.before {
          bottom: anchor(top);
          transform: translateY(50%);
        }
        
        &.after {
          top: anchor(bottom);
          transform: translateY(-50%);
        }
      }
      
      /* Horizontal orientation */
      &.horizontal {
        /* Match target element's height */
        top: anchor(top);
        bottom: anchor(bottom);
        width: 4px;
        
        &.before {
          right: anchor(left);
          transform: translateX(50%);
        }
        
        &.after {
          left: anchor(right);
          transform: translateX(-50%);
        }
      }
    }
    .entry-previews .preview-card :global(.media-item:not(.fit-to-viewport)){
      max-height: calc(100svh - var(--dynamic-island-bottom-edge));
    }
  }

  /* Flip the position of the search select input to be always close to the ButtonLabel */
  :global(.link-editor-container.place-top-center .search-select-input-wrapper) {
    order: 2;
  }

  /* ═══════════════════════════════════════════════════════════════════════════════════════════════
   * IMAGE REVEAL
   * When a picture first comes into view (.at-0, set by the inview action on the card) it unveils
   * from the bottom edge up while it settles from slightly closer to its own framing, on a clock -
   * the same direction and curve as the text rising beside it. Afterwards the image's own style
   * applies again (fill backwards: no animation lingers, the hover zoom of linked cards works).
   *
   * Replaces a scroll-linked fade with an object-view-box crop: Safari has no object-view-box, so
   * there pictures only faded, and linked to the scroll the whole entrance was over within a turn
   * of the wheel - pictures read as static. Pictures shown whole (fit-to-frame, SVG) only unveil:
   * a settle would shrink the whole picture inside its box.
   * Not in edit mode, not when animations are off, not for the first block (already in view) and
   * not where covers, 67, 144 and the fullscreen scroll stage their media themselves. Not in grids
   * either: a whole row of pictures rising at once was too much - there they are simply in place.
   * ═══════════════════════════════════════════════════════════════════════════════════════════════ */
  @keyframes image-unveil {
    from { clip-path: inset(100% 0 0 0); }
    to { clip-path: inset(0); }
  }
  @keyframes image-settle {
    from { scale: calc(1 + 0.12 * var(--animation-intensity, 1)); }
  }

  /* Held: not yet in view, never seen */
  :global(.story-block:not(.is_editing):not(:first-child) .story-layout.use-animations:not(:is(.variant-67, .variant-144, .group-cover, .group-grid, .group-fullscreen-scroll)) .entry-previews .preview-card:not(.at-0):not(.seen-0) .preview-images .media-item) {
    opacity: 0;
  }

  /* Released: the first time the card comes into view */
  @media (prefers-reduced-motion: no-preference) {
    :global(.story-block:not(.is_editing):not(:first-child) .story-layout.use-animations:not(:is(.variant-67, .variant-144, .group-cover, .group-grid, .group-fullscreen-scroll)) .entry-previews .preview-card.at-0:not(.seen-0) .preview-images) {
      :global(.zoomable-image) {
        overflow: clip; /* the settling picture is larger than its frame */
        animation: image-unveil var(--duration-reveal) var(--ease-settle) backwards;
      }
      :global(.zoomable-image > :is(.media-item, .video-wrapper):not(.fit-to-frame, .svg)) {
        animation: image-settle var(--duration-reveal) var(--ease-settle) backwards;
      }
    }
  }

  /* Edit mode: everything in place */
  :global(.story-block.is_editing .story-layout.use-animations .entry-previews .preview-images .media-item) {
    opacity: 1;
  }

  /* Cover Grid (130): the first preview is the section backdrop - never reveal it.
     Scroll-snap carousels (carousel split, inset carousels): every card after the first enters sideways,
     where a block-axis reveal cannot lead it in - it stayed blank until it was already on screen
     and then popped. Only the first card keeps the reveal. */
  :global(.story-block .story-layout.variant-130 .entry-previews .preview-card.first .preview-images :is(.media-item, .zoomable-image)),
  :global(.story-block .story-layout:is(.variant-111, .variant-113, .variant-114, .variant-115, .variant-117, .variant-118, .variant-119, .variant-120, .inset-carousel) .entry-previews .preview-card:not(.first) .preview-images :is(.media-item, .zoomable-image)) {
    opacity: 1 !important;
    animation: none !important;
  }

  /* ═══════════════════════════════════════════════════════════════════════════════════════════════
   * TEXT SETTLE-IN (held and released by the observer in the script, see settles_text)
   * A held field waits invisible and 2rem low. Released, it rises on the settle curve and is
   * opaque before it has quite arrived, so it reads as soon as it is near its place; several
   * released together follow one another 90ms apart (--settle-order). Held without motion,
   * released with it. Afterwards the field's own style applies again: no animation lingers and
   * nothing stays a composited layer (a filling animation made Chromium draw its glyphs lighter).
   * ═══════════════════════════════════════════════════════════════════════════════════════════════ */
  .text-fields > :global(.field[data-settle='in']) {
    transition:
      opacity var(--duration-smooth) var(--ease-settle) calc(var(--settle-order, 0) * 90ms),
      translate var(--duration-reveal) var(--ease-settle) calc(var(--settle-order, 0) * 90ms);
  }
  .text-fields > :global(.field[data-settle='held']) {
    opacity: 0;
    translate: 0 calc(2rem * var(--animation-intensity, 1));
  }

  /* ═══════════════════════════════════════════════════════════════════════════════════════════════
   * PAGE INTRO (.intro, see the script)
   * The texts on screen when a page appears come in the way tbc.co's do: the title reads in while
   * it rises out of a slight blur, the fields after it follow once it is mostly there (the ones
   * before it lead by a step), each rising 3rem, 120ms apart; a block's buttons 100ms apart, later
   * blocks a little after earlier ones. Meanwhile the menu bar drops in (DynamicIsland). CSS only,
   * running from the first paint: nothing is hidden until a script arrives, and fill backwards
   * leaves no animation and no composited layer behind. Fields the settle-in holds back
   * (data-settle: below the window) are left to it. Not where a layout stages its texts itself:
   * 147 fades them in with its slideshow, bento and the fullscreen scroll tie them to the scroll.
   *
   * The title reads in without being cut into words: a mask with a soft edge travels across it
   * from left to right, one band per line, each band 0.3 box widths behind the one above, so the
   * words appear in reading order. The bands follow the lines (lh) and are cut halfway between a
   * line's baseline and the next line's cap height; the first reaches up and the last down past
   * the box (trimmed to cap height and baseline) for accents and descenders. Each band's tile is
   * 3 box widths wide: opaque for 2.6, then fading out over 0.4, about two words. It travels from
   * where its fade ends 0.2 left of the box to where its opaque part covers the box and 0.2 either
   * side - the fifth band (all lines from the fifth on) 1.2 behind the first. A mask-position
   * percentage counts the tile's offset in (box - tile) widths: an offset of -x box widths is
   * x / 2 * 100%.
   * Chromium and WebKit clip a masked element to its border box whatever mask-clip says, and the
   * trimmed box would cut off accents and descenders. So while it reads in, the title gets 3rem
   * of padding above and below, taken back by a negative margin above and by its field's margin
   * below (its own bottom margin is the gap to the next field) - the layout does not move.
   * ═══════════════════════════════════════════════════════════════════════════════════════════════ */
  @keyframes intro-rise {
    from {
      opacity: 0;
      translate: 0 calc(3rem * var(--animation-intensity, 1));
      filter: blur(3px);
    }
  }
  /* The title's own mask makes it appear */
  @keyframes intro-lift {
    from, to {
      margin-block-end: -3rem;
    }
    from {
      translate: 0 calc(3rem * var(--animation-intensity, 1));
      filter: blur(3px);
    }
  }
  @keyframes intro-read {
    from, to {
      padding-block: 3rem;
      margin-block-start: -3rem;
      mask-origin: content-box;
      mask-clip: border-box;
      mask-image:
        linear-gradient(to right, #000 calc(100% * 2.6 / 3), transparent),
        linear-gradient(to right, #000 calc(100% * 2.6 / 3), transparent),
        linear-gradient(to right, #000 calc(100% * 2.6 / 3), transparent),
        linear-gradient(to right, #000 calc(100% * 2.6 / 3), transparent),
        linear-gradient(to right, #000 calc(100% * 2.6 / 3), transparent);
      mask-size:
        300% calc(1lh + var(--intro-line-cut)),
        300% 1lh,
        300% 1lh,
        300% 1lh,
        300% calc(100% + 1lh);
      mask-repeat: no-repeat;
    }
    from {
      mask-position:
        160% -1lh,
        175% var(--intro-line-cut),
        190% calc(var(--intro-line-cut) + 1lh),
        205% calc(var(--intro-line-cut) + 2lh),
        220% calc(var(--intro-line-cut) + 3lh);
    }
    to {
      mask-position:
        10% -1lh,
        25% var(--intro-line-cut),
        40% calc(var(--intro-line-cut) + 1lh),
        55% calc(var(--intro-line-cut) + 2lh),
        70% calc(var(--intro-line-cut) + 3lh);
    }
  }

  :global(.story-block:nth-child(2)) .story-layout.intro {
    --intro-block-delay: 200ms;
  }
  :global(.story-block:nth-child(n + 3)) .story-layout.intro {
    --intro-block-delay: 400ms;
  }
  .story-layout.intro .text-fields {
    > :global(.field) {
      --intro-delay: calc(var(--intro-block-delay, 0ms) + 100ms + var(--intro-order, 0) * 120ms + var(--intro-after-title, 0ms));
    }
    > :global(.field:nth-child(2)) { --intro-order: 1; }
    > :global(.field:nth-child(3)) { --intro-order: 2; }
    > :global(.field:nth-child(4)) { --intro-order: 3; }
    > :global(.field:nth-child(5)) { --intro-order: 4; }
    > :global(.field:nth-child(n + 6)) { --intro-order: 5; }
    > :global(.field.title ~ .field) { --intro-after-title: 230ms; }
    > :global(.field.links > a:nth-of-type(n + 2)) { --intro-link-order: 1; }
    /* Lines are cut between baseline and cap height only where the box is trimmed to them */
    > :global(.field.title > .text) {
      --intro-line-cut: 1lh;
      @supports (text-box: trim-both cap alphabetic) {
        --intro-line-cut: calc((1cap + 1lh) / 2);
      }
    }
  }

  @media (prefers-reduced-motion: no-preference) {
    .story-layout.intro.use-animations:not(.variant-147, .group-bento, .group-fullscreen-scroll) .text-fields {
      > :global(.field:not(.links, [data-settle])),
      > :global(.field.links:not([data-settle]) > a) {
        animation: intro-rise var(--duration-reveal) var(--ease-settle) backwards;
        animation-delay: calc(var(--intro-delay) + var(--intro-link-order, 0) * 100ms);
      }
      @supports (mask-position: 0 1lh) and (height: 1cap) {
        > :global(.field.title:not([data-settle])) {
          animation-name: intro-lift;
        }
        > :global(.field.title:not([data-settle]) > .text) {
          animation: intro-read var(--duration-reveal) linear var(--intro-delay) backwards;
        }
      }
    }
  }

  /* ═══════════════════════════════════════════════════════════════════════════════════════════════
   * LINKED CARD HOVER
   * The picture of a card that leads somewhere drifts slightly closer while it is pointed at - a
   * slow 3% that answers the pointer without the card itself jumping. Clipped by the image's own
   * frame, so the layout never moves. Scales the outer media box only: a <picture> and the <img>
   * inside it both carry .media-item, and scaling both made it 6%. Not for pictures shown whole
   * (fit-to-frame, SVG): they float inside their box, so the whole picture would grow and seem to
   * jump instead of the view drifting closer. Not in the grid group - linked grid cards already
   * answer with a tinted halo - and not where slideshows, covers, the expanding layouts and the
   * slices of 77/177 (which widen on hover) move their media themselves.
   * ═══════════════════════════════════════════════════════════════════════════════════════════════ */
  @media (hover: hover) and (prefers-reduced-motion: no-preference) {
    :global(.story-block:not(.is_editing)) .story-layout.use-animations:is(.group-single-column, .group-two-column, .group-editorial):not(.variant-77, .variant-177) .entry-previews a.preview-card {
      .preview-images :global(.zoomable-image) {
        overflow: clip;
      }
      .preview-images :global(.zoomable-image > :is(.media-item, .video-wrapper):not(.fit-to-frame, .svg)) {
        transition: scale var(--duration-smooth) var(--ease-settle);
      }
      &:hover .preview-images :global(.zoomable-image > :is(.media-item, .video-wrapper):not(.fit-to-frame, .svg)) {
        scale: calc(1 + 0.03 * var(--animation-intensity, 1));
      }
    }
  }

  /* Base settings for mobile */
  @media (max-width: 768px) {
    .story-layout {
      :global(.preamble)    { grid-area: 1 / 2 / 2 / 50;}
      :global(.title)       { grid-area: 2 / 2 / 3 / 50;}
      :global(.description) { grid-area: 3 / 2 / 4 / 50;}
      :global(.links)       { grid-area: 4 / 2 / 5 / 50;}
      .entry-previews       { grid-area: 5 / 2 / 16 / 50;}
    }
  }


  /* Add spacing for grid layouts so texts don't touch the edge of the grid */
  .variant-6, .variant-7, .variant-9, 
  .variant-15, .variant-21, .variant-23, 
  .variant-26, .variant-27, .variant-29, .variant-30, .variant-31, .variant-32, .variant-33, .variant-34, .variant-35, .variant-36, .variant-37, .variant-38, .variant-39, .variant-40, .variant-41, .variant-42, .variant-43, .variant-44, .variant-45, .variant-46, .variant-47, .variant-48, .variant-49, .variant-50, .variant-51, .variant-52, .variant-53, .variant-54, .variant-55, .variant-56,
  .variant-66, .variant-68, .variant-69, .variant-75, .variant-76
  {
    .entry-previews .preview-card .preview-texts :is(.preview-preamble, .preview-title, .preview-description) {
      padding-inline-end: var(--block-spacing-s);
    }
  }
    

  /* Layouts */
  /* ————————————————————————————————————————————— 1 —————————————————————————————————————————————————————————— */
  /* single-column inner-track */
  /* ————————————————————————————————————————————— 57 —————————————————————————————————————————————————————————— */
  /* single-column extended-1-track */
  /* ————————————————————————————————————————————— 58 —————————————————————————————————————————————————————————— */
  /* single-column extended-2-track */
  /* ————————————————————————————————————————————— 59 —————————————————————————————————————————————————————————— */
  /* single-column flex-track */
  /* ————————————————————————————————————————————— 80 —————————————————————————————————————————————————————————— */
  /* single-column flex-track center-aligned-text */
  /* ————————————————————————————————————————————— 84 —————————————————————————————————————————————————————————— */
  /* single-column flex-track center-aligned-text preamble-below-title */
  /* Block spacing is now set via inline style */
  .story-layout:is(
    .variant-1,
    .variant-57,
    .variant-58,
    .variant-59,
    .variant-80,
    .variant-84
  ) {
    @media (max-width: 768px) {
      &:has(.entry-previews){
        :global(.field.last .text) {
          margin-block-end: var(--block-spacing-s);
        }
      }
    }
    @media (min-width: 769px) {
      grid-template-rows: auto auto auto 1fr auto;
      /* grid-area: <row-start> / <column-start> / <row-end> / <column-end>; */
      :global(.preamble)    { grid-area: 1 / var(--start-column-texts) / 2 / var(--end-column-texts);}
      :global(.title)       { grid-area: 2 / var(--start-column-texts) / 3 / var(--end-column-texts);}
      :global(.description) { grid-area: 3 / var(--start-column-texts) / 4 / var(--end-column-texts);}
      :global(.links)       { grid-area: 4 / var(--start-column-texts) / 5 / var(--end-column-texts);}
      .entry-previews       { 
        grid-area: 5 / var(--start-column-entry-previews) / 6 / var(--end-column-entry-previews);
        margin-block-start: var(--block-spacing-s);
      }
    }
  }

  /* Center-aligned single-column text fields (center the text block + text-align) */
  .story-layout:is(.variant-80, .variant-84, .variant-124) {
    --text-align: center;
    /* The entry previews container carries .field too, so it has to be excluded here:
       making it a flex row collapsed every preview card into a single squeezed line
       instead of leaving them stacked as in the other single-column layouts. */
    :global(.field:not(.links):not(.entry-previews)) {
      display: flex;
      justify-content: center;
    }
    :global(.field:not(.links) .text) {
      text-align: center;
      --text-align: center;
      margin-inline: auto;
    }
    :global(.links) {
      justify-content: center;
    }
    :global(.button-label) {
      justify-content: center;
    }
  }

  /* ————————————————————————————————————————————— 123 —————————————————————————————————————————————————————————— */
  /* single-column entry-previews-on-top (inset carousel) texts-below: preamble+title left, description+links right */
  /* ————————————————————————————————————————————— 124 —————————————————————————————————————————————————————————— */
  /* single-column entry-previews-on-top (inset carousel) texts-below center-aligned (shared with 80) */
  /* Previews first, then the texts as one real box (not display: contents), so the text
     arrangement below is the box's own business and never depends on page-grid columns. */
  .story-layout:is(.variant-123, .variant-124) {
    grid-template-rows: auto auto;
    .entry-previews {
      grid-area: 1 / 2 / 2 / 50;
    }
    .text-fields {
      grid-area: 2 / 2 / 3 / 50;
      display: flex;
      flex-direction: column;
      /* The same distance the two-column layouts keep between their texts and previews */
      margin-block-start: var(--block-spacing-m);
    }
    @media (min-width: 769px) {
      .entry-previews {
        grid-area: 1 / var(--start-column-entry-previews) / 2 / var(--end-column-entry-previews);
      }
      .text-fields {
        grid-area: 2 / var(--start-column-texts) / 3 / var(--end-column-texts);
      }
    }
  }
  /* 123: two halves. Preamble + title hold the left edge; description + links take the right
     half, starting level with the first line on the left.
     Columns rather than a grid, because the two halves must stack independently: in a grid the
     links' row starts below the taller side, so a two-line title opened a gap between a short
     description and its links. One forced break gives two runs for two columns, and each run
     keeps its own flow. */
  .story-layout.variant-123 {
    @media (min-width: 769px) {
      .text-fields {
        display: block;
        column-count: 2;
        column-gap: calc(2 * var(--grid-gutter));
        :global(.field) { break-inside: avoid; }
        /* Without a right-hand run there is nothing to break to, and balancing would otherwise
           move the title across on its own */
        :global(.preamble) { break-after: avoid; }
        :global(.description),
        :global(.field:not(.description) + .links) { break-before: column; }
      }
      /* A one-line description (or none) goes to the right edge together with the links,
         mirroring the title on the left edge. A paragraph stays left-aligned in its half. */
      &.short-description .text-fields {
        :global(.description .text) {
          text-align: right;
          --text-align: right;
          margin-inline-start: auto;
        }
        /* A single line reads as the buttons' label, not as a paragraph before them, so it
           sits at half the usual text-to-links distance (2x font-size, set under .story-layout) */
        :global(.description:not(.last) .text) {
          margin-block-end: var(--font-size);
        }
        :global(.links) {
          justify-content: flex-end;
        }
      }
    }
  }

  /* Preamble below title (swap grid rows on mobile + desktop) */
  .story-layout.variant-84 {
    @media (max-width: 768px) {
      :global(.title)       { grid-area: 1 / 2 / 2 / 50;}
      :global(.preamble)    { grid-area: 2 / 2 / 3 / 50;}
    }
    @media (min-width: 769px) {
      :global(.title)       { grid-area: 1 / var(--start-column-texts) / 2 / var(--end-column-texts);}
      :global(.preamble)    { grid-area: 2 / var(--start-column-texts) / 3 / var(--end-column-texts);}
    }
  }

  /* ————————————————————————————————————————————— 2 —————————————————————————————————————————————————————————— */
  /* two-columns assymetric-entry-previews aligned-to-top-left space-between-texts */
  /* ————————————————————————————————————————————— 3 —————————————————————————————————————————————————————————— */
  /* two-columns assymetric-entry-previews aligned-to-top-right space-between-texts */
  /* ————————————————————————————————————————————— 12 —————————————————————————————————————————————————————————— */
  /* two-columns equal-sized-entry-previews entry-previews-aligned-right */
  .variant-2, 
  .variant-3 {
    .entry-previews {
      display: grid;
      grid-auto-flow: dense;
      grid-area: 9 / var(--track-start) / 10 / calc(var(--track-end) - 4)  ;
      grid-template-columns: auto;
      &:has(.preview-card-2) {
        column-gap: var(--grid-gutter);
      }
    }
    @media (max-width: 480px) {
      .entry-previews:has(.preview-card-2) {
        grid-template-columns: 1fr 1fr;
      }
    }    
    @media (min-width: 769px) {
      .entry-previews .preview-card.odd {
        grid-column: 2 / 2;
      }
      .entry-previews .preview-card.even {
        grid-column: 1 / 2;
      }
    }
  }

  /* Block spacing for two-column layouts is now set via inline style */
  .variant-2, 
  .variant-3, 
  .variant-12,
  .variant-112,
  .variant-121,
  .variant-122 {
    .text-fields :global(.field.last) {
      margin-block-end: var(--block-spacing-m);
    }
    @media (max-width: 768px) {
      &:has(.entry-previews){
        :global(.field.last .text) {
          margin-block-end: var(--block-spacing-s);
        }
      }
    }
    @media (min-width: 769px) {
      grid-template-rows: auto auto 1fr;
      .text-fields {
        position: sticky;
        top: calc(var(--dynamic-island-bottom-edge) + var(--block-spacing-m));
        display: flex;
        flex-direction: column;
        align-self: start;
        /* min-height: min(50vw, 4 * var(--max-char-per-line), 80vh); */
        /* :global(.description .text) {
          margin-block-end: calc(2 * var(--font-size));
        } */
        :global(.links) {
          margin-top: auto;
        }
        :global(.preamble .text) {
          margin-block-end: var(--block-spacing-s);
        }
      }
      .entry-previews {
        /* row-gap: var(--grid-gutter); */
        row-gap: var(--block-spacing-m);
        .preview-button {
          width: fit-content;
        }
      }
      .entry-previews .preview-card {
        grid-row: auto;
        margin-block-end: 0;
      }
    }
  }
  /* ————————————————————————————————————————————— 2 —————————————————————————————————————————————————————————— */
  /* two-columns assymetric-entry-previews aligned-to-top-left space-between-texts */
  .variant-2,
  .variant-12,
  .variant-112,
  .variant-121,
  .variant-122 {
    /* Inner gap: the shared one-slot split, see --split-left-end in BlockGrid */
    @media (min-width: 769px) {
      .text-fields {
        grid-area: 1 / var(--track-start) / 2 / var(--split-left-end);
      }
      .entry-previews { 
        grid-area: 1 / var(--split-right-start) / 2 / var(--track-end);
      }
    }
  }
  .variant-2 {
    .entry-previews:has(.preview-card-2) {
        grid-template-columns: 2fr 3fr;
    }
  }
  /* ————————————————————————————————————————————— 3 —————————————————————————————————————————————————————————— */
  /* two-columns assymetric-entry-previews aligned-to-top-right space-between-texts */
  .variant-3 {
    .entry-previews:has(.preview-card-2) {
        grid-template-columns: 3fr 2fr;
    }
    @media (min-width: 769px) {
      .text-fields {
        grid-area: 1 / var(--split-right-start) / 2 / var(--track-end);
      }
      .entry-previews { 
        grid-area: 1 / var(--track-start) / 2 / var(--split-left-end);
      }
    }
  }
  /* ————————————————————————————————————————————— 12 —————————————————————————————————————————————————————————— */
  /* two-columns equal-sized-entry-previews entry-previews-aligned-right */
  .variant-12,
  .variant-112 {
    .entry-previews {
      display: flex;
      flex-direction: column;
    }
  }

  /* ————————————————————————————————————————————— 112 —————————————————————————————————————————————————————————— */
  /* Mirror of variant-12: two-columns equal-sized-entry-previews entry-previews-aligned-left */
  /* ————————————————————————————————————————————— 122 —————————————————————————————————————————————————————————— */
  /* Mirror of variant-121 */
  /* Placed explicitly rather than by flipping `direction`: an rtl grid mirrors the line numbers
     too, which put the empty slot on the other side of the centre and gave the mirror a
     different pair of edges from every other two-column block. */
  .variant-112,
  .variant-122 {
    @media (min-width: 769px) {
      .text-fields {
        grid-area: 1 / var(--split-right-start) / 2 / var(--track-end);
      }
      .entry-previews {
        grid-area: 1 / var(--track-start) / 2 / var(--split-left-end);
      }
    }
  }

  /* ————————————————————————————————————————————— 121 —————————————————————————————————————————————————————————— */
  /* two-columns (structure shared with 12) scroll-snap-carousel */
  /* Inset carousel (layouts flagged `inset_carousel`): the host layout places .entry-previews
     as it always does, and this only turns that box into the scroller - activation + markers
     are shared, see SHARED CAROUSEL. The cards stay ordinary preview cards as wide as the box,
     their height following the image's aspect ratio, so crop, fit and focal point all apply. */
  .inset-carousel {
    .entry-previews {
      display: flex;
      align-items: start;
      column-gap: var(--grid-gutter);
      /* Single card: never a scroll container, or it eats the iOS swipe-back gesture.
         See the note on the cover carousel. */
      overflow: clip;
      scrollbar-width: none;
      -ms-overflow-style: none;
      &::-webkit-scrollbar { display: none; }
      .preview-card {
        flex: 0 0 100%;
        min-width: 0;
        scroll-snap-align: center;
        /* Height is the slide's width through its aspect ratio, nothing else. The generic
           viewport cap (same selector, under .story-layout) would cut a wide or square slide
           short and crop it to a ratio nobody chose. */
        :global(.media-item:not(.fit-to-viewport)) { max-height: none; }
      }
    }
    /* Full bleed holds on mobile too; the mobile base rule pins every .entry-previews to 2 / 50 */
    @media (max-width: 768px) {
      &.track-full .entry-previews {
        grid-column: var(--start-column-entry-previews) / var(--end-column-entry-previews);
      }
    }
  }


  /* ————————————————————————————————————————————— 77 —————————————————————————————————————————————————————————— */
  /* two-columns with optional SVG mask overlay and expandable image slices. */
  /* ————————————————————————————————————————————— 177 —————————————————————————————————————————————————————————— */
  /* Mirror of 77: images on the right, text on the left */
  .variant-77,
  .variant-177 {
    @media (min-width: 769px) {
      display: grid;
    }
    .text-fields {
      display: flex;
      flex-direction: column;
      grid-area: 1 / var(--track-start) / 2 / var(--track-end);
      @media (max-width: 768px) {
        margin-block-end: var(--block-spacing-s);
      }
      @media (min-width: 769px) {
        grid-area: 1 / var(--split-right-start) / 2 / var(--track-end);
      }
    }
    .entry-previews { 
      display: grid;
      gap: 0.5px;
      container-type: inline-size;
      grid-template-columns: repeat(var(--preview-card-count), minmax(0, 1fr));
      grid-template-rows: 1fr 0px;
      overflow: hidden;
      grid-area: 2 / var(--track-start) / 3 / var(--track-end);
      @media (min-width: 769px) {
        grid-area: 1 / var(--track-start) / 2 / var(--split-left-end);
      }
      &:hover {
        /* This creates a nice horizontal shift effect, doesn't work well if the image has a transition */
        /* gap: 5px; */
      }
      .preview-card {
        position: relative;
        grid-row: 1 / 2;
        grid-column: var(--preview-card-index) / calc(var(--preview-card-index) + 1);
        height: 100%;
        margin-block-end: 0;
        height: 100%;
        .preview-images {
          width: 100%;
          height: 100%;
        }
      }
      /* no svg (or image with transparency) */
      &:not(:has(.preview-card:first-child :global(.svg))) {
        --offset-correction: 1;
        /** ensure full height of cards */
        aspect-ratio: var(--common-aspect-ratio); 
      }
      &:has(.preview-card:first-child :global(.svg)) {
        --offset-correction: 0;
        .preview-card:first-child {
          grid-column: 1 / calc(var(--preview-card-count) + 1);
          z-index: 1;
          pointer-events: none;
          user-select: none;
        }
      }
      .preview-card:not(:first-child), 
      &:not(:has(.preview-card:first-child :global(.svg))) .preview-card {
        transition: opacity 0.2s cubic-bezier(.45,0,.26,1) 0s;
        .preview-images {
          transform-origin: top left;
          /* no transition on mouse out to avoid flickering */
          :global(img) {
            object-position: calc((var(--preview-card-index) - 2 + var(--offset-correction)) * 100% / (var(--preview-card-count) - 2 + var(--offset-correction))) center !important;
          }
        }
        &:hover {
          opacity: 1;
          z-index: 6;
          position: relative;
          .preview-images {    
            transform-origin: top right;
            /* 
            ** KEY INSIGHT: Use margin-left instead of transform: translateX() to avoid bouncing **
            ** Problem: translateX(%) calculates relative to element's own width (moving target)
            ** Solution: margin-left(%) calculates relative to parent width (stable reference)
            ** This eliminates arc/bouncing during simultaneous width + position animation
            */
            margin-left: calc((var(--preview-card-index) - 2 + var(--offset-correction)) * -100%);
            width: calc(100% * calc(var(--preview-card-count) - 1 + var(--offset-correction)));
            /* Only transition on hover (mouse over), not on mouse out */
            /* Animate both width and margin - margin calculation is stable */
            transition-property: width, margin-left;
            transition-duration: .7s;
            transition-delay: .15s;
            transition-timing-function: cubic-bezier(.45,0,.26,1);
            will-change: width, margin-left;
          }
        }
      }

      /* hover effect */
      /* with svg */
      &:has(.preview-card:not(:first-child):hover) .preview-card:not(:first-child):not(:hover),
      /* no svg */
      &:not(:has(.preview-card:first-child :global(.svg))):has(.preview-card:hover) .preview-card:not(:hover) {
        @media (max-width: 768px) {
          grid-row: 2 / 3;
          transform: translateY(-100%);
          height: calc(100cqw / (var(--preview-card-count) - 1));
          outline: 1px solid var(--color-default-background);
          outline-offset: -0.5px;
          z-index: 20;
          animation: 500ms fade-in ease-out;
        }
        @media (min-width: 769px) {
          opacity: 0;
        }
      }

      /* card adjacent to hovered card - these act as a cursor trap, so we can switch between images and have no stacking issue */
      /* with svg */
      .preview-card:not(:first-child):has(+ .preview-card:hover),
      .preview-card:not(:first-child):hover + .preview-card,
      /* no svg */
      &:not(:has(.preview-card:first-child :global(.svg))) .preview-card:has(+ .preview-card:hover),
      &:not(:has(.preview-card:first-child :global(.svg))) .preview-card:hover + .preview-card {
        z-index: 10;
        /* Override transition with immediate fade-in (0s) but allow fade-out */
        transition: opacity 0s cubic-bezier(.45,0,.26,1) 0s;
      }
    }
  }

  /* Layout 77: Simplified editing mode - disable animations and hover effects for easier reordering */
  :global(.story-block.is_editing.block-selected) .variant-77,
  :global(.story-block.is_editing.block-selected) .variant-177 {
    .entry-previews {
      /* Use simple grid layout without hover effects */
      .preview-card:not(:first-child), 
      &:not(:has(.preview-card:first-child :global(.svg))) .preview-card {
        transition: none;
        &:hover {
          opacity: 1;
          z-index: auto;
          .preview-images {
            margin-left: 0;
            width: 100%;
            transition: none;
            will-change: auto;
          }
        }
      }
      /* Disable fade-out of adjacent cards on hover */
      &:has(.preview-card:not(:first-child):hover) .preview-card:not(:first-child):not(:hover),
      &:not(:has(.preview-card:first-child :global(.svg))):has(.preview-card:hover) .preview-card:not(:hover) {
        opacity: 1;
        transform: none;
        height: auto;
      }
    }
  }

  /* ————————————————————————————————————————————— 177 —————————————————————————————————————————————————————————— */
  /* Mirror of variant-77: two-columns SVG mask with images on the right */
  /* Explicit, not `direction: rtl` - see the note on 112 */
  .variant-177 {
    @media (min-width: 769px) {
      .text-fields {
        grid-area: 1 / var(--track-start) / 2 / var(--split-left-end);
      }
      .entry-previews {
        grid-area: 1 / var(--split-right-start) / 2 / var(--track-end);
      }
    }
  }


  /* ————————————————————————————————————————————— 4 —————————————————————————————————————————————————————————— */
  /* ————————————————————————————————————————————— 14 —————————————————————————————————————————————————————————— */
  /* 14 is a mirror of 4: full-image-right-small-image-left-off-center-texts */
  /* Block spacing is now controlled via inline style */
  /* Full-height layout */
  :global(.story-block:has(:is(.variant-4, .variant-14))) {
    --layout-max-height: 100vh;
  }
  :global(.story-block:not(.is_editing):first-child:has(:is(.variant-4, .variant-14))) {
    margin-block-start: calc(-1 * var(--dynamic-island-bottom-edge));
    :is(.variant-4, .variant-14) .text-fields :global(> .first .text) {
      margin-block-start: calc(var(--dynamic-island-bottom-edge) + var(--block-spacing-l));
    }
  }
  .variant-4,
  .variant-14 { /* full-image-left-small-image-right-off-center-texts */
    min-height: 100vh;
    /* Override default padding - spacing is applied as margin */
    padding-block: 0;
    margin-block-start: var(--block-spacing-top);
    margin-block-end: var(--block-spacing-bottom);
    @media (max-width: 768px) {
      :global(.preamble)    { grid-area: 2 / 2 / 3 / 50;}
      :global(.title)       { grid-area: 3 / 2 / 4 / 50;}
      :global(.description) { grid-area: 4 / 2 / 5 / 50;}
      :global(.links)       { 
        grid-area: 5 / 2 / 6 / 50;
        margin-block-start: var(--block-spacing-s);
        :global(> *:last-child .text) {
          margin-block-end: 0;
        }
      }
      :global(.field){
        display: flex;
        flex-direction: column;
        /* Center text fields on mobile */
        align-items: center;
        :global(.button-label){
          justify-content: center;
        }
      }
      .entry-previews { 
        grid-area: 1 / 1 / 2 / -1;
        display: grid;
        grid-template-columns: 1fr;
        &:has(.preview-card-2) {
          grid-template-columns: 1fr 1fr;
        }
        margin-block-end: var(--block-spacing-l);
        gap: var(--grid-gutter);
      }
      :global(*){
        text-align: center;
        --text-align: center;
      }
    }
    @media (min-width: 769px) {
      --texts-start-column: 29;
      --end-of-first-row: 7;
    }
    @media (min-width: 1281px) {
      --texts-start-column: 31;
    }
    @media (min-width: 769px) {
      --links-start-row: 4;
      &:has(.description) {
        --description-row-height: auto;
      }
      &:has(.preview-card-2) {
        --small-image-row-max-height: max(30vh,300px);
        --links-start-row: 5;
      }
      &:has(.preview-card-2):not(:has(.description)) {
        --small-image-row-max-height: calc(70vh - var(--dynamic-island-bottom-edge));
      }
      &:not(:has(.preview-card-2)) {
        --links-row-height: 1fr;
      }
      grid-template-rows: auto auto var(--description-row-height, 1fr) var(--small-image-row-max-height, 0px) var(--links-row-height, auto);

      /* texts */
      .text-fields :global(> .first .text) {
      margin-block-start: var(--block-spacing-l);
    }
      .text-fields :global(> .last > .text) {
        margin-block-end: var(--block-spacing-l);
      }
      :global(.preamble)    { grid-area: 1 / var(--texts-start-column) / 2 / 50;}
      :global(.title)       { grid-area: 2 / var(--texts-start-column) / 3 / 50;}
      :global(.description) { 
        grid-area: 3 / var(--texts-start-column) / 4 / 50;
        :global(.text){
          margin-block-end: var(--block-spacing-m);
        }
      }
      :global(.links) { 
        grid-area: var(--links-start-row) / var(--texts-start-column) / calc(var(--links-start-row) + 1) / 50;
        display: flex;
        align-items: flex-start;
        margin-block: var(--block-spacing-m);
      }
      &:has(.preview-card-3) {
        :global(.links > *:last-child) {
          margin-block-end: var(--block-spacing-s);
        }
      }
      
      /* cards */
      .entry-previews{
        display: contents;
        .preview-images {
          width: 100%;
        }
      }
      .entry-previews .preview-card {
        margin: 0;
      }
      .entry-previews .preview-card.odd.first {
        grid-area: 1 / 1 / calc( var(--end-of-first-row) - 1) / 26;
        display: flex;
        justify-content: flex-end;
        height: max(100%, 100vh);
        :global(.preview-images) {
          position: sticky;
          bottom: 0;
        }
      }
      .entry-previews .preview-card.even.second {
        grid-area: 4 / var(--texts-start-column) / 5 / 48;
        :global(.preview-images) {
          height: var(--small-image-row-max-height);
        }
        :global(.media-item) {
          width: auto;
          object-fit: contain;
          object-position: top left;
        }
      }

      /* 1. Group A (1,5,9,13,...): */
      .entry-previews .preview-card.every-4-starting-1 {
        grid-area: calc(var(--end-of-first-row) + var(--preview-card-index)) / 1 / calc(var(--end-of-first-row) + var(--preview-card-index)) / 26;
      }
      /* 2. Group B (2,6,10,14,...): */
      .entry-previews .preview-card.every-4-starting-2 {
        grid-area: calc(var(--end-of-first-row) + var(--preview-card-index) - 1) / 31 / calc(var(--end-of-first-row) + var(--preview-card-index)) / 42;
      }
      /* 3. Group C (3,7,11,15,...): */
      .entry-previews .preview-card.every-4-starting-3 {
        grid-area: calc(var(--end-of-first-row) + var(--preview-card-index)) / 10 / calc(var(--end-of-first-row) + var(--preview-card-index)) / 21;
      }
      /* 4. Group D (4,8,12,16,...): */
      .entry-previews .preview-card.every-4-starting-4 {
        grid-area: calc(var(--end-of-first-row) + var(--preview-card-index) - 1 ) / 26 / calc(var(--end-of-first-row) + var(--preview-card-index) - 1) / 50;
      }

      .entry-previews .preview-card.every-4-starting-1,
      .entry-previews .preview-card.every-4-starting-4 {
        height: var(--layout-max-height);
        :global(.preview-images) {
          height: var(--layout-max-height);
          :global(.media-item) {
            object-fit: cover;
            max-height: 100vh;
          }
        }
      }

      .entry-previews .preview-card.every-4-starting-3 {
        height: var(--layout-max-height);
        :global(.preview-images) {
          height: auto;
          max-height: 70vh;
        }
      }
      .entry-previews .preview-card.every-4-starting-2,
      .entry-previews .preview-card.every-4-starting-3 {      
        display: flex;
        justify-content: center;
        align-items: flex-start;        
      }
    }
  }

  /* ————————————————————————————————————————————— 14 —————————————————————————————————————————————————————————— */
  /* Mirror of variant-4: full-image-right-small-image-left-off-center-texts */
  .variant-14 {
    @media (min-width: 769px) {
      direction: rtl;
      :global(> *) {
        direction: ltr;
      }
    }
  }

  /* ————————————————————————————————————————————— 5 —————————————————————————————————————————————————————————— */
  /* full-image-left tiny-image-right texts-center-aligned-in-right-column */
  /* ————————————————————————————————————————————— 28 —————————————————————————————————————————————————————————— */
  /* full-image-right tiny-image-left texts-center-aligned-in-left-column (reverse of variant-5) */
  /* ————————————————————————————————————————————— 70 —————————————————————————————————————————————————————————— */
  /* full-image-left tiny-image-right texts-center-aligned-in-right-column preamble-below-title */
  /* ————————————————————————————————————————————— 71 —————————————————————————————————————————————————————————— */
  /* full-image-right tiny-image-left texts-center-aligned-in-left-column preamble-below-title (reverse of variant-70) */
  /* Block spacing is now controlled via inline style */
  :global(.story-block:has(:is(.variant-5, .variant-28, .variant-70, .variant-71))) {
    --layout-max-height: calc(100vh - var(--dynamic-island-bottom-edge));
  }
  :global(.story-block:first-child:has(:is(.variant-5, .variant-28, .variant-70, .variant-71))) {
    --layout-max-height: 100vh;
  }
  :global(.story-block:not(.is_editing):first-child:has(:is(.variant-5, .variant-28, .variant-70, .variant-71))) {
    margin-block-start: calc(-1 * var(--dynamic-island-bottom-edge));
  }
  .variant-5,
  .variant-28,
  .variant-70,
  .variant-71 {
    @media (max-width: 768px) {
      --second-image-height: 50px;
    }
    @media (min-width: 769px) {
      /* --second-image-height: 100px; */
      --second-image-height: calc(100vw / 13.6); /* Colbourns specific: the aspect ratio of the second image is 1360:100 */
    }
    /* --single-line-text-row-height: var(--dynamic-island-bottom-edge); */
    --single-line-text-row-height: 50px;
    /* the space that's left for the space above the preamble and the title field */
    --flex-row-height: calc((100vh - var(--dynamic-island-bottom-edge) - 3 * var(--single-line-text-row-height) - var(--second-image-height)) / 2);

    min-height: max(100%, var(--layout-max-height));
    grid-template-rows: 1fr;
    align-items: center;

    /* Entry Previews */
    .entry-previews{
      display: contents;
      .preview-card {
        margin-block-end: 0;
        /* Only support 2 cards */
        &:not(.first):not(.second) {
          display: none;
        }
        .preview-texts {
          display: none;
        }
      }
      :global(.preview-images) {
        height: 100%;
        :global(.media-item){
          width: 100%;
          height: 100%;
          max-height: none !important;
          /* disable parallax. TODO: we should instead explicitly enable animations per layout, or introduce dedicated animations */
          animation: none;
        }
      }
    }
    .entry-previews .preview-card.odd { 
      @media (max-width: 768px) {
        /* Top Half */
        grid-area: 1 / 1 / 2 / -1;
        height: 50vh;
      }
      @media (min-width: 769px) {
        /* Left Column */
        grid-area: 1 / 1 / 2 / 26;
        height: var(--layout-max-height);
      }
      :global(.media-item) {
        object-fit: cover;
      }
    }
    .entry-previews .preview-card.even {
      @media (max-width: 768px) {
        grid-area: 3 / 1 / 4 / -1;
        padding-block-end: var(--second-image-height);
      }
      @media (min-width: 769px) {
        grid-area: 1 / 26 / 2 / -1;
      }
      :global(.preview-images){        
        height: var(--second-image-height);
        margin-inline: auto;
        :global(.media-item){
          object-fit: contain;
        }
      }
    }

    &:has(.preview-card-2) {
      @media (min-width: 769px) {
        :global(.title.first), :global(.description.first) {
          margin-block-start: calc(var(--flex-row-height) + var(--dynamic-island-bottom-edge) + 2 * var(--single-line-text-row-height) + var(--second-image-height));
          min-height: var(--single-line-text-row-height);
          margin-block-end: var(--second-image-height);
        }
      }
    }
    &:not(:has(.preview-card-2)):not(:has(:global(.preamble))) {
      @media (min-width: 769px) {
        .text-fields {
          justify-content: center;
        }
      }
    }

    /* Texts */
    .text-fields {
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      :global(.field){
        padding-inline: var(--page-inline-spacing);
      }
      :global(.field .text){
        margin-block: 0;
        margin-inline: auto;
        text-align: center;
        --text-align: center;
      }
      @media (max-width: 768px) {
        grid-area: 2 / 1 / 3 / -1;
        gap: var(--block-spacing-s);
        padding-block: var(--block-spacing-m);
        min-height: calc(50vh - 2 * var(--second-image-height)); /** the height of the image plus its padding-block-end */
        justify-content: center;
      }
      @media (min-width: 769px) {
        grid-area: 1 / 26 / 2 / -1;
        :global(.preamble) {
          margin-block-start: calc(var(--flex-row-height) + var(--dynamic-island-bottom-edge));
          min-height: var(--single-line-text-row-height);
          margin-block-end: var(--second-image-height);
        } 
        :global(.title) {
          margin-block-start: var(--single-line-text-row-height);
        }
        :global(.links) {
          margin-block-start: auto;
          min-height: var(--single-line-text-row-height);
          margin-block-end: var(--block-spacing-m);
        } 
        :global(.description) {
          margin-block: var(--block-spacing-m);
        }

      }
    }
  }

  /* ————————————————————————————————————————————— 28 —————————————————————————————————————————————————————————— */
  /* full-image-right tiny-image-left texts-center-aligned-in-left-column */
  /* reverse of 5 */
  /* ————————————————————————————————————————————— 71 —————————————————————————————————————————————————————————— */
  /* full-image-right tiny-image-left texts-center-aligned-in-left-column preamble-below-title */
  /* reverse of 70 */
  .variant-28,
  .variant-71 {
    direction: rtl;
    :global(> *) {
      direction: ltr;
    }
  }

  /* ————————————————————————————————————————————— 70 —————————————————————————————————————————————————————————— */
  /* full-image-left tiny-image-right texts-center-aligned-in-right-column preamble-below-title */
  /* ————————————————————————————————————————————— 71 —————————————————————————————————————————————————————————— */
  /* full-image-right tiny-image-left texts-center-aligned-in-left-column preamble-below-title */
  /* reverse of 70 */
  .variant-70,
  .variant-71 {
    &:not(:has(.preview-card-2)) {
      .text-fields  {
        justify-content: center;
        :global(.preamble) {
          order: 2;
          margin-block: var(--block-spacing-l);
        }
        :global(.title) {
          order: 1;
          margin-block-start: var(--block-spacing-m);
        }
        :global(.description) {
          order: 3;
        }
        :global(.links) {
          order: 4;
          margin-block-start: var(--block-spacing-m);
        }
      }
    }
  }
  





  


  /* ————————————————————————————————————————————— 30 —————————————————————————————————————————————————————————— */ 
  /* center-aligned inner-track equalised-grid entry-previews-up-to-5-columns one-column-outer-spacing-on-mobile */
  /* ————————————————————————————————————————————— 6 —————————————————————————————————————————————————————————— */ 
  /* center-aligned extended-track equalised-grid entry-previews-up-to-5-columns */
  /* ————————————————————————————————————————————— 32 —————————————————————————————————————————————————————————— */ 
  /* center-aligned flex-track equalised-grid entry-previews-up-to-5-columns */

  /* ————————————————————————————————————————————— 29 —————————————————————————————————————————————————————————— */ 
  /* left-aligned inner-track equalised-grid entry-previews-up-to-5-columns one-column-outer-spacing-on-mobile */
  /* ————————————————————————————————————————————— 23 —————————————————————————————————————————————————————————— */ 
  /* left-aligned extended-track equalised-grid entry-previews-up-to-5-columns */
  /* ————————————————————————————————————————————— 31 —————————————————————————————————————————————————————————— */ 
  /* left-aligned flex-track equalised-grid entry-previews-up-to-5-columns */

  /* ————————————————————————————————————————————— 15 —————————————————————————————————————————————————————————— */ 
  /* left-aligned inner-track equalised-grid up-to-3-column-preview-cards */
  /* ————————————————————————————————————————————— 26 —————————————————————————————————————————————————————————— */ 
  /* left-aligned extended-track equalised-grid up-to-3-column-preview-cards */
  /* ————————————————————————————————————————————— 33 —————————————————————————————————————————————————————————— */ 
  /* left-aligned flex-track equalised-grid up-to-3-column-preview-cards */
  
  /* ————————————————————————————————————————————————————————————————————————————————————————————— */ 
  /* ═══════════════════════════════════════════════════════════════════════════════════════════════
     GRID LAYOUTS - Block spacing and track widths using group classes
     ═══════════════════════════════════════════════════════════════════════════════════════════════ */
  
  /* Full screen grid layouts - add spacing to bottom */
  :global(.story-block:has(.story-layout.group-grid.track-full)) {
    padding-block-end: var(--block-spacing-s);
  }

  /* Spans exactly the block's content track so the width handles sit on the edges they move.
     Falls back to the flex track for layouts that do not derive columns from the track yet,
     which is where their handles are disabled anyway.

     Absolutely positioned on purpose. As an in-flow grid item it was a real child of whatever
     grid the layout happens to define, and layouts that replace the page grid with their own
     (142's 3x3 halo, and anything using display:contents) got implicit columns 4..50 conjured
     up to hold it - which wrecked them in edit mode only. Out of flow it still resolves against
     the grid area for measurement, but contributes nothing to the layout. */
  .story-layout:has(> .track-anchor) {
    position: relative;
  }
  .track-anchor {
    position: absolute;
    grid-row: 1;
    grid-column: var(--start-column-texts, 2) / var(--end-column-texts, 50);
    /* Fills its grid area. Without this it shrink-wraps its own (empty) content to zero
       width, so both handles landed on the same x and the stripes had nothing to mask to. */
    inset-inline: 0;
    height: 0;
    visibility: hidden;
    pointer-events: none;
  }

  /* ═══════════════════════════════════════════════════════════════════════════════════════════════
     TRACK GEOMETRY

     A block's width is a span of the 51-column page grid; its position is where that span
     sits. Both are derived here rather than enumerated per layout, so any group whose CSS
     reads --start-column-* gets every width and position for free.

     Centre is the default. Left and right only differ from centre while the track leaves
     slack to move into, which is why the position control hides itself on flex and full
     (see track_has_slack() in StoryLayouts.js) - an inert control is worse than no control.

     Currently consumed by group-grid and the single-column variants. Cover, editorial and
     bento still hard-code grid-column per variant, which is why GROUP_TRACKS does not list
     them yet.

     NEXT: cover converts cleanly and is the biggest win (30 ids -> 3 shapes). Its centred
     variants already use exactly the centred track spans, and its Left/Right variants are
     the track's start (or end) to the centre gutter - verified against the existing values:
       variant-104 (flex, left)  = 2 / 25  = var(--track-start) / var(--center-gutter-track-start)
       variant-105 (ext-1, left) = 10 / 25 = same formula
       variant-106 (inner, left) = 14 / 25 = same formula
     So cover's "position" means half the track on that side rather than the whole track
     shifted, which preserves the existing composition and gives the missing extended-2
     variants for free. Note that means cover must read the *centred* start/end, not the
     shifted ones, and its max-width:1024px rules need carrying over.
     ═══════════════════════════════════════════════════════════════════════════════════════════════ */

  .story-layout:is(.track-inner, .track-extended-1, .track-extended-2, .track-flex) {
    /* The span centred on the page, before any alignment shift. Groups that place content
       against the page centre rather than against their own edge (cover, FAQ) need this
       pair - --track-start is rewritten by .track-align-left and would move their centre. */
    --track-centred-start: calc((52 - var(--track-span-cols)) / 2);
    --track-centred-end: calc(var(--track-centred-start) + var(--track-span-cols));

    /* Centred: equal slack either side of the 51-column grid */
    --track-start: var(--track-centred-start);
    --track-end: calc(var(--track-start) + var(--track-span-cols));

    &.track-align-left {
      --track-start: 2;
    }
    &.track-align-right {
      --track-start: calc(50 - var(--track-span-cols));
    }

    --start-column-texts: var(--track-start);
    --end-column-texts: var(--track-end);
    --start-column-entry-previews: var(--track-start);
    --end-column-entry-previews: var(--track-end);
  }

  .story-layout.track-inner {
    --track-span-cols: 24;
    /* Narrower tracks would leave too little room on small screens, so they widen there */
    @media (max-width: 768px) {
      --track-span-cols: 20;
    }
  }
  .story-layout.track-extended-1 { --track-span-cols: 32; }
  .story-layout.track-extended-2 { --track-span-cols: 40; }
  .story-layout.track-flex       { --track-span-cols: 48; }

  /* Full bleed is not a span - the previews go edge to edge while the texts stay inset
     so they never touch the viewport edge. Position does not apply. */
  .story-layout.track-full {
    --start-column-texts: 2;
    --end-column-texts: 50;
    --start-column-entry-previews: 1;
    --end-column-entry-previews: 51;
  }
  .story-layout.group-grid.track-full {
    .entry-previews .preview-card {
      :is(.preview-preamble, .preview-title, .preview-description) {
        margin-inline-start: var(--page-inline-spacing);
      }
    }
  }

  /* ═══════════════════════════════════════════════════════════════════════════════════════════════
     COLUMN COUNTS - Using group classes
     ═══════════════════════════════════════════════════════════════════════════════════════════════ */
  
  /* Up to 6 columns */
  .story-layout.group-grid.columns-6 {
    --grid-column-count-mobile: 3;
    --grid-column-count-tablet: 4;
    --grid-column-count-desktop: 6;
  }
  
  /* Up to 5 columns */
  .story-layout.group-grid.columns-5 {
    --grid-column-count-mobile: 2;
    --grid-column-count-tablet: 3;
    --grid-column-count-desktop: 5;
  }
  
  /* Up to 4 columns */
  .story-layout.group-grid.columns-4 {
    --grid-column-count-mobile: 2;
    --grid-column-count-tablet: 2;
    --grid-column-count-desktop: 4;
  }
  /* Up to 3 columns */
  .story-layout.group-grid.columns-3 {
    --grid-column-count-mobile: 1;
    --grid-column-count-tablet: 2;
    --grid-column-count-desktop: 3;
  }
  
  /* Up to 2 columns */
  .story-layout.group-grid.columns-2 {
    --grid-column-count-mobile: 1;
    --grid-column-count-tablet: 2;
    --grid-column-count-desktop: 2;
  }

  /* ═══════════════════════════════════════════════════════════════════════════════════════════════
     GRID LAYOUT STRUCTURE - Main grid template for all grid layouts
     ═══════════════════════════════════════════════════════════════════════════════════════════════ */
  
  .story-layout.group-grid {
    grid-template-rows: auto auto 1fr auto;
    :global(.preamble)    { grid-area: 1 / var(--start-column-texts) / 2 / var(--end-column-texts);}
    :global(.title)       { grid-area: 2 / var(--start-column-texts) / 3 / var(--end-column-texts);}
    :global(.description) { grid-area: 3 / var(--start-column-texts) / 4 / var(--end-column-texts);}
    :global(.links)       { grid-area: 4 / var(--start-column-texts) / 5 / var(--end-column-texts);}

    &:not(:has(.entry-previews)) {
      :global(.title:last-child) {
        :global(.text) {
          margin-block-end: 0;
        }
      }
    }
    &:has(.entry-previews){
      .text-fields :global(.field.last) { 
        margin-block-end: 1.5em;
      }
    }
    .entry-previews { 
      display: grid;
      grid-row: 5 / 6;
      /* TODO: We need a better solution to preserve the track width intent on mobile (so on smaller screens the entry previews also occupy less space). 
      But for now keep the desktop width */
      grid-column: var(--start-column-entry-previews) / var(--end-column-entry-previews);
      /* grid-column: min( var(--start-column-entry-previews) + 8, 18) / max(var(--end-column-entry-previews) - 8, 34); */
      @media (min-width: 769px) {
        grid-column: min( var(--start-column-entry-previews) + 4, 18) / max(var(--end-column-entry-previews) - 4, 34);
      }
      @media (min-width: 1025px) {
        grid-column: var(--start-column-entry-previews) / var(--end-column-entry-previews);
      }
      grid-template-columns: repeat(min(var(--grid-column-count-mobile), var(--entry-previews-count)),minmax(0, 1fr));
      /* Rows are auto-sized; subgrid cards inherit these tracks for consistent row heights */
      grid-auto-rows: auto;
      column-gap: var(--grid-gutter);
      /* Row gap applies to all rows including subgrid rows within cards */
      row-gap: 0;
      /* Offset extra margin from last row of cards */
      --card-margin: var(--block-spacing-s);
      &:has(:global(.preview-title)) { --card-margin: var(--block-spacing-m); }
      &:has(:global(.preview-button)) { --card-margin: var(--block-spacing-l); }
      margin-block-end: calc(-1 * var(--card-margin));

      .preview-card{
        /* Span 5 rows: image, preamble, title, description, button */
        grid-row: span 5;
        /* Inherit row sizing from parent for consistent heights across cards */
        grid-template-rows: subgrid;
        display: grid;
        /* Spacing between card rows */
        margin-block-end: var(--card-margin);

        /* Make it obvious that the card is clickable */
        &a:not(:has(.preview-button)) {
          --hover-color: oklch(
            from 
            var(--color-background, var(--color-default-background)) 
            calc(l - var(--color-default-background-lux-mode) * 0.05) c h
          );
          transition: all 0.12s ease-in-out;
          @media (hover: hover) {
            &:has(.preview-images){
              &:hover {
                box-shadow: 0 0 0 calc(0.3 * var(--grid-gutter)) var(--hover-color);
                background: var(--hover-color);
              }
            }
            &:not(:has(.preview-images)){
              &:hover {
                :global(.preview-title) {
                  text-decoration: underline;
                }
              }
            }
          }
        }

        /* Assign explicit grid rows to each content type for subgrid alignment */
        .preview-images {
          grid-row: 1;
          margin-block-end: 0;
          :global(.media-item){
            width: 100%;
            min-width: 100%;
            height: auto;
            animation-name: fade-in-hold !important;
            &.fit-to-frame {
              object-position: top center;
            }
          }
        }
        &:has(.preview-images):has(:global(.text:first-child)) .preview-images {
          margin-block-end: var(--size-5);
        }
        .preview-texts {
          display: contents;          
          :global(.text){
            max-width: 100%;
            text-align: left;
            overflow-wrap: anywhere;
          }
          :global(.text:last-child) {
            margin-block-end: 0;
          }
          :global(.preview-preamble) { grid-row: 2; }
          :global(.preview-title) { grid-row: 3; }
          :global(.preview-description) { grid-row: 4; }
          :global(.preview-button) { grid-row: 5; }
        }
        /* Without a picture the texts start right under the card's rule instead of waiting in
           the rows below the neighbours' pictures, which left a hole the height of an image */
        &.text-only .preview-texts {
          display: flex;
          flex-direction: column;
          grid-row: 1 / span 5;
          align-self: start;
        }
      }
    }
    @media (min-width: 769px) {
      .entry-previews{
        grid-template-columns: repeat(min(var(--grid-column-count-tablet), var(--entry-previews-count)),minmax(0, 1fr));
      }
      /* If there are exactly 3 items but the variant caps at 2 columns,
         show 3 columns on tablet to avoid a lonely row with just one item */
      &:is(
        .variant-49,
        .variant-50,
        .variant-51,
        .variant-52,
        .variant-53,
        .variant-54,
        .variant-7,
        .variant-55,
        .variant-9,
        .variant-27,
        .variant-21,
        .variant-56,
        .variant-76
      ) {
        .entry-previews:has(> :nth-child(3):last-child) {
          grid-template-columns: repeat(3, minmax(0, 1fr));
        }
      }
    }
    @media (min-width: 1281px) {
      .entry-previews{
        grid-template-columns: repeat(min(var(--grid-column-count-desktop), var(--entry-previews-count)),minmax(0, 1fr));
      }
    }
    /* Apply common aspect ratio to all grid layout images */
    .entry-previews .preview-card .preview-images {
      :global(.media-item:not(.fit-to-frame)) {
        aspect-ratio: var(--common-aspect-ratio);
      }
    }
  }


  /* Center aligned variation */
  .story-layout:is(
    .variant-6,
    .variant-30,
    .variant-32,
    .variant-34,
    .variant-35,
    .variant-36,
    .variant-66,
    .variant-40,
    .variant-41,
    .variant-42, 
    .variant-44, 
    .variant-47, 
    .variant-48,
    .variant-54,
    .variant-7,
    .variant-55,
    .variant-9,
    .variant-27,
    .variant-21,
    .variant-56,
    .variant-69
  ){
    .text-fields :global(.field) {
      display: flex;
      justify-content: center;
    }
    .text-fields :global(.field .text),
    .entry-previews .preview-card :global(.preview-title){
      text-align: center;
      --text-align: center;
    }
    :global(.button-label) {
      justify-content: center;
    }
  }  


  /* ————————————————————————————————————————————— 75 —————————————————————————————————————————————————————————— */ 
  /* left-aligned extended-2-track equalised-grid entry-previews-up-to-5-columns entry-titles-above-image */
  /* TODO: We should make the order of title and images in a preview card configurable by drag and drop */
  /* Using .story-layout.variant-75 for higher specificity to override base :is() styles */
  .story-layout.variant-75 {
    .entry-previews {
      /* Override default 5-row subgrid: preamble/title above image needs 4 rows */
      --card-margin: var(--block-spacing-m);
      
      .preview-card {
        /* Span 4 rows: preamble, title, image, description, button */
        grid-row: span 5;
        grid-template-rows: subgrid;
        
        .preview-images {
          grid-row: 3;
          margin-block-start: var(--size-3);
        }
        .preview-texts {
          display: contents;
          :global(.preview-preamble) {
            grid-row: 1;
            margin-block-start: 0;
          }
          :global(.preview-title) {
            grid-row: 2;
            align-content: flex-end;
            margin-block-start: 0;
          }
          :global(.preview-description) {
            grid-row: 4;
          }
          :global(.preview-button) {
            grid-row: 5;
            margin-block-start: var(--size-3);
            width: fit-content;
          }
        }
      }
    }
  }

  /* ————————————————————————————————————————————— 76 —————————————————————————————————————————————————————————— */ 
  /* Links below previews. Images linking to primary link. left-aligned flex-track equalised-grid entry-previews-up-to-3-columns */
  .variant-76 > .field.entry-previews {
    grid-row: 4 / 5;
  }
  .variant-76 {
    :global(.preamble)    { grid-area: 1 / var(--start-column-texts) / 2 / var(--end-column-texts);}
    :global(.title)       { grid-area: 2 / var(--start-column-texts) / 3 / var(--end-column-texts);}
    :global(.description) { grid-area: 3 / var(--start-column-texts) / 4 / var(--end-column-texts);}
    :global(.links)       { grid-area: 5 / var(--start-column-texts) / 6 / var(--end-column-texts);}
  }

  /* ————————————————————————————————————————————— 66 —————————————————————————————————————————————————————————— */ 
  /* center-aligned flex-track equalised-grid entry-previews-up-to-3-columns - entry-preview-description-on-hover over image */
  /* Using .story-layout.variant-66 for higher specificity to override base :is() styles */
  .story-layout.variant-66 {
    .entry-previews {
      .preview-card {
        /* Override subgrid - single cell grid for overlay */
        grid-row: span 1;
        display: grid;
        grid-template-rows: auto;
        grid-template-columns: 1fr;
        
        .preview-images {
          grid-area: 1 / 1 / 2 / 2;
          transition: all 0.3s ease-in-out;
        }
        .preview-texts {
          display: contents;
          :global(.preview-description) {
            grid-area: 1 / 1 / 2 / 2;
            z-index: 2;
            /* Stretch to fill cell and use grid for centering */
            place-self: stretch;
            display: grid;
            place-content: center;
            padding: var(--block-spacing-m);
            text-align: center;
            --text-align: center;
            opacity: 0;
            transition: all 0.3s ease-in-out;
            margin: 0;
            min-height: 100%;
          }
        }
        &:hover {
          .preview-images {
            opacity: 0.2;
            filter: brightness(1.1);
          }
          .preview-texts :global(.preview-description) {
            opacity: 1;
          }
        }
      }
    }
  }
  

  /* ————————————————————————————————————————————— 21 —————————————————————————————————————————————————————————— */
  /* center-aligned inner-track equalised-grid entry-previews-up-to-2-columns description-sticky-bottom-left */
  /* ————————————————————————————————————————————— 56 —————————————————————————————————————————————————————————— */
  /* center-aligned inner-track equalised-grid entry-previews-up-to-2-columns description-sticky-bottom-right */
  .story-layout:is(.variant-21) {
    @media (min-width: 1281px) {
      .text-fields :global(.description) {
        grid-area: 1 / 2 / 6 / 13;
      }
    }
  }
  .story-layout:is(.variant-56) {
    @media (min-width: 1281px) {
      .text-fields :global(.description) {
        grid-area: 1 / 39 / 6 / 50;
      }
    }
  }
  .story-layout:is(.variant-21, .variant-56) {
    @media (min-width: 1281px) {
      .text-fields :global(.description) {
        height: 100%;
        justify-content: flex-end;
        align-items: flex-end;
        :global(.text) {
          border-top: 1px solid var(--color-default-stroke);
          text-align: start;
          padding-block-start: var(--font-size);
          margin-block-end: 0;
          position: sticky;
          bottom: var(--block-spacing-s);
        }
      }
    }
  }

  /* ————————————————————————————————————————————— 68 —————————————————————————————————————————————————————————— */
  /* left-aligned flex-track equalised-grid entry-previews-up-to-4-columns - change image on hover */
  .variant-68 {
    .entry-previews .preview-card {
      .preview-images {
        display: grid;
        :global(.zoomable-image) {
          grid-area: 1 / 1 / 2 / 2;
          transition: opacity 0.2s ease-in-out;
          transition-behavior: allow-discrete;
          &:has(.placeholder-image) {
            display: none;
          }
        }
        :global(.zoomable-image:nth-child(2)) {
          z-index: 2;
          pointer-events: none;
          opacity: 0;
          @starting-style {
            /* this prevents the additional image from loading and increasing total bandwidth */
            display: none;
          }
        }
      }
      @media (hover: hover) {
        &:hover {
          .preview-images {
            :global(.zoomable-image:nth-child(1)) {
              opacity: 0;
            }
            :global(.zoomable-image:nth-child(2):not(:has(.placeholder-image))) {
              opacity: 1;
              display: block;
              pointer-events: auto;
            }
          }
        }
      }
      &:not(:hover) {
        .preview-images {
          :global(.zoomable-image:nth-child(1)) {
            opacity: 1;
          }
          :global(.zoomable-image:nth-child(2):not(:has(.placeholder-image))) {
            opacity: 0;
            @starting-style {
              opacity: 1;
            }
          }
        }
      }
    }
  }



  /* ————————————————————————————————————————————— 8 —————————————————————————————————————————————————————————— */
  :global(.story-block.has-color-set:has(.variant-8)) {
    :global(.preamble), :global(.title), :global(.links), :global(.description) {
      padding: max(var(--size-6), calc(var(--card-radius) * 0.9));
    }
    :global(.description *), :global(.title * ) {
      margin-block-end: 0;
    }
    :global(.description) { 
      border-radius: var(--card-radius);
      background-color: var(--color-foreground);
      :global(.text){
        color: var(--color-background);
        /* TODO: make this dependend on --color-foreground-lux-mode, e.g. only if 1 apply geometricPrecision */
        text-rendering: geometricPrecision; /** lightens the font-weight */
      }
    }
    :global(.preamble), :global(.title), :global(.links) {
      background-color: var(--color-background);
      height: 100%;
      box-shadow: 0 0 0 1px var(--color-background); /** fix "blitzer" */
    }
  }
  .variant-8 { /* bento-symmetric-diagonal */
    --card-radius: var(--radius-xl);
    background-color: transparent;
    grid-template-rows: auto 1fr auto auto auto auto;
    
    /* update row heights if certain fields are hidden */
    &:global(:not(:has(.description))){
      grid-template-rows: auto auto auto 1fr auto auto;
    }
    &:global(:not(:has(.title))){
      grid-template-rows: auto 0 auto auto auto auto;
    }

    /* set border-radius and margin between fields depending on field visibility */
    :global(:is(.preamble,.title,.links):first-child), 
    .text-fields :global(.description:first-child + .links){
      border-start-end-radius: var(--card-radius);
      border-start-start-radius: var(--card-radius);
    }
    :global(.text-fields:has(.preamble + .title + .description):not(:has(.links)) .title),
    :global(.text-fields:has(.title:first-child + .description):not(:has(.links)) .title),
    :global(.text-fields:has(.preamble + .description):not(:has(.links)) .preamble),
    :global(.text-fields > *:last-child),
    :global(.links) {
      border-end-start-radius: var(--card-radius);
      border-end-end-radius: var(--card-radius); 
    }
    :global(.text-fields:has(.preamble + .description) .preamble .text) {
      margin-block-end: 0;
    }
    .text-fields :global(.preamble + .title ~ .links),
    .text-fields :global(.preamble + .title){
      padding-block-start: 0;
    }
    :global(.text-fields:has(*:is(.preamble, .title) ~ *:is(.title, .links)) :is(.preamble)) {
      padding-block-end: 0;
    }
    :global(.text-fields .title .text) {
      margin-block-end: calc(0.5 * var(--font-size));      
    }

    :global(.preview-card) {
      border-radius: var(--card-radius);
      clip-path: border-box;
      :global(:is(.media-item,.zoomable-image)) {
        border-radius: var(--card-radius);
        max-height: 100%;
      }
    }
    .entry-previews{
      display: contents;
    }
    @media (max-width: 768px) {
      .text-fields {
        display: flex;
        flex-direction: column;
        grid-column: 2 / 50;
      }
      :global(.description) {
        order: 10;
      }
      :global(.description),
      .entry-previews .preview-card {
        margin-block-start: var(--grid-gutter);
      }
      /*
        Keep the diagonal rather than flattening to two equal columns. The pair stays side by
        side, but the tiles are unequal and the right one steps down - which is the whole
        composition in miniature. Equal columns read as a plain two-up, not as a bento.
      */
      .entry-previews .preview-card.odd  { grid-area: 10 / 2 / 13 / 28;}
      .entry-previews .preview-card.even {
        grid-area: 10 / 30 / 13 / 50;
        margin-block-start: var(--block-spacing-m);
      }
    }


    @media (min-width: 769px) {
      &:global(:not(:has(.title,.links))){
        grid-template-rows: auto 0 0 auto auto auto;
      }

      :global(.field) { 
        margin-block-end: 0;
      }
      /* --bento-left is the track; --bento-left-wide is the bleeding column one step wider.
         Holding them a fixed distance apart is what preserves the diagonal at any width. */
      --bento-left: var(--track-start);
      --bento-left-wide: max(2, calc(var(--track-start) - 8));
      --bento-right: var(--track-end);
      :global(.preamble)    { grid-area: 1 / var(--center-gutter-track-end) / 2 / var(--bento-right);}
      :global(.title)       { grid-area: 2 / var(--center-gutter-track-end) / 3 / var(--bento-right);}
      :global(.links)       { grid-area: 3 / var(--center-gutter-track-end) / 4 / var(--bento-right); }
      &:global(:has(.entry-previews) .description) { 
        grid-area: 5 / var(--bento-left) / 6 / var(--center-gutter-track-start);
        margin-block-start: var(--grid-gutter);
      }
      &:global(:not(:has(.entry-previews)) .description) { 
        grid-area: 1 / var(--bento-left) / 6 / var(--center-gutter-track-start);
      }
      :global(.preview-card) {
        margin-block-end: 0;
        margin-block-start: var(--grid-gutter);
      }
      .entry-previews .preview-card.odd { grid-area: calc(6 + var(--preview-card-index)) / var(--bento-left-wide) / calc(7 + var(--preview-card-index)) / var(--center-gutter-track-start)}
      .entry-previews .preview-card.even { grid-area: calc(5 + var(--preview-card-index)) / var(--center-gutter-track-end) / calc(6 + var(--preview-card-index)) / var(--bento-right);}
      .entry-previews .preview-card-1.odd { 
        grid-area: 1 / var(--bento-left) / 5 / var(--center-gutter-track-start);
        height: 100%;
        margin-block-start: 0;
      }
      .entry-previews .preview-card-1.odd :global(:is(img,picture,.preview-images)) { height: 100%; }
      .entry-previews .preview-card-2.even { grid-area: 4 / var(--center-gutter-track-end) / 6 / var(--bento-right);}
    }
  }

  /* ————————————————————————————————————————————— 10 —————————————————————————————————————————————————————————— */
  .variant-10 { /* bento-asymmetric-alternating-flex-width */
    --card-radius: var(--radius-xl);
    background-color: transparent;
    grid-template-rows: auto;
    grid-auto-flow: dense;
    /* Thirds of the track: at extended-1 these are exactly the old stops 21 and 31.
       Rounded explicitly - other spans land on fractions, and while browsers do round grid
       lines themselves, doing it here keeps the two stops from drifting apart by a column. */
    --bento-stop-1: round(calc(var(--track-start) + var(--track-span-cols) * 11 / 32), 1);
    --bento-stop-2: round(calc(var(--track-start) + var(--track-span-cols) * 21 / 32), 1);
    .text-fields {
      grid-area: 1 / var(--track-start) / 2 / var(--bento-stop-1);
      display: flex;
      flex-direction: column;
      animation: fade-in linear both;
      animation-timeline: view(block);
      animation-range: cover 12% cover 20%;
      :global(.field.last:not(.links) .text) {
        margin-block-end: var(--grid-gutter);
      }
    }
    .entry-previews{
      display: contents;
    }
    .text-fields, 
    /* no border-radius for non-cropped images */
    .preview-card:not(:has(.fit-to-frame)) {
      border-radius: var(--card-radius);
      :global(:is(.media-item,.zoomable-image)) {
        border-radius: var(--card-radius);
      }
    }

    /* has visible text fields */
    &.has-text-fields .preview-card {
      grid-row: auto;
      &.every-4-starting-1 { grid-column: calc(var(--bento-stop-1) + 1) / var(--track-end); } /* 1, 8, 15, ... */
      &.every-4-starting-2 { grid-column: var(--track-start) / calc(var(--bento-stop-2) - 1); }  /* 2, 9, 16, ... */
      &.every-4-starting-3 { grid-column: var(--bento-stop-2) / var(--track-end); } /* 3, 10, 17, ... */
      &.every-4-starting-4 { grid-column: var(--track-start) / var(--bento-stop-1); }  /* 4, 11, 18, ... */
    }

    /* has no visible text fields */
    &:not(.has-text-fields) .preview-card {
      grid-row: auto;
      &.every-4-starting-1 { grid-column: var(--track-start) / calc(var(--bento-stop-2) - 1); } /* 1, 8, 15, ... */
      &.every-4-starting-2 { grid-column: var(--bento-stop-2) / var(--track-end); }  /* 2, 9, 16, ... */
      &.every-4-starting-3 { grid-column: var(--track-start) / var(--bento-stop-1); } /* 3, 10, 17, ... */
      &.every-4-starting-4 { grid-column: calc(var(--bento-stop-1) + 1) / var(--track-end); }  /* 4, 11, 18, ... */
    }

    .entry-previews .preview-card {
      overflow: clip;
      &.last:not(.second) {
        margin-block-end: 0;
      }
      &.second-last {
        &:not(:is(.first,.second)) {
          margin-block-end: 0;
        }
      }
      &.last:not(.first) {
        grid-column: var(--track-start) / var(--track-end);
      }
    }
    @media (min-width: 1025px) {
      .text-fields {
        min-height: 30vw;
      } 
      .entry-previews .preview-card :global(.media-item){
        height: 30vw;
      }
    }

    @media (max-width: 1024px) {
      .text-fields {
        margin-block-end: var(--grid-gutter);
      } 
      .entry-previews .preview-card :global(.media-item){
        height: max(40vh,100%);
        height: 50vmin;
      }
      .text-fields { grid-area: 1 / 2 / 2 / 42; }
      .entry-previews .preview-card.odd.first { grid-area: 2 / 2 / 3 / 42 }
      /*
        The alternation is this layout's whole idea, so it survives the collapse: cards keep
        two different widths and keep swapping sides, instead of every card running full width.
      */
      .entry-previews .preview-card:not(.first) {
        &.odd  { grid-column: 2 / 44; }
        &.even { grid-column: 8 / 50; margin-block-start: var(--grid-gutter); }
      }
    }
  }
  :global(.has-color-set .variant-10 .text-fields) {
    padding: max(var(--size-6), calc(var(--card-radius) * 0.9));
    margin-block-end: var(--grid-gutter);
    background-color: var(--color-background);
    overflow: hidden;
    :global(.text) {
      color: var(--color-foreground);
    }
  }

  /* ————————————————————————————————————————————— 11 —————————————————————————————————————————————————————————— */
  .variant-11 { /* asymmetric-minimal-spacious-grid */

    /* TODO: find a better way to preserve the asymmetric grid with lots of whitespace on mobile, currently only visible from tablet size upwards */

    --entry-previews-image-fraction-height: 20vmin;

    /* The whitespace IS the layout, so even the narrow stack keeps its margins rather than
       running edge to edge - going full width is the one change that stops it reading as a
       white cube at all. */
    --col-1-start: var(--track-start);
    --col-1-end: var(--center-gutter-track-start);
    --col-2-start: var(--center-gutter-track-end);
    --col-2-end: var(--track-end);
    @media (min-width: 769px) {
      --col-1-end: 23;
    }
    @media (min-width: 1024px) {
      /* Narrow left column, wide right column meeting at the centre gutter. 13 of the
         track's 40 columns at extended-2, which is what the literals used to be. */
      --col-1-start: var(--track-start);
      --col-1-end: round(calc(var(--track-start) + var(--track-span-cols) * 13 / 40), 1);
      --col-2-start: var(--center-gutter-track-end);
      --col-2-end: var(--track-end);
    }
    
    .text-fields {
      display: flex;
      flex-direction: column;
      padding-block-end: calc(0.5 * var(--entry-previews-image-fraction-height));
      grid-area: 1 / var(--col-1-start) / 2 / var(--col-2-end);
      @media (min-width: 1024px) {
        grid-area: 1 / var(--col-1-start) / 2 / var(--col-1-end);
      }
    }
    
    .entry-previews {
      grid-area: 2 / 1 / 5 / -1;
      display: grid;
      grid-template-columns: inherit;
      .preview-card:not(:last-child) {
        margin-block-end: calc(0.5 * var(--entry-previews-image-fraction-height));
        @media (min-width: 1024px) {
          margin-block-end: calc(1 * var(--entry-previews-image-fraction-height));
        }
      }
      .preview-card.odd {
        grid-column: var(--col-1-start) / var(--col-1-end);
        @media (min-width: 769px) {
          margin-block-start: calc(1 * var(--entry-previews-image-fraction-height));
        }
        @media (min-width: 1024px) {
          margin-block-start: calc(2 * var(--entry-previews-image-fraction-height));
        }
        :global(.media-item) {
          max-height: calc(3 * var(--entry-previews-image-fraction-height));
        }
      }
      .preview-card.even {
        /* no image */
        grid-column: var(--col-2-start) / var(--col-2-end);
        &:global(:has(.landscape)){
          grid-column: var(--col-2-start) / var(--col-2-end);
          :global(.media-item) {
            height: calc(2 * var(--entry-previews-image-fraction-height));
          }
        }
        &:global(:has(.square)){
          grid-column: var(--col-2-start) / var(--col-2-end);
          :global(.media-item) {
            height: calc(3 * var(--entry-previews-image-fraction-height));
          }
        }
        &:global(:has(.portrait)){
          grid-column: 33 / var(--col-2-end);
          :global(.media-item) {
            max-height: calc(4 * var(--entry-previews-image-fraction-height));
          }
        }
      }
    }
  }

  /* ————————————————————————————————————————————— 13 —————————————————————————————————————————————————————————— */
  /* symmetric split-screen preview-images-on-hover preview-images-left preview-texts-right */
  :global(.story-block:has(.variant-13)) {
    --layout-max-height: calc(100vh - var(--dynamic-island-bottom-edge));
  }
  :global(.story-block:first-child:has(.variant-13) .story-layout.variant-13) {    
    --layout-max-height: 100vh;
  }
  :global(.story-block:not(.is_editing):first-child:has(.variant-13) .story-layout.variant-13) {
    margin-block-start: calc(-1 * var(--dynamic-island-bottom-edge));
  }
  .variant-13 {
    --card-radius: var(--radius-xl);
    max-height: var(--layout-max-height);
    /* Override default padding - spacing is applied as margin  */
    padding-block: 0;
    margin-block-start: var(--block-spacing-top);
    margin-block-end: var(--block-spacing-bottom);
    .text-fields {
      display: flex;
      flex-direction: column;
      grid-area: var(--preview-card-index) / 1 / calc(1 + var(--preview-card-index)) / 50;
    }
    .entry-previews {
      grid-area: 2 / 1 / 3 / 50;
        display: grid;
      grid-template-columns: inherit;
      .preview-card {
        overflow: clip;
        .preview-images {
          height: max(var(--layout-max-height), 100%);
          :global(.media-item) {
            width: 100%;
            height: var(--layout-max-height);
            max-height: none;
          }
        }
        .preview-texts {
          z-index: 2;
        }
      }
    }
    /* mobile */
    @media (max-width: 768px) {
      .entry-previews {
        grid-template-rows: repeat(var(--entry-previews-count), var(--layout-max-height));
        .preview-card {
          grid-column: 1 / 50;
          display: grid;
          height: max(var(--layout-max-height), 100%);
          .preview-images {
            grid-area: 1 / 1 / 2 / 2;
          }
          .preview-texts {
            grid-area: 1 / 1 / 2 / 2;
            text-align: center;
            --text-align: center;
            height: fit-content;
            margin: auto auto var(--block-spacing-l) auto;
            padding: var(--size-3) var(--size-3);
            width: fit-content;
            background: rgba(0, 0, 0, 0.05);
            backdrop-filter: blur(10px) invert(0.1) ;
            border-radius: max(var(--card-radius), var(--size-2));
            /* mix-blend-mode: difference; */
            .text {
              color: var(--color-default-background);
            }
            .text:last-child {
              margin-block-end: 0;
            }
          }
        }
      }
    }
    /* desktop */
    @media (min-width: 769px) {
      --column-1-start: 1;
      --column-1-end: 25;
      --column-2-start: 25;
      --column-2-end: 50;
      .text-fields {
        grid-area: 1 / 14 / 2 / 38;
      }
      .entry-previews {
        height: 100%;
        max-height: var(--layout-max-height);
        grid-template-rows: var(--dynamic-island-bottom-edge) var(--block-spacing-m) repeat(var(--entry-previews-count), 1fr) var(--block-spacing-s);
        .preview-card {
          display: contents;
          overflow: hidden;
          .preview-images {
            visibility: hidden;
            opacity: 0;
            /* transition: opacity 50ms cubic-bezier(.005,.315,0,.985); */
            grid-area: 1 / var(--column-1-start) / calc(3 + var(--entry-previews-count)) / var(--column-1-end);
            margin-block-end: 0;
            position: sticky;
            top: 0;
            :global(.media-item) {
              height: 100vh;
              position: sticky;
              top: 0;
            }
          }
          .preview-texts {
            padding-inline-start: var(--grid-gutter);
            padding-inline-end: var(--page-inline-spacing);
            grid-area: calc( var(--preview-card-index) + 2) / var(--column-2-start) / calc(var(--preview-card-index) + 2) / var(--column-2-end);
            height: 100%;
            transition: opacity 300ms cubic-bezier(.005,.315,0,.985);
            /* text-decoration: underline 1px oklch(0 0 0 / 0);
            text-underline-offset: 0.4em;
            transition: 
              text-decoration-color 300ms cubic-bezier(.005,.315,0,.985),
              padding-inline-start 300ms cubic-bezier(.005,.315,0,.985); */
            .preview-description {
              /* truncate */
              overflow-x: clip;
              white-space: nowrap;
              text-overflow: ellipsis;
            }
          }
          /* Show the images on hover */
          &:has(.preview-texts:hover) {
            .preview-images {
              z-index: 1;
              opacity: 1;
              visibility: visible;
            }
            .preview-texts {
              /* text-decoration-color: var(--color-body, var(--color-default-foreground)); */
              opacity: 0.8;
            }
          }
        }
      }
    }
  }
  /* Flip arrangement of columns for consecutive blocks of variant-13 */
  :global(.story-block:has(.variant-13) + .story-block:has(.variant-13) .variant-13) {
    @media (min-width: 769px) {
      --column-1-start: 27;
      --column-1-end: 50;
      --column-2-end: 27;
      --column-2-start: 1;
      .preview-texts {
        padding-inline-end: var(--grid-gutter);
        align-items: flex-end;
      }
    }
  }



  /* ————————————————————————————————————————————— 16 —————————————————————————————————————————————————————————— */
  /* ————————————————————————————————————————————— 116 —————————————————————————————————————————————————————————— */
  /* 116 is a mirror of 16: inner-assymmetric-texts-spread-at-angles-in-bottom-right-corner */
  .variant-16,
  .variant-116 { /* inner-assymmetric-texts-spread-at-angles-in-bottom-left-corner-3-columns-images-span-two-columns-up-to-3-in-a-row */
    /* Text takes 7 of the track's 24 columns at inner, the images the rest, with a one
       column gap between. Kept as fractions so the proportion survives every width. */
    --angled-text-span: round(calc(var(--track-span-cols) * 7 / 24), 1);
    --angled-gap: round(calc(var(--track-span-cols) * 8 / 24), 1);
    --text-col-start: var(--track-start);
    --text-col-end: calc(var(--track-start) + var(--angled-text-span));
    --img-col-start: calc(var(--track-start) + var(--angled-gap));
    --img-col-end: var(--track-end);
    --links-col-start: var(--img-col-start);
    --links-col-end: var(--img-col-end);
    row-gap: var(--grid-gutter);
    grid-template-rows: 1fr auto auto;
    .text-fields {
      display: contents;
      :global(.preamble)    { grid-area: 1 / var(--text-col-start) / 2 / var(--text-col-end);}
      :global(.description) { grid-area: 2 / var(--text-col-start) / 3 / var(--text-col-end);}
      :global(.title)       { grid-area: 3 / var(--text-col-start) / 4 / var(--text-col-end);}
      :global(.links)       { grid-area: 3 / var(--links-col-start) / 3 / var(--links-col-end);}
    }
    .entry-previews {
      grid-area: 1 / var(--img-col-start) / 3 / var(--img-col-end);
      display: grid;
      height: 100%;
      grid-template-columns: 1fr 1fr;
      /* has only 1 card */
      &:not(:has(.preview-card-2)){
        grid-template-columns: 1fr;
      }
      /* ... 3 + images will wrap */
      column-gap: var(--grid-gutter);
      /* fill grid cells */
      .preview-card .preview-images {
        height: 100%;
      }
    }
  }

  /* Mirror columns for variant-116 */
  .variant-116 {
    --text-col-start: calc(var(--track-end) - var(--angled-text-span));
    --text-col-end: var(--track-end);
    --img-col-start: var(--track-start);
    --img-col-end: calc(var(--track-end) - var(--angled-gap));
    --links-col-start: var(--img-col-start);
    --links-col-end: var(--img-col-end);
  }

  /* ————————————————————————————————————————————— 18 —————————————————————————————————————————————————————————— */
  .variant-18, /* text-fields-left preamble-right-edge flex-tracks large-gap-between images-left-in-one-column */
  .variant-110 /* text-fields-right preamble-left-edge flex-tracks large-gap-between images-right-in-one-column */
  { 
    --col-1-start: 6;
    --col-1-end: 50;
    --col-2-start: 10;
    --col-2-end: 50;
    .text-fields {
      display: contents;
      :global(.preamble)    { grid-area: 1 / var(--col-2-start) / 2 / var(--col-2-end); }
      :global(.title)       { grid-area: 2 / var(--col-1-start) / 3 / var(--col-1-end); }
      :global(.description) { grid-area: 3 / var(--col-1-start) / 4 / var(--col-1-end); }
      :global(.links)       { grid-area: 4 / var(--col-1-start) / 5 / var(--col-1-end); }
    }
    .entry-previews {
      margin-block-start: var(--block-spacing-m);
      grid-area: 5 / var(--col-1-start) / 6 / var(--col-1-end);
    }
    @media (min-width: 769px) {
      /* Body column then the margin note, as fractions of the span. At flex these are the
         old 6/29 and 33/50; the note keeps its share of the width at any track. */
      --col-1-start: round(calc(var(--track-start) + var(--track-span-cols) * 4 / 48), 1);
      --col-1-end: round(calc(var(--track-start) + var(--track-span-cols) * 27 / 48), 1);
      --col-2-start: round(calc(var(--track-start) + var(--track-span-cols) * 31 / 48), 1);
      --col-2-end: var(--track-end);
      grid-template-rows: auto auto auto auto auto;
      .text-fields {
        :global(.preamble)    { grid-area: 1 / var(--col-2-start) / 5 / var(--col-2-end); }
        :global(.title)       { grid-area: 1 / var(--col-1-start) / 2 / var(--col-1-end); }
        :global(.description) { grid-area: 2 / var(--col-1-start) / 3 / var(--col-1-end); }
        :global(.links)       { grid-area: 3 / var(--col-1-start) / 4 / var(--col-1-end); }
      }
      .entry-previews {
        grid-area: 4 / var(--col-1-start) / 5 / var(--col-1-end);
      }
    }
    @media (min-width: 1025px) { --col-1-end: 30; }
    @media (min-width: 1281px) { --col-1-end: 32; --col-2-start: 35; }
    @media (min-width: 1681px) { --col-1-end: 34; --col-2-start: 39; }
  }

  /* Flip arrangement for variant-110 (text-fields-right, preamble-left) */
  .variant-110 {
    @media (min-width: 769px)  { --col-1-start: 23; --col-1-end: 46; --col-2-start: 2; --col-2-end: 19; }
    @media (min-width: 1025px) { --col-1-start: 22; }
    @media (min-width: 1281px) { --col-1-start: 20; --col-2-end: 17; }
    @media (min-width: 1681px) { --col-1-start: 18; --col-2-end: 13; }
  }

  /* ————————————————————————————————————————————— 19 —————————————————————————————————————————————————————————— */
  .variant-19 { /* three-columns-in-a-row one-small one-medium one-large */
    &:has(.text-fields) .entry-previews {
      margin-block-start: var(--block-spacing-s);
    }
    @media (min-width: 769px) {
      .text-fields {
        display: contents;
        :global(.preamble)    { grid-area: 1 / var(--track-start) / 2 / round(calc(var(--track-start) + var(--track-span-cols) * 13 / 48), 1);}
        :global(.title)       { grid-area: 2 / var(--track-start) / 3 / round(calc(var(--track-start) + var(--track-span-cols) * 13 / 48), 1); margin-block-start: auto;}
        :global(.title .text), 
        :global(.description .text) { margin-block-end: var(--size-5);}
        :global(.links)       { grid-area: 3 / var(--track-start) / 4 / round(calc(var(--track-start) + var(--track-span-cols) * 13 / 48), 1);}
        :global(.description) { grid-area: 1 / round(calc(var(--track-start) + var(--track-span-cols) * 14 / 48), 1) / 3 / var(--track-end);}
      }
      .entry-previews {
        display: grid;
        grid-area: 4 / 1 / 5 / -1;
        grid-template-columns: inherit;
        height: 100%;
        .every-3-starting-1 {
          grid-column: var(--track-start) / round(calc(var(--track-start) + var(--track-span-cols) * 17 / 48), 1);
        }
        .every-3-starting-2 {
          grid-column: round(calc(var(--track-start) + var(--track-span-cols) * 18 / 48), 1) / round(calc(var(--track-start) + var(--track-span-cols) * 26 / 48), 1);
        }
        .every-3-starting-3 {
          grid-column: round(calc(var(--track-start) + var(--track-span-cols) * 29 / 48), 1) / var(--track-end);
        }
      }
    }
    @media (min-width: 1025px) {
      .entry-previews {
        .every-3-starting-1 {
          grid-column: var(--track-start) / round(calc(var(--track-start) + var(--track-span-cols) * 11 / 48), 1);
        }
        .every-3-starting-2 {
          grid-column: round(calc(var(--track-start) + var(--track-span-cols) * 14 / 48), 1) / round(calc(var(--track-start) + var(--track-span-cols) * 26 / 48), 1);
        }
        .every-3-starting-3 {
          grid-column: round(calc(var(--track-start) + var(--track-span-cols) * 29 / 48), 1) / var(--track-end);
        }
      }
    }
    @media (min-width: 1281px) {
      .entry-previews {
        .every-3-starting-1 {
          grid-column: var(--track-start) / round(calc(var(--track-start) + var(--track-span-cols) * 11 / 48), 1);
        }
        .every-3-starting-2 {
          grid-column: round(calc(var(--track-start) + var(--track-span-cols) * 14 / 48), 1) / round(calc(var(--track-start) + var(--track-span-cols) * 23 / 48), 1);
        }
        .every-3-starting-3 {
          grid-column: round(calc(var(--track-start) + var(--track-span-cols) * 27 / 48), 1) / var(--track-end);
        }
      }
    }
  }


  /* ————————————————————————————————————————————— 20 / 147 ——————————————————————————————————————————————————————— */
  /*  autoplay-crossfade-slideshow full-screen-entry-previews
      20:  block texts top left
      147: block texts bottom centre, revealed after a short delay */

  /* Slideshow intro: the frame settles in once, from slightly oversized and transparent. It
     scales down, not up, so the full-bleed image never shows an edge - the block clips the
     overhang. Runs on the container, so it never touches the cards' own crossfade.
     A plain mount-time animation on purpose, with no hidden resting state waiting for an
     inview class: Block.svelte rewrites the block's class attribute when edit mode toggles,
     which drops at-20/seen-20, and the observer does not fire again for a block that never
     moved - so a slideshow hidden "until at-20" stayed invisible after saving. */
  @keyframes slideshow-settle-in {
    from { opacity: 0; scale: 1.04; }
    to { opacity: 1; scale: 1; }
  }
  @media (prefers-reduced-motion: no-preference) {
    :global(.story-block:not(.is_editing)) :is(.variant-20, .variant-147).use-animations .entry-previews {
      animation: slideshow-settle-in 1.6s cubic-bezier(0.16, 1, 0.3, 1) 0.1s both;
    }
  }

  /* The dissolve. Every slide runs the same loop (count × period), offset by one period per
     slide. A slide fades in ON TOP of the one before it (z-index 3), which stays opaque
     underneath until it is fully covered and only then drops out - two half-transparent
     slides would show the background through both. The slide keeps zooming until it drops
     out, so the zoom never snaps back while it is still visible.
     The visible window is a fixed 1.6s fade + 2.5s rest whatever the slide count, so the
     keyframe percentages depend on the count: one keyframe set per count, 2 to 8. They
     encode --slideshow-fade and --slideshow-dwell below - change those and regenerate:
     fade-in ends at fade/total, z-index drops at 1/count, the slide drops out at
     (period + fade)/total, with total = count × period.
     A single slide, or more than eight, shows the first slide only. */
  @property --slide-zoom {
    syntax: '<number>';
    inherits: true;
    initial-value: 1;
  }

  @keyframes slideshow-2 { 0% { opacity: 0; z-index: 3; --slide-zoom: 1; } 19.512% { opacity: 1; z-index: 3; } 50% { z-index: 2; } 69.512% { opacity: 1; --slide-zoom: 1.04; } 69.522%, 100% { opacity: 0; z-index: 1; --slide-zoom: 1; } }
  :is(.variant-20, .variant-147) .count-2 .preview-card { animation-name: slideshow-2; }
  @keyframes slideshow-3 { 0% { opacity: 0; z-index: 3; --slide-zoom: 1; } 13.008% { opacity: 1; z-index: 3; } 33.333% { z-index: 2; } 46.341% { opacity: 1; --slide-zoom: 1.04; } 46.351%, 100% { opacity: 0; z-index: 1; --slide-zoom: 1; } }
  :is(.variant-20, .variant-147) .count-3 .preview-card { animation-name: slideshow-3; }
  @keyframes slideshow-4 { 0% { opacity: 0; z-index: 3; --slide-zoom: 1; } 9.756% { opacity: 1; z-index: 3; } 25% { z-index: 2; } 34.756% { opacity: 1; --slide-zoom: 1.04; } 34.766%, 100% { opacity: 0; z-index: 1; --slide-zoom: 1; } }
  :is(.variant-20, .variant-147) .count-4 .preview-card { animation-name: slideshow-4; }
  @keyframes slideshow-5 { 0% { opacity: 0; z-index: 3; --slide-zoom: 1; } 7.805% { opacity: 1; z-index: 3; } 20% { z-index: 2; } 27.805% { opacity: 1; --slide-zoom: 1.04; } 27.815%, 100% { opacity: 0; z-index: 1; --slide-zoom: 1; } }
  :is(.variant-20, .variant-147) .count-5 .preview-card { animation-name: slideshow-5; }
  @keyframes slideshow-6 { 0% { opacity: 0; z-index: 3; --slide-zoom: 1; } 6.504% { opacity: 1; z-index: 3; } 16.667% { z-index: 2; } 23.171% { opacity: 1; --slide-zoom: 1.04; } 23.181%, 100% { opacity: 0; z-index: 1; --slide-zoom: 1; } }
  :is(.variant-20, .variant-147) .count-6 .preview-card { animation-name: slideshow-6; }
  @keyframes slideshow-7 { 0% { opacity: 0; z-index: 3; --slide-zoom: 1; } 5.575% { opacity: 1; z-index: 3; } 14.286% { z-index: 2; } 19.861% { opacity: 1; --slide-zoom: 1.04; } 19.871%, 100% { opacity: 0; z-index: 1; --slide-zoom: 1; } }
  :is(.variant-20, .variant-147) .count-7 .preview-card { animation-name: slideshow-7; }
  @keyframes slideshow-8 { 0% { opacity: 0; z-index: 3; --slide-zoom: 1; } 4.878% { opacity: 1; z-index: 3; } 12.5% { z-index: 2; } 17.378% { opacity: 1; --slide-zoom: 1.04; } 17.388%, 100% { opacity: 0; z-index: 1; --slide-zoom: 1; } }
  :is(.variant-20, .variant-147) .count-8 .preview-card { animation-name: slideshow-8; }

  /* Full-height layout */
  :global(.story-block:not(.is_editing):first-child:has(:is(.variant-20:not(:has(.text-fields)), .variant-147))) {
    margin-block-start: calc(-1 * var(--dynamic-island-bottom-edge));
  }
  .variant-20, .variant-147 {
    --slideshow-dwell: 2.5s;
    --slideshow-fade: 1.6s;
    --slideshow-period: calc(var(--slideshow-dwell) + var(--slideshow-fade));
    /* The opening slide rests only briefly: visitors decide to scroll on within a few seconds
       of landing, and with the full dwell up front most of them never saw a second image.
       From the second slide on the regular dwell applies. */
    --slideshow-first-dwell: 1s;
    /* Not dvh: it follows the mobile toolbar while scrolling, and everything below this block
       jumped by the toolbar height whenever the scroll direction changed (screen_height.js) */
    height: var(--screen-height, 100svh);
    width: min(100vw,100%);
    position: relative;
    overflow: hidden;
    /* Override default padding - spacing is applied as margin */
    padding-block: 0;
    margin-block-start: var(--block-spacing-top);
    margin-block-end: var(--block-spacing-bottom);
    .text-fields {
      grid-area: 1 / 10 / 2 / 42;
      display: flex;
      flex-direction: column;
      z-index: 2;
      /* Add top spacing for text fields so they don't stick to the edge */
      margin-block-start: calc(var(--dynamic-island-bottom-edge) + var(--block-spacing-m));
    }
    &:has(.entry-previews) {
      .text-fields {
        margin-block-end: var(--block-spacing-m);
      }
    }
    .entry-previews {
      z-index: 1;
      position: absolute;
      inset: 0;
      /* The mobile base rule places every layout's entry previews in grid row 5. For an
         absolutely positioned grid child that grid area becomes the containing block, so on
         phones the slide stack shrank to one implicit row at the bottom of the frame. */
      grid-area: auto;
      display: grid;
      grid-template-columns: inherit;
      height: 100%;
      width: 100%;
      .preview-card {
        animation-duration: calc(var(--entry-previews-count) * var(--slideshow-period));
        /* Shifted by one fade so the first slide is already opaque when the block appears, and
           by the part of the regular dwell the opening slide skips - the whole loop moves
           earlier by the same amount, so every later slide keeps its full dwell. */
        animation-delay: calc(
          (var(--preview-card-index) - 1) * var(--slideshow-period)
          - var(--slideshow-fade)
          - (var(--slideshow-dwell) - var(--slideshow-first-dwell))
        );
        animation-timing-function: ease-in-out;
        animation-iteration-count: infinite;
        animation-play-state: var(--animation-play-state);

        background-color: var(--color-default-background);
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        opacity: 0;
        z-index: 1;
        &.preview-card-1 {
          opacity: 1;
        }

        display: grid;
        grid-template-columns: inherit;
        grid-template-rows: var(--dynamic-island-bottom-edge) 1fr;
        .preview-images {
          grid-area: 1 / 1 / 3 / -1;
          margin-block-end: 0;
          @media (prefers-reduced-motion: no-preference) {
            scale: var(--slide-zoom);
          }
        }
        .preview-texts {
          justify-content: flex-end;
          z-index: 2;
          grid-area: 2 / 2 / 3 / 50;
          margin-bottom: var(--block-spacing-m);
          mix-blend-mode: difference;
          .text {
            color: var(--color-default-background);
            &:last-child {
              margin-block-end: 0;
            }
          }
        }
        :global(.preview-images .media-item) {
          height: var(--screen-height, 100svh);
          max-height: var(--screen-height, 100svh);
          width: min(100vw,100%);
          max-width: none;
          /* No object-fit here: Image.svelte crops to fill by default and shows the whole image
             when the preview (or its entry) is set to - forcing `contain` ignored that setting */
          /* disable parallax */
          /* TODO: we should instead explicitly enable animations per layout, or introduce dedicated animations */
          animation: none;
        }
      }
    }
  }

  /* 147: the block's texts sit centred on the bottom edge like the bottom-centre covers, and
     fade in while the second image crosses in over the first - early enough that a visitor
     who scrolls on soon has still seen the title. */
  .variant-147 {
    grid-template-rows: 1fr;
    .text-fields {
      grid-row: 1 / 2;
      align-self: end;
      align-items: center;
      text-align: center;
      margin-block-start: 0;
      @media (max-width: 1024px) { grid-column: 2 / 50; }
      @media (min-width: 1025px) { grid-column: var(--track-span-flex); }
      :global(.text) {
        text-align: center;
        --text-align: center;
        margin-inline: auto;
      }
      :global(.links) {
        justify-content: center;
      }
    }
  }
  @media (prefers-reduced-motion: no-preference) {
    :global(.story-block:not(.is_editing)) .variant-147.use-animations .text-fields {
      animation: fade-in 0.8s ease-out 1s both;
    }
  }

  /* Slideshow in edit mode: a contact sheet instead of the stack. Published, every slide sits
     on top of the others and only the crossfade tells them apart - so in edit mode they all
     overlapped, the topmost card took every click, and no slide but one could be reached.
     Here the texts come first, in flow, and the slides follow as a two-column sheet. Each tile
     is half the viewport wide and half as tall, i.e. the published frame's proportions, so
     "crop to fill" / "show whole image" and the focal point look exactly as they will live. */
  :global(.story-block.is_editing) :is(.variant-20, .variant-147) {
    height: auto;
    overflow: visible;
    grid-template-rows: auto auto;
    .text-fields {
      grid-row: 1 / 2;
      margin-block-end: var(--block-spacing-m);
    }
    .entry-previews {
      position: static;
      grid-area: 2 / 1 / 3 / -1;
      height: auto;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 2px;
      .preview-card {
        position: relative;
        height: 50dvh;
        animation: none;
        opacity: 1;
        z-index: auto;
        /* The published card borrows the page grid for its caption; a tile only needs the
           page's inline inset either side of it */
        grid-template-columns: var(--page-inline-spacing) minmax(0, 1fr) var(--page-inline-spacing);
        grid-template-rows: minmax(0, 1fr) auto;
        .preview-images {
          grid-area: 1 / 1 / 3 / -1;
          height: 100%;
          min-height: 0;
        }
        .preview-texts {
          grid-area: 2 / 2 / 3 / 3;
        }
        :global(.preview-images .zoomable-image) {
          height: 100%;
        }
        :global(.preview-images .media-item) {
          height: 100%;
          max-height: none;
          width: 100%;
        }
      }
    }
  }

  /* ————————————————————————————————————————————— 22 —————————————————————————————————————————————————————————— */
  /* assymmetric grid-aligned up-to-3-columns-for-entry-previews flex-track texts-top-left texts-top-right entry-previews-at-end */
  :global(.story-block:not(.is_editing):first-child:has(.variant-22):not(:has(.text-fields))) {
    margin-block-start: calc(-1 * var(--dynamic-island-bottom-edge));
  }
  .variant-22 {
    @media (max-width: 640px) {
      .text-fields {
        display: flex;
        flex-direction: column;
        grid-column: 2 / 50;
        :global(.links) {
          justify-content: space-between;
        }
      }
    }
    @media (min-width: 641px) {
      /* Two columns either side of the shared one-slot split, both following the track. The link
         split marches with the viewport, expressed as an offset from column two's start. */
      --col-1-start: var(--track-start);
      --col-1-end: var(--split-left-end);
      --col-2-start: var(--split-right-start);
      --col-2-end: var(--track-end);
      --link-1-col-end: calc(var(--col-2-start) + 5);
      grid-template-rows: auto 1fr;
      .text-fields {
          display: contents;
        :global(.preamble)    { grid-area: 1 / var(--col-1-start) / 2 / var(--col-1-end);}
        :global(.title)       { grid-area: 2 / var(--col-1-start) / 3 / var(--col-1-end); 
          :global(.text) {
            margin-block-end: 0;
          }
        }
        :global(.description) { grid-area: 1 / var(--col-2-start) / 3 / var(--col-2-end);}
        :global(.links)       { 
          display: contents;
          :global(.primary-link), :global(.secondary-link) {
            width: fit-content;
          }
          :global(.primary-link) {
            grid-area: 3 / var(--col-2-start) / 4 / var(--link-1-col-end);
            :global(.button-label) {
              width: fit-content;
              /* padding: 0; */
            }
          }
          :global(.secondary-link) {
            grid-area: 3 / calc(var(--link-1-col-end) + 1) / 4 / var(--col-2-end);
            :global(.button-label) {
              width: fit-content;
              /* padding: 0; */
            }
          }
        }
      }
      /* Only separate the previews from the texts when there are texts: with every text field
         turned off the previews are the whole block and the gap reads as stray top spacing. */
      &:has(.text-fields) .entry-previews {
        margin-block-start: var(--block-spacing-m);
      }
      .entry-previews {
        grid-area: 4 / var(--col-1-start) / 5 / var(--col-2-end);
        display: grid;
        grid-template-columns: 1fr var(--grid-gutter) 1fr var(--grid-gutter) 1fr var(--grid-gutter) 1fr;
        /* Row rhythm comes from the grid, not from per-card margins: a card that ends a row
           without being the last card would otherwise hang its margin under the final row. */
        row-gap: var(--grid-gutter);
        .preview-card:not(.last) { margin-block-end: 0; }
        &:has(.preview-card-3){
          /* 1 left half, 2 on the right half */
          .preview-card.every-3-starting-1 {
            grid-column: 1 / 4;
          }
          .preview-card.every-3-starting-2 {
            grid-column: 5 / 6;
          }
          .preview-card.every-3-starting-3 {
            grid-column: 7 / 8;
          }
        }
        &:has(.preview-card-2):not(:has(.preview-card-3)){
          /* 1 left half, 1 on the right half */
          .preview-card.odd {
            grid-column: 1 / 4;
          }
          .preview-card.even {
            grid-column: 5 / 8;
          }
        }
        &:has(.preview-card-1):not(:has(.preview-card-2)){
          /* 1 full width */
          .preview-card {
            grid-column: 2 / 10;
          }
        }
      }
    }
    @media (min-width: 1281px) {
      --link-1-col-end: 34;
    }
    @media (min-width: 1681px) {
      --link-1-col-end: 38;
    }

  }


  /* ————————————————————————————————————————————— 24 —————————————————————————————————————————————————————————— */
  /* definition half-screen-width right-side-container-column horizontal-aligned-texts two-columns offset-by-1-inner-column entry-previews-up-to-3-columns-right-column */
  :global(.grid-size-full-screen:has(> .story-block .variant-24) .variant-24) {
    /* Term column stays a constant 9-column measure; the definition column takes the rest,
       which is the right way round for a definition list. */
    --col-1-start: var(--track-start);
    --col-1-end: calc(var(--track-start) + 9);
    --col-2-start: calc(var(--track-start) + 10);
    --col-2-end: var(--track-end);
  }
  :global(:is(.grid-size-left-half-screen,.grid-size-right-half-screen):has(> .story-block .variant-24) .variant-24) {
    --col-1-start: 2;
    --col-1-end: 50;
    --col-2-start: 2;
    --col-2-end: 50;
    
    --preamble-area: 1 / var(--col-1-start) / 2 / var(--col-1-end);
    --title-area: 2 / var(--col-1-start) / 3 / var(--col-1-end);
    --description-area: 3 / var(--col-2-start) / 4 / var(--col-2-end);
    --links-area: 4 / var(--col-2-start) / 5 / var(--col-2-end);
    --entry-previews-area: 5 / var(--col-2-start) / 6 / var(--col-2-end);

    @media (min-width: 451px) {
      --col-1-start: 2;
      --col-1-end: 23;
      --col-2-start: 24;
      --col-2-end: 49;
      
      --title-area: 1 / var(--col-1-start) / 2 / var(--col-1-end);
      --preamble-area: 1 / var(--col-1-start) / 2 / var(--col-1-end);
      --description-area: 1 / var(--col-2-start) / 3 / var(--col-2-end);
      --links-area: 1 / var(--col-2-start) / 2 / var(--col-2-end);
    }

    @media (min-width: 1025px) {
      --col-1-start: 4;
      --col-1-end: 9;
      --col-2-start: 10;
      --col-2-end: 21;
    }
  }

  .variant-24 {
    :global(&:has(.preamble.first + .links.last) .preamble) {
      min-height: var(--input-min-height);
      display: flex;
      align-items: center;
      :global(.text) {
        margin-block-end: 0;
      }
    }
    
    .text-fields {
      display: contents;
      :global(.title)       { grid-area: var(--title-area); }
      :global(.preamble)    { grid-area: var(--preamble-area); }
      :global(.description) { 
        grid-area: var(--description-area);
        :global(.text) { text-wrap: balance; }
      }
      :global(.links)       { grid-area: var(--links-area); }

      :global(.description.first.last) {
        grid-column: var(--col-1-start) / var(--col-2-end);
      }

      :global(&:has(.description)) {
        @media (min-width: 451px) {
          --description-area: 1 / var(--col-2-start) / 3 / var(--col-2-end);
          --title-area: 1 / var(--col-1-start) / 2 / var(--col-1-end);
          --links-area: 3 / var(--col-2-start) / 4 / var(--col-2-end);
        }
      }

      :global(&:has(.preamble)) {
        :global(.preamble .text) {
          margin-block-end: var(--font-size);
        }
        @media (min-width: 451px) {
          --preamble-area: 1 / var(--col-1-start) / 2 / var(--col-1-end);
          --title-area: 2 / var(--col-1-start) / 4 / var(--col-1-end);
          :global(.title .text) { margin-block-end: 0; }
        }
        :global(.preamble .text) { margin-block-end: var(--font-size); }
      }

      :global(&:not(:has(.description))) {
        @media (min-width: 451px) {
          --links-area: 1 / var(--col-2-start) / 2 / var(--col-2-end);
        }
      }

      :global(.links.first) {
        grid-column: var(--col-1-start) / var(--col-2-end);
      }

      :global(.preamble.first + .links.last),
      :global(.preamble.first + .description.last) {
        :global(.text) { margin-block-start: 0; }
      }

      :global(&:has(:is(.title, .preamble)) .links:not(.first) .text:not(.editable)) {
        margin-block-start: var(--font-size);
      }

      :global(&:not(:has(:is(.title, .preamble))) .links) {
        grid-area: var(--preamble-area);
      }

      :global(&:has(.preamble)), :global(&:has(.title)) {
        :global(.description) { grid-column: var(--col-2-start) / var(--col-2-end); }
      }

      :global(&:not(:has(.preamble))):has(.title) {
        @media (min-width: 451px) {
          --description-area: 1 / var(--col-2-start) / 2 / var(--col-2-end);
        }
      }

      @media (min-width: 451px) {
        :global(.text) {
          margin-block-end: 0;
        }
      }
    }

    .entry-previews {
      grid-column: var(--col-2-start) / var(--col-2-end);
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: var(--grid-gutter);
    }
    &:has(.text-fields) .entry-previews {
      margin-block-start: var(--block-spacing-s);
      grid-area: var(--entry-previews-area, 5 / var(--col-2-start) / 6 / var(--col-2-end));
      grid-template-columns: 1fr;
    }
  }

  /* ————————————————————————————————————————————— 25 —————————————————————————————————————————————————————————— */
  /* up-to-3-images one-entry-preview-per-row extended-track equal-grid */
  .variant-25 {
    .text-fields {
      &:not(:has(.title)){
        :global(.preamble .text) {
          margin-block-end: 0;
        }
      }
      :global(.preamble)    { grid-area: 1 / var(--track-start) / 2 / var(--track-end);}
      :global(.title)       { grid-area: 2 / var(--track-start) / 3 / var(--track-end);}
      :global(.description) { grid-area: 3 / var(--track-start) / 4 / var(--track-end);}
      :global(.links)       {
        grid-area: 5 / var(--track-start) / 6 / var(--track-end);
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        column-gap: var(--grid-gutter);
        :global(> *) {
          max-width: none;
          :global(.button-label) {
            justify-content: flex-start;
          }
        }
      }
    }
    &:has(.text-fields) .entry-previews {
      margin-block-start: var(--block-spacing-s);
    }
    .entry-previews {
      grid-area: 4 / var(--track-start) / 5 / var(--track-end);
      .preview-images {
        display: grid;
        column-gap: var(--grid-gutter);
        grid-template-columns: repeat(2, 1fr);
        @media (max-width: 1024px) {
          :global(.zoomable-image:nth-child(3)) {
            /* Hide the third image on small screens */
            display: none;
          }
        }
        @media (min-width: 1025px) {
          grid-template-columns: repeat(3, 1fr);
        }
        :global(.zoomable-image) {
          overflow: clip;
          :global(.media-item) {
            aspect-ratio: var(--common-aspect-ratio);
          }
        }
      }
      /* Limit the width of the preview texts to the width of one preview image */
      .preview-texts :global(.preview-button) {
        @media (min-width: 1025px) {
          max-width: calc((100% - 2 * var(--grid-gutter)) / 3);
        }
      }
    }
  }

  /* ————————————————————————————————————————————— 148 ————————————————————————————————————————————————————————— */
  /* Program: texts on top, event rows below, both across the track */
  .variant-148 {
    .text-fields {
      :global(.preamble)    { grid-area: 1 / var(--track-start) / 2 / var(--track-end); }
      :global(.title)       { grid-area: 2 / var(--track-start) / 3 / var(--track-end); }
      :global(.description) { grid-area: 3 / var(--track-start) / 4 / var(--track-end); }
      :global(.links)       { grid-area: 5 / var(--track-start) / 6 / var(--track-end); }
    }
    .program-list {
      grid-area: 4 / var(--track-start) / 5 / var(--track-end);
    }
    .text-fields ~ .program-list {
      margin-block-start: var(--block-spacing-s);
    }
  }
  /* ————————————————————————————————————————————— 149 ————————————————————————————————————————————————————————— */
  /* Regular dates: texts on top, the weekly plan below, both across the track */
  .variant-149 {
    .text-fields {
      :global(.preamble)    { grid-area: 1 / var(--track-start) / 2 / var(--track-end); }
      :global(.title)       { grid-area: 2 / var(--track-start) / 3 / var(--track-end); }
      :global(.description) { grid-area: 3 / var(--track-start) / 4 / var(--track-end); }
      :global(.links)       { grid-area: 5 / var(--track-start) / 6 / var(--track-end); }
    }
    .regular-dates {
      grid-area: 4 / var(--track-start) / 5 / var(--track-end);
      min-width: 0;
    }
    .text-fields ~ .regular-dates {
      margin-block-start: var(--block-spacing-s);
    }
    /* the button follows the plan, not the texts above it */
    .text-fields :global(.links) {
      margin-block-start: var(--block-spacing-s);
    }
  }
  .program-note {
    margin: 0;
    padding-block: var(--size-4);
    color: var(--text-2);
  }
  .program-list {
    position: relative;
    border-block-start: 1px solid var(--color-foreground);
    .program-item {
      position: relative;
      &:last-of-type :global(.program-row) {
        border-block-end: 0;
      }
      &.is-past {
        opacity: 0.45;
      }
      &.is-dragging {
        opacity: 0.5;
        outline: 1px dashed var(--editing);
        outline-offset: 2px;
      }
      :global(.visibility-icon) {
        position: absolute;
        top: var(--size-5);
        right: 0;
      }
    }
  }

  /*
    List focus: pointing at one row of a FAQ or of a linked program list quiets the other rows,
    so the row under the pointer reads as the one being chosen. Only the row contents fade - the
    hairlines between rows keep their weight, so the list keeps its rhythm. To 0.6, not further:
    rows also pass under a resting pointer while the page scrolls, and quieted text has to stay
    readable (black on the limberger beige at 0.4 was 2.8:1). Open answers never fade - they
    are what is being read.

    Plain :hover on the list and the row - no :has(), nothing toggled from JS - so moving across
    the rows costs no restyle beyond the rows themselves. The rows touch each other, so the list
    is hovered only through a row; the program list's own top rule is taken out of hit testing,
    or crossing it would dim every row for a moment. Follows the site's animation setting like
    the rest of the page motion. Not while editing: rows are dragged and selected there.
  */
  @media (hover: hover) {
    .use-animations {
      .faq-item > .faq-summary,
      .faq-preamble,
      .program-item > :global(a.program-row > *) {
        transition: opacity var(--duration-fast) var(--ease-settle);
        @media (prefers-reduced-motion: reduce) {
          transition: none;
        }
      }
    }
    :global(.story-block:not(.is_editing)) .use-animations {
      .program-list {
        pointer-events: none;
      }
      .program-item {
        pointer-events: auto;
      }
      .faq-list:hover > .faq-item:not(:hover) > .faq-summary,
      .program-list:hover > .program-item:not(:hover) > :global(a.program-row > *) {
        opacity: 0.6;
      }
      /* The preamble is already at 0.7 of the row; inside a quieted row it lifts to the row's
         own 0.6, or it would end at 0.42 */
      .faq-list:hover > .faq-item:not(:hover) > .faq-summary .faq-preamble {
        opacity: 1;
      }
    }
  }

  /* ————————————————————————————————————————————— 60 —————————————————————————————————————————————————————————— */
  /* 3 columns, texts centered in middle column, sticky if has description */
  .variant-60 {
    /* The text sits between images that alternate left and right, so the outer columns stay
       full-bleed and only the centre column follows the track. --centre-half is a quarter of
       the span either side of the page centre; the images clear it by a constant 3 columns. */
    --centre-half: calc(var(--track-span-cols) / 4);
    --column-1-start: 1;
    --column-1-end: 25;
    --column-2-start: 14;
    --column-2-end: 38;
    --column-3-start: 27;
    --column-3-end: -1;
    --entry-previews-row-start: 3;
    @media (min-width: 1025px) {
      --column-1-start: 1;
      --column-1-end: calc(26 - var(--centre-half) - 3);
      --column-2-start: calc(26 - var(--centre-half));
      --column-2-end: calc(26 + var(--centre-half));
      --column-3-start: calc(26 + var(--centre-half) + 3);
      --column-3-end: -1;
      --entry-previews-row-start: 1;
    }
    @media (min-width: 1281px) {
      --centre-half: calc((var(--track-span-cols) + 8) / 4);
    }
    .text-fields {
      :global(:is(.preamble, .title, .description) .text){
        text-align: center;
        --text-align: center;
        margin-inline: auto;
      }
      :global(.links) {
        flex-direction: column;
        align-items: center;
        :global(.button-label) {
          justify-content: center;
        }
      }
      :global(.preamble) {
        margin-block-end: var(--block-spacing-s);
      }
      @media (max-width: 1024px) {
        :global(.preamble)    { grid-area: 1 / 2 / 2 / 50;}
        :global(.title)       { grid-area: 2 / 2 / 3 / 50;}
        :global(.description) { grid-area: 4 / 2 / 5 / 50;}
        :global(.links)       { grid-area: 5 / 2 / 6 / 50;}
      }
      @media (min-width: 1025px) {
        display: flex;
        flex-direction: column;
        grid-area: 1 / var(--column-2-start) / 5 / var(--column-2-end);
        &:not(:has(.description)){
          align-self: center;
        }
        &:has(.description){
          position: sticky;
          top: calc(var(--dynamic-island-bottom-edge) + var(--block-spacing-s));
        }
      }
    }
    &:has(.description) .entry-previews,
    &:has(.links) .entry-previews {
      @media (max-width: 1024px) {
        margin-block-end: var(--block-spacing-s);
      }
    }
    .entry-previews {
      .preview-card {
        /* Equal aspect ratios for images */
        .preview-images {
          :global(.media-item){
            width: 100%;
            height: auto;
            &:not(.fit-to-frame){
              aspect-ratio: var(--common-aspect-ratio) !important;
            }
          }
        }
        .preview-texts {
          padding-inline: var(--page-inline-spacing);
        }
      }
      .preview-card:not(:last-child) .preview-texts {
        margin-block-end: var(--block-spacing-s);
      }
      @media (max-width: 1024px) {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: var(--grid-gutter);        
        grid-area: 3 / 1 / 4 / -1;
      }
      @media (min-width: 1025px) {
        display: contents;
        .preview-card {
          &.first, &.second {
            grid-row: var(--entry-previews-row-start) / calc(var(--entry-previews-row-start) + 2);
          }
          &.odd {
            grid-column: var(--column-1-start) / var(--column-1-end);
          }
          &.even {
            grid-column: var(--column-3-start) / var(--column-3-end);
          }
        }
      }      
    }
  }
  /* ————————————————————————————————————————————— 61 —————————————————————————————————————————————————————————— */
  /* 5 columns symmetric, flex track, preamble marginale column 1, description & title in column 2, images centered in column 3, links column 4-5 */
  .variant-61 {
    :global(.links) {
      justify-content: center;
    }
    --col-1: 20 / 50;
    --col-2: 20 / var(--track-end);
    --col-3: 20 / 34;
    --col-4: var(--track-start) / var(--track-end);
    --preamble-row: 2 / 3;
    --title-row: 1 / 2;
    --description-row: 3 / 4;
    --embeds-row: 4 / 5;
    --links-row: 5 / 6;
    @media (min-width: 361px) {
      --preamble-row: 2 / 3;
      --title-row: 1 / 2;
      --description-row: 2 / 3;
      --links-row: 4 / 5;
      --embeds-row: 3 / 4;
      --col-1: var(--track-start) / 19;
      --col-2: 20 / var(--track-end);
      --col-3: 20 / 34;
      --col-4: var(--track-start) / var(--track-end);
    }
    @media (min-width: 1025px) {
      grid-template-rows: auto auto 1fr;
      /* One flex column, scaled to whatever track is in use. Every stop below is an offset
         from the track's own start in these units, so the four columns keep their order and
         their proportions at any width - mixing literal stops with a track-derived start
         inverted the ranges as soon as the track was narrower than the literal. */
      --u: calc(var(--track-span-cols) / 48);
      --col-1: var(--track-start) / round(calc(var(--track-start) + 15 * var(--u)), 1);
      --col-2: var(--track-start) / round(calc(var(--track-start) + 15 * var(--u)), 1);
      --col-3: round(calc(var(--track-start) + 18 * var(--u)), 1) / round(calc(var(--track-start) + 30 * var(--u)), 1);
      --col-4: round(calc(var(--track-start) + 31 * var(--u)), 1) / var(--track-end);
      --preamble-row: 1 / 2;
      --title-row: 2 / 3;
      --description-row: 3 / 4;
      --links-row: 1 / 4;
      --embeds-row: 1 / 4;
      :global(.links) {
        justify-content: flex-end;
      }
    }
    @media (min-width: 1281px) {
      grid-template-rows: 0 auto 1fr;
      --col-1: var(--track-start) / round(calc(var(--track-start) + 7 * var(--u)), 1);
      --col-2: round(calc(var(--track-start) + 8 * var(--u)), 1) / round(calc(var(--track-start) + 17 * var(--u)), 1);
      --col-4: round(calc(var(--track-start) + 31 * var(--u)), 1) / var(--track-end);
      /* From here the preamble is a marginale sitting BESIDE the title, not above it, so it
         belongs on the title's row. It was still assigned to row 1, which this breakpoint
         collapses to 0 - so it overflowed downwards and printed on top of the title. */
      --preamble-row: 2 / 3;
    }
    @media (min-width: 1681px) {
      --col-1: var(--track-start) / round(calc(var(--track-start) + 5 * var(--u)), 1);
      --col-2: round(calc(var(--track-start) + 6 * var(--u)), 1) / round(calc(var(--track-start) + 15 * var(--u)), 1);
      --col-3: round(calc(var(--track-start) + 16 * var(--u)), 1) / round(calc(var(--track-start) + 32 * var(--u)), 1);
      --col-4: round(calc(var(--track-start) + 33 * var(--u)), 1) / var(--track-end);
    }
    .text-fields {
      :global(.preamble)    { 
        grid-column: var(--col-1); grid-row: var(--preamble-row);
        margin-block-end: var(--block-spacing-m);
      }
      :global(.title)       { grid-column: var(--col-2); grid-row: var(--title-row)}
      :global(.description) { grid-column: var(--col-2); grid-row: var(--description-row)}
      :global(.links)       { grid-column: var(--col-4); grid-row: var(--links-row)}
      :global(.links) {
        @media (max-width: 1024px) {
          margin-block-start: var(--block-spacing-m);
        }
      }
    }
    .entry-previews {
      grid-column: var(--col-3);
      grid-row: var(--embeds-row);
      .preview-card:not(:last-child){
        margin-block-end: var(--block-spacing-m);
      }
    }
  }

  /* ————————————————————————————————————————————— 62 —————————————————————————————————————————————————————————— */
  /* 4 columns symmetric, flex track, preamble marginale column 1, description & title on top left aligned, images centered in column 2-3 (two columns), links column 4 */
  /* ————————————————————————————————————————————— 65 —————————————————————————————————————————————————————————— */
  /* 4 columns symmetric, flex track, preamble marginale column 1, description & title on top center aligned, images centered in column 2-3 (two columns), links column 4 */
  .variant-62,
  .variant-65 {
    @media (max-width: 640px) {
      .text-fields :global(.field.last) {
        margin-block-end: var(--block-spacing-s);
      }
    }
    --preamble-row: 1 / 2;
    --title-row: 2 / 3;
    --description-row: 3 / 4;
    --links-row: 4 / 5;
    --embeds-row: 5 / 6;
    --col-2: var(--track-start) / var(--track-end);
    --col-1: var(--track-start) / var(--track-end);
    --col-3: var(--track-start) / var(--track-end);
    @media (min-width: 641px) {
      --col-1: var(--track-start) / calc(var(--center-gutter-track-start) + 6);
      --links-row: 1 / 4;
      --embeds-row: 4 / 5;
      &:has(.preamble), &:has(.title), &:has(.description) {
        :global(.links) {
          --col-3: calc(var(--center-gutter-track-start) + 7) / var(--track-end);
          justify-content: flex-end;
        }
      }
      &:has(.description) :global(.description) {
        margin-block-end: var(--block-spacing-s);
      }
      &:has(.title):not(:has(.description)) :global(.title) {
        margin-block-end: var(--block-spacing-s);
      }
      &:has(.preamble):not(:has(.title)):not(:has(.description)) :global(.preamble) {
        margin-block-end: var(--block-spacing-s);
      }
    }
    /* Middle column grows with the viewport; the side columns are whatever is left of the
       track. --col-2-inset is half its width, as a fraction of the span, so the proportions
       hold at any track (at flex these are exactly the old 16/36, 14/38, 10/42, 8/44). */
    --col-2-start: calc(26 - var(--col-2-inset));
    --col-2-end: calc(26 + var(--col-2-inset));
    @media (min-width: 1025px) {
      grid-template-rows: auto auto auto 1fr;
      --col-2-inset: calc(var(--track-span-cols) * 10 / 48);
      --col-1: var(--track-start) / calc(var(--col-2-start) - 1);
      --col-2: var(--col-2-start) / var(--col-2-end);
      --col-3: calc(var(--col-2-end) + 1) / var(--track-end);
      --preamble-row: 3 / 4;
      --title-row: 1 / 2;
      --description-row: 2 / 3;
      --links-row: 3 / 4;
      --embeds-row: 3 / 4;
    }
    @media (min-width: 1281px) { --col-2-inset: calc(var(--track-span-cols) * 12 / 48); }
    @media (min-width: 1601px) { --col-2-inset: calc(var(--track-span-cols) * 16 / 48); }
    @media (min-width: 1801px) { --col-2-inset: calc(var(--track-span-cols) * 18 / 48); }
    .text-fields {
      :global(.preamble)    { grid-column: var(--col-1); grid-row: var(--preamble-row);}
      :global(.title)       { grid-column: var(--track-start) / var(--track-end); grid-row: var(--title-row);}
      :global(.description) { grid-column: var(--track-start) / var(--track-end); grid-row: var(--description-row);}
      :global(.links)       { grid-column: var(--col-3); grid-row: var(--links-row);}

      &:not(:has(.description)) {
        :global(.title) {
          margin-block-end: var(--block-spacing-l);
        }
      }
      &:has(.description) {
        :global(.description) {
          margin-block-end: var(--block-spacing-l);
        }
      }

      :global(.title:last-child) {
        :global(.text) {
          margin-block-end: 0;
        }
      }
    }
    .entry-previews {
      grid-column: var(--col-2);
      grid-row: var(--embeds-row);
      display: grid;
      grid-template-columns: 1fr;
      &:has(.preview-card-2) {
        grid-template-columns: repeat(2, 1fr);
      }
      column-gap: var(--grid-gutter);
      .preview-card:not(:last-child) {
        margin-block-end: var(--block-spacing-m);
      }
    }
  }
  .variant-65 {

    :global(.title), :global(.description) {
      display: flex;
      justify-content: center;
      :global(.text) {
        text-align: center;
        --text-align: center;
      }
    }
  }

  /* ————————————————————————————————————————————— 63 —————————————————————————————————————————————————————————— */
  /* 4 columns assymmetric, flex track, preamble marginale column 1, description, title & links column 2, images in column 3-4 */
  .variant-63 {
    @media (max-width: 640px) {
      .text-fields :global(.field.last) {
        margin-block-end: var(--block-spacing-s);
      }
    }
    --preamble-row: 1 / 2;
    --title-row: 2 / 3;
    --description-row: 3 / 4;
    --links-row: 4 / 5;
    --embeds-row: 5 / 6;
    --col-2: var(--track-start) / var(--track-end);
    --col-1: var(--track-start) / var(--track-end);
    --col-3: var(--track-start) / var(--track-end);
    @media (min-width: 769px) {
      grid-template-rows: auto auto 1fr;
      /* col-2 always stops two short of the centre gutter; col-1 is the marginale that
         narrows as the viewport grows. Clamped so it cannot invert at narrow tracks. */
      --col-2-start: max(calc(var(--track-start) + 2), calc(var(--inner-track-start) + 2));
      --col-1: var(--track-start) / calc(var(--col-2-start) - 1);
      --col-2: var(--col-2-start) / var(--split-left-end);
      --col-3: var(--split-right-start) / var(--track-end);
      --preamble-row: 1 / 4;
      --title-row: 1 / 2;
      --description-row: 2 / 3;
      --links-row: 3 / 4;
      --embeds-row: 1 / 4;
    }
    @media (min-width: 1281px) { --col-2-start: max(calc(var(--track-start) + 2), var(--inner-track-start)); }
    @media (min-width: 1601px) {
      --col-1: 2 / 9;
      --col-2: 10 / 23;
    }
    @media (min-width: 1801px) {
      --col-1: 2 / 5;
      --col-2: 6 / 23;
    }
    .text-fields {
      :global(.preamble)    { grid-column: var(--col-1); grid-row: var(--preamble-row);}
      :global(.title)       { grid-column: var(--col-2); grid-row: var(--title-row);}
      :global(.description) { grid-column: var(--col-2); grid-row: var(--description-row);}
      :global(.links)       { grid-column: var(--col-2); grid-row: var(--links-row);}
    }
    .entry-previews {
      grid-column: var(--col-3);
      grid-row: var(--embeds-row);
      display: grid;
      grid-template-columns: 1fr;
      @media (min-width: 1025px) or (max-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
      }
      column-gap: var(--grid-gutter);
      .preview-card:not(:last-child) {
        margin-block-end: var(--block-spacing-m);
      }
    }
  }

  /* ————————————————————————————————————————————— 64 —————————————————————————————————————————————————————————— */
  /* Popup - reveal when inview, 1 Column, center aligned, flex width background, inner-tracks 3-8 for texts */
  /* Block spacing is now controlled via inline style */
  :global(.story-block:not(.seen-20):not(.is_editing):has(.variant-64)) {
    opacity: 0;
    translate: 0 10vh 0;
  }
  :global(.story-block.at-20:not(.seen-20):not(.is_editing):has(.variant-64)) {
    animation: slide-and-fade-in-strong 1s ease-out forwards;
  }
  :global(.story-block.seen-20:has(.variant-64)),
  :global(.story-block.is_editing:has(.variant-64)) {
    opacity: 1;
    translate: 0 0 0;
  }
  .variant-64 {
    align-items: center;
    /* Override default padding to use larger internal spacing for this layout */
    padding-block-start: max(var(--block-spacing-top), var(--block-spacing-l));
    padding-block-end: max(var(--block-spacing-bottom), var(--block-spacing-l));
    .text-fields {
      z-index: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;      
      /* Centred on the track: half its span, narrowing to a third on wide screens */
      grid-area: 1 / round(calc(var(--track-start) + var(--track-span-cols) / 4), 1)
               / 2 / round(calc(var(--track-end) - var(--track-span-cols) / 4), 1);
      @media (min-width: 1025px) {
        /* A third in from each edge, i.e. centre +/- span/6 */
        grid-area: 1 / round(calc(var(--track-start) + var(--track-span-cols) / 3), 1)
                 / 2 / round(calc(var(--track-end) - var(--track-span-cols) / 3), 1);
      }
      :global(.text) {
        text-align: center;
        --text-align: center;
      }
      :global(.links) {
        justify-content: center;
      }
    }
    .entry-previews {
      grid-area: 1 / 2 / 2 / 50;
      z-index: 0;
      .preview-images :global(.media-item) {
        max-height: 80vh;
      }
    }
  }
  /* ═══════════════════════════════════════════════════════════════════════════════════════════════
     COVER / HERO LAYOUTS - Using group classes
     ═══════════════════════════════════════════════════════════════════════════════════════════════ */
  
  /* Text on top of fullscreen image - all cover layouts */
  /* Full-height layout */
  
  :global(.story-block:not(.seen-20):not(.is_editing):has(.story-layout.group-cover)),
  :global(.story-block:not(.seen-20):not(.is_editing):has(.story-layout.group-special.variant-67)) {
    /* Set initial state for container to prevent flicker */
    .entry-previews {
      scale: 0.96;
    }
  }
  
  :global(.story-block:not(.is_editing):first-child:has(.story-layout.group-cover)),
  :global(.story-block:not(.is_editing):first-child:has(.story-layout.group-special.variant-67)) {
    margin-block: calc(-1 * var(--dynamic-island-bottom-edge));
  }
  
  :global(.story-block:not(.is_editing):first-child:has(.story-layout.group-cover) + .story-block),
  :global(.story-block:not(.is_editing):first-child:has(.story-layout.group-special.variant-67) + .story-block) {
    margin-block-start: var(--dynamic-island-bottom-edge);
  }
  
  :global(.story-block.at-20:not(.seen-20):not(.is_editing):has(.story-layout.group-cover) .entry-previews),
  :global(.story-block.at-20:not(.seen-20):not(.is_editing):has(.story-layout.group-special.variant-67) .entry-previews) {
    /* Container starts scaled down to reveal peek of next card, then zooms to full */
    animation: subtle-zoom-in 1.2s cubic-bezier(0.16, 1, 0.3, 1) forwards 0.1s;
  }
  
  :global(.story-block:is(.seen-20,.is_editing):has(.story-layout.group-cover) .entry-previews),
  :global(.story-block:is(.seen-20,.is_editing):has(.story-layout.group-special.variant-67) .entry-previews) {
    /* Final state after animation or when editing */
    scale: 1;
  }

  /* ————————————————————————————————————————————— 67 —————————————————————————————————————————————————————————— */
  /* Automatic collapsing cover - special variant that auto-collapses */
  :global(.story-block:has(.variant-67)) {
    opacity: 1;
  }
  :global(.story-block.at-80:not(.seen-80):not(.is_editing):has(.variant-67)) {
    animation: collapse-up-fade-out 1.3s cubic-bezier(.64,.01,0,1) forwards;
    animation-delay: 2s;
  }
  :global(.story-block.seen-80:has(.variant-67)) {
    opacity: 0;
    pointer-events: none;
    height: 0;
    overflow: hidden;
  }
  :global(.story-block.is_editing:has(.variant-67)) {
    opacity: 1;
    pointer-events: auto;
    height: auto;
    overflow: visible;
  }
  .variant-67 {
    :global(.preview-card-2) {
      grid-area: 1 / 14 / 3 / 38 !important;
    }
  }
  
  /* All cover layouts - shared structure */
  .story-layout.group-cover,
  .story-layout.group-special.variant-67 {
    grid-template-rows: 1fr auto;
    /* Override default padding - spacing is applied as margin */
    padding-block: 0;
    margin-block-start: var(--block-spacing-top);
    margin-block-end: var(--block-spacing-bottom);
    @media (min-width: 1025px) {
      min-height: 100vh;
      grid-template-rows: 1fr;
      position: relative;
    }
    .text-fields {
      z-index: 1;   
      display: flex;
      flex-direction: column;
      justify-content: center;
      z-index: 2;
      @media (min-width: 1025px) {
        margin-block-start: calc(var(--dynamic-island-bottom-edge) + var(--block-spacing-m));
        margin-block-end: var(--block-spacing-m);
        grid-row: 1 / 2;
      }
      :global(.text) {
        /* TODO: automatically change accent color based on background image brightness */
      }
      :global(.title:not(.last) .text) {
        margin-block-end: calc(0.5 * var(--font-size));
      }
      :global(.title.last .text) {
        margin-block-end: 0;
      }
    }
    
    /* Vertical alignment for cover text fields (grid child positioning) */
    &.vertical-top .text-fields {
      align-self: start;
    }
    &.vertical-center .text-fields {
      align-self: center;
    }
    &.vertical-bottom .text-fields {
      align-self: end;
    }

    .entry-previews {
      z-index: 0;
      display: flex;
      /* With a single card there is nothing to scroll to, so this must not be a scroll
         container: iOS swallows the edge swipe-back gesture inside one, and
         `overscroll-behavior-x: contain` blocks it outright. `clip` clips exactly like
         `hidden` without ever creating a scroll port - and it has to cover both axes,
         since `visible` on one axis computes to `auto` when the other one clips. */
      overflow: clip;
      scrollbar-width: none;
      -ms-overflow-style: none;
      &::-webkit-scrollbar {
        display: none;
      }
      @media (min-width: 1025px) {
        /* Cover full height of layout including any text overflow */
        grid-area: 1 / 1 / -1 / -1;
        position: absolute;
        inset: 0;
      }

      /* Becomes a carousel once there is a second card - see SHARED CAROUSEL. */
            
      .preview-card {
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-template-rows: auto auto;
        min-width: 100vw;
        scroll-snap-align: center;
        margin-block-end: 0 !important;
        .preview-images {
          grid-column: 1 / 3;
          grid-row: 1 / 3;
        }
        .preview-texts {
          display: flex;
          flex-direction: column;
          gap: var(--size-2);
          z-index: 2;
          pointer-events: none;
          opacity: var(--preview-text-opacity, 1);
          :global(*) {
            pointer-events: auto;
          }
          /* Line clamping for limited space */
          .preview-preamble {
            --line-clamp: 1;
          }
          .preview-title {
            --line-clamp: 2;
          }
          .preview-description {
            --line-clamp: 2;
          }
          :global(.preview-button) {
            width: fit-content;
          }
        }
        :global(.media-item) {
          width: 100%;
          height: 100%;
          @media (min-width: 1025px) {
            max-height: none;
          }
        }
        /* Videos are wrapped in .video-wrapper (legacy Safari object-fit workaround that sets
           object-fit: unset on the video), so forcing width AND height to 100% distorts them.
           For full-bleed covers the wrapper's aspect-ratio simulation cannot work anyway (the
           target area is viewport-shaped, not media-shaped) — use real object-fit: cover. */
        :global(.video-wrapper) {
          width: 100%;
          height: 100%;
          aspect-ratio: auto !important; /* overrides the inline aspect-ratio from Image.svelte */
          @media (min-width: 1025px) {
            max-height: none;
          }
        }
        :global(.video-wrapper > .media-item) {
          width: 100% !important;
          height: 100% !important;
          max-width: none !important;
          max-height: none !important;
          object-fit: cover !important;
        }
      }

    }
  }

  /* Covers and the expanding layout share the same horizontal slide transition. */
  @supports (animation-timeline: scroll()) {
    .story-layout:is(.group-cover, .group-special.variant-67, .variant-144) .entry-previews .preview-card {
      --preview-text-opacity: 0;
      animation: scroll-card-transition both, preview-texts-opacity both;
      animation-timeline: view(x), view(x);
      animation-range: entry 0% exit 100%, entry 0% exit 100%;
    }
  }


  /* ————————————————————————————————————————————— 140 / 141 ————————————————————————————————————————————————————— */
  /*
    Collage layouts.

    Nothing is perfectly aligned: every card is rotated, nudged vertically and — in 140 — allowed
    to overlap its neighbours. The variation is deterministic, driven by the .every-7-starting-N /
    .every-8-starting-N classes that every .preview-card already carries, so the same set of images
    always composes identically. No JS, no randomness, no layout shift.

    Rotation is scaled by --collage-tilt so the whole effect can be turned down in one place
    (and is halved on small screens, where a tilted card next to a screen edge reads as a bug).
  */
  /*
    Caption rhythm, shared by the collage and centred layouts.

    The preamble is a LABEL for the title — "Malerei" over a name — but the shared card margin is a
    whole font-size, which left it sitting almost as far from the title it belongs to as from the
    image above it. Grouping the two is the whole job of that gap.
  */
  .story-layout:is(.variant-140, .variant-141, .variant-142, .variant-143, .variant-145, .variant-146) {
    /* Size photographs by width: a viewport-height cap would crop tall originals. */
    .entry-previews .preview-card :global(.media-item) {
      max-height: none;
    }
    .entry-previews .preview-card .preview-texts .preview-preamble:not(:last-child) {
      margin-block-end: calc(var(--font-size) * 0.5);
    }
  }

  .story-layout:is(.variant-140, .variant-145, .variant-146),
  .story-layout.variant-141 {
    --collage-tilt: 1;

    /*
      Hand-drawn underline, drawn on hover.

      Two overlapping wobbly strokes in an inline SVG data URI — a hand goes over a line twice and
      never hits the same path, so a single stroke reads as a border rather than as ink. The SVG
      is URL-encoded rather than base64: same size, but it stays legible and editable in place,
      and `currentColor` is not available inside a data URI so the stroke colour is written in.

      `preserveAspectRatio='none'` lets it stretch to any title width. Revealing it by animating
      background-size from 0 to 100% draws it left-to-right the way a pen would, instead of
      fading a finished line in.

      This replaces a straighten-on-hover rotation, which read as a cheap CSS trick — the card
      wobbling back and forth had nothing to do with the content.
    */
    --scribble-underline: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 14' preserveAspectRatio='none'%3E%3Cpath d='M2 8.5C28 5 52 11 78 7.5s46-4 68 1.5 34-2.5 52-1' fill='none' stroke='%23000' stroke-width='2.4' stroke-linecap='round'/%3E%3Cpath d='M7 11.5C33 9 58 13.5 84 10.5s48-3 70 2' fill='none' stroke='%23000' stroke-width='1.4' stroke-linecap='round' opacity='.6'/%3E%3C/svg%3E");

    .entry-previews .preview-card {
      /* rotate() rather than transform: cheaper to composite and does not stomp on the
         translate/scale that hover and in-view animations set elsewhere */
      rotate: calc(var(--card-tilt, 0deg) * var(--collage-tilt));

      /*
        `min-width: 0` — the single most important line here.

        A grid item defaults to `min-width: auto`, i.e. it refuses to shrink below its content's
        min-content width. These cards contain 4000px-wide photographs, so on a phone every card
        stayed far wider than its track (measured: 697px inside a 700px viewport) and ran off the
        right edge regardless of how the columns were arranged. Rearranging the composition alone
        never fixed it, and clipping the overflow only hid it.
      */
      min-width: 0;

      :global(.preview-title) {
        position: relative;
        /* NOT `display: inline` — the title carries `line-clamp`, which needs a block-ish box, so
           inline is overridden and the underline would stretch the full card width instead of the
           text width. `fit-content` shrinks the block to its longest line, which is what the
           underline should measure. */
        width: fit-content;
        max-width: 100%;
        /*
          The title carries `leading-trim`, so its box ends tight against the letterforms and a
          line pinned to the bottom sits right on them — it cut through the descenders of a name
          like "Elisabeth le Blanc". This padding is the gap the line sits in.
        */
        padding-block-end: 0.42em;
      }

      /*
        The line lives on a pseudo-element rather than as a background on the title itself, so it
        can be revealed by a wipe.

        The first version animated `background-size` from 0% to 100%, which *scales* the SVG as it
        grows: the wobble starts compressed and stretches out as the line extends, which no pen
        does. Holding the background at its final size and uncovering it with `clip-path` keeps
        every kink fixed in place and simply reveals more of it — the way ink actually arrives.
      */
      :global(.preview-title)::after {
        content: '';
        position: absolute;
        inset-inline: 0;
        bottom: 0;
        height: 0.4em;
        background-image: var(--scribble-underline);
        background-repeat: no-repeat;
        background-size: 100% 100%; /* constant — the line never rescales */
        clip-path: inset(0 100% 0 0);
        transition: clip-path 0.34s var(--ease-out, ease-out);
        pointer-events: none;
      }

      @media (hover: hover) {
        &:hover {
          z-index: 20;
          :global(.preview-title)::after { clip-path: inset(0 0 0 0); }
        }
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .entry-previews .preview-card :global(.preview-title)::after { transition: none; }
    }

    /* Rotation cycle of 7 against a column count of 2/3/4 — coprime, so tilts never line up
       into visible stripes down a column. */
    .preview-card.every-7-starting-1 { --card-tilt: -2.5deg; }
    .preview-card.every-7-starting-2 { --card-tilt:  1.75deg; }
    .preview-card.every-7-starting-3 { --card-tilt: -1deg; }
    .preview-card.every-7-starting-4 { --card-tilt:  3deg; }
    .preview-card.every-7-starting-5 { --card-tilt: -1.75deg; }
    .preview-card.every-7-starting-6 { --card-tilt:  2.25deg; }
    .preview-card.every-7-starting-7 { --card-tilt: -3deg; }

    /* Text-only previews get the shared hairline treatment (`.preview-card.text-only`). The
       hard-coded watercolour washes that used to sit here were a second colour system next to
       the site's colour sets. */
  }

  /* 140 — freeform pinned-up arrangement. Cards span uneven widths on a 12-column field and are
     pulled into each other vertically. Tuned for 3–8 images (the home-page collage). */
  .story-layout.variant-140 {
    --collage-overlap: clamp(0.75rem, 2.5vw, 3rem);

    /*
      The brief for this layout is "one view": eight photographs that together give an impression,
      on one screen. Size is controlled by the column span alone — capping image *height* is the
      obvious move and the wrong one, because the cards use object-fit: cover, so a height cap
      crops a portrait into a letterbox instead of scaling it down. Narrow spans keep every image
      whole and let the natural mix of portrait and landscape do the composing.
    */

    .text-fields {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      grid-row: 1 / 2;
      /* One step inside the picture field, scaled to the track so the relationship holds at
         any width. At flex these are the old extended-2 (span/12) and extended-1 (span/6). */
      grid-column: calc(var(--track-start) + var(--track-span-cols) / 12)
                 / calc(var(--track-end) - var(--track-span-cols) / 12);
      z-index: 10; /* text stays legible above any card that drifts under it */
      @media (min-width: 1025px) {
        grid-column: calc(var(--track-start) + var(--track-span-cols) / 6)
                   / calc(var(--track-end) - var(--track-span-cols) / 6);
      }
    }

    .entry-previews {
      grid-row: 2 / 3;
      /* Wider than 141: the home collage is the one place the images get to spread out. */
      grid-column: var(--track-start) / var(--track-end);
      display: grid;
      grid-template-columns: repeat(12, minmax(0, 1fr));
      align-items: start;
      margin-block-start: var(--block-spacing-m);
      /*
        Both of these were a single gutter, which is the spacing of a *grid* — and this is not one.
        Pinned-up prints need to be read as separate objects: neighbouring cards were landing a few
        pixels apart once the tilt had eaten into the gap, and the outermost ones sat right on the
        page edge. The inset is on top of the flex track's own page padding.
      */
      padding-inline: max(var(--page-inline-spacing), 3cqw);
      column-gap: calc(var(--grid-gutter) * 2.5);

      .preview-card {
        /* later cards sit on top of earlier ones — reads as a stack of pinned prints */
        z-index: calc(var(--preview-card-index, 1));
        grid-column: span 6;
      }

      /*
        NOTE on the class names: .every-8-starting-K is set when (n % 8 === K - 1), so the FIRST
        card carries .every-8-starting-2, not -1, and the eighth carries -1. The desktop rules
        below use them; the mobile rules deliberately do not.

        MOBILE: one column, not a scatter.

        This used to place cards across the same 12-column field as desktop, at half-width with
        overlaps. On a phone that produced a column of ~160px thumbnails, several of them tucked
        against the page edge where the rotation pushed them out of view — photographs arriving
        half off-screen. Clipping the overflow only hid it; the arrangement itself was wrong at
        that width.

        A phone has room for exactly one photograph across. Each card takes the full track and the
        collage character comes from alternating which edge it hangs off, plus the tilt — not from
        squeezing two columns into 375px.
      */
      @media (max-width: 768px) {
        /*
          A real single column, not `1 / -1` across the 12-column field. Those twelve tracks
          collapse to 0px at this width, so a card spanning them had a zero-width track and fell
          back to its content's min-content size — a 4000px photograph — which is why cards
          measured 697px inside a 700px viewport and ran off the screen.
        */
        grid-template-columns: minmax(0, 1fr);

        .preview-card {
          grid-column: auto;
          margin-block-start: 0;
        }
        /* The media has its own `min-width: auto` to defeat, for the same reason. */
        .preview-card :global(.media-item),
        .preview-card :global(.preview-images) { min-width: 0; }

        /* Alternate the inset so the column zig-zags instead of stacking flush. */
        .preview-card.odd  { margin-inline-end: 11%; }
        .preview-card.even { margin-inline-start: 11%; }
        .preview-card:not(:first-child) { margin-block-start: var(--collage-overlap); }
      }

      /*
        Desktop: eight cards on a 12-column field, three or four to a row, spans of 3–4 so a
        portrait photograph stays around 300px tall and the whole set fits one screen. Vertical
        offsets are what break the rows — no two neighbours share a top edge.
      */
      @media (min-width: 769px) {
        /* Clear the mobile zig-zag insets before the scattered composition takes over. */
        .preview-card.odd, .preview-card.even { margin-inline: 0; }

        .preview-card.every-8-starting-2 { grid-column: 1 / span 4;  margin-block-start: calc(var(--collage-overlap) *  0.5); }  /* card 1 */
        .preview-card.every-8-starting-3 { grid-column: 5 / span 4;  margin-block-start: calc(var(--collage-overlap) *  2.5); }  /* card 2 */
        .preview-card.every-8-starting-4 { grid-column: 10 / span 3; margin-block-start: 0; }                                    /* card 3 */
        .preview-card.every-8-starting-5 { grid-column: 2 / span 3;  margin-block-start: calc(var(--collage-overlap) * -1); }    /* card 4 */
        .preview-card.every-8-starting-6 { grid-column: 6 / span 4;  margin-block-start: calc(var(--collage-overlap) *  1.5); }  /* card 5 */
        .preview-card.every-8-starting-7 { grid-column: 10 / span 3; margin-block-start: calc(var(--collage-overlap) * -0.5); }  /* card 6 */
        .preview-card.every-8-starting-8 { grid-column: 1 / span 4;  margin-block-start: calc(var(--collage-overlap) *  0.5); }  /* card 7 */
        .preview-card.every-8-starting-1 { grid-column: 6 / span 4;  margin-block-start: calc(var(--collage-overlap) *  2); }    /* card 8 */
      }

      /*
        One or two images: scale up.

        The eight-position composition divides a 12-column field into spans of 3–4, which is right
        when the field is full and wrong when it is nearly empty — a lone photograph at a third of
        the width reads as a thumbnail that failed to load rather than as a picture. With one or
        two images there is no composition to preserve, so they simply get the room.

        `.preview-card-N` is the class the markup already puts on each card, and testing item count
        with `:has()` on the container is the convention used elsewhere in this file. These come
        after the placements above and carry a higher specificity, so they win at every width.
      */
      &:not(:has(.preview-card-2)) .preview-card {
        grid-column: 2 / span 10;
        margin-block-start: 0;
        margin-inline: 0;
      }
      &:has(.preview-card-2):not(:has(.preview-card-3)) {
        .preview-card { margin-inline: 0; }
        .preview-card-1 { grid-column: 1 / span 7; margin-block-start: 0; }
        .preview-card-2 { grid-column: 6 / span 7; margin-block-start: var(--collage-overlap); }
      }

      @media (min-width: 769px) {
        &:not(:has(.preview-card-2)) .preview-card { grid-column: 3 / span 8; }
        &:has(.preview-card-2):not(:has(.preview-card-3)) {
          .preview-card-1 { grid-column: 1 / span 6; }
          .preview-card-2 { grid-column: 6 / span 6; margin-block-start: calc(var(--collage-overlap) * 1.5); }
        }
      }
    }

    @media (max-width: 768px) {
      --collage-tilt: 0.6;
    }
  }

  /* 145 / 146 — Collage Split. The collage beside its text instead of under it.

     Shares 140's card treatment (tilt, scribble underline, washes — see the 140/141 block above)
     but not its field. 140 picks its composition from the VIEWPORT, which is only right while the
     field is as wide as the page. Here the field is half a track, and the track itself can be
     anything from inner to flex, so the viewport says nothing about the room the cards have.

     So the field is a query container and the composition is picked from ITS width. One rule
     drives every tier: no picture narrower than 180px. That holds at any track, any viewport,
     split or stacked, and in the editor with a sidebar open — no breakpoint knows about those.

       field  < 432px   one zig-zag column
             >= 432px   two across, equal
             >= 520px   two across, uneven (spans of 6 and 5)
             >= 648px   three across, equal
             >= 870px   three across, uneven (140's 4 / 4 / 3)

     The thresholds are the widths at which the narrowest card of that tier reaches 180px with
     --collage-gap taken off (e.g. 648 / 3 - 36 = 180). The field's width includes the half
     gutter it takes back on each side, so these are 36px more than the grid area it is given.

     Nothing overlaps. 140 pulls cards into each other with negative margins, which works for a
     full-width field of mixed formats; with portraits in a narrow field the pulled-up card landed
     on the caption above it. Offsets here only ever push DOWN, and rows are held apart by a real
     row-gap, so the tilt is the only thing eating into the space between two cards. */
  .story-layout:is(.variant-145, .variant-146) {
    --collage-gap: 2.25rem;

    .text-fields {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      z-index: 10;
    }

    .entry-previews {
      container: collage-field / inline-size;
      display: grid;
      grid-template-columns: repeat(12, minmax(0, 1fr));
      /*
        No column-gap: the gutter is a margin on each card instead (half of --collage-gap a side).

        A container cannot restyle itself from its own query, so the 12 tracks stay for every
        tier, including the single column — and eleven real gaps are wider than a phone. As
        margins the gutter only exists where a card is.

        That leaves half a gutter at the field's own edges, which the negative margin takes back:
        the outermost cards sit exactly on the grid lines the field was given, so the gap to the
        text is the shared split and nothing more. (Which is why --collage-gap is a fixed length
        and not cqi — the field cannot size its own margin from a query on itself.)
      */
      column-gap: 0;
      margin-inline: calc(var(--collage-gap) / -2);
      row-gap: clamp(1.75rem, 3.5vw, 3.5rem);
      align-items: start;

      .preview-card {
        --collage-drop: clamp(0.75rem, 4cqi, 2.5rem);
        z-index: calc(var(--preview-card-index, 1));
        grid-column: 1 / -1;
        margin-block: 0;
      }
      /* The media has its own `min-width: auto` to defeat — see the note on 140's cards. */
      .preview-card :global(.media-item),
      .preview-card :global(.preview-images) { min-width: 0; }

      /* One column: alternate the inset so it zig-zags instead of stacking flush. */
      .preview-card.odd  { margin-inline: calc(var(--collage-gap) / 2) calc(11% + var(--collage-gap) / 2); }
      .preview-card.even { margin-inline: calc(11% + var(--collage-gap) / 2) calc(var(--collage-gap) / 2); }

      @container collage-field (min-width: 432px) {
        .preview-card.odd,
        .preview-card.even { margin-inline: calc(var(--collage-gap) / 2); }
        .preview-card.odd  { grid-column: 1 / span 6; }
        .preview-card.even { grid-column: 7 / span 6; }

        /* Downward offsets on a cycle of 8 — coprime with three columns, and against two it
           takes four rows to repeat. (.every-8-starting-K: the FIRST card carries -2.) */
        .preview-card.every-8-starting-2 { margin-block-start: calc(var(--collage-drop) * 0.5); }  /* card 1 */
        .preview-card.every-8-starting-3 { margin-block-start: calc(var(--collage-drop) * 2.5); }  /* card 2 */
        .preview-card.every-8-starting-4 { margin-block-start: 0; }                                /* card 3 */
        .preview-card.every-8-starting-5 { margin-block-start: calc(var(--collage-drop) * 1.5); }  /* card 4 */
        .preview-card.every-8-starting-6 { margin-block-start: calc(var(--collage-drop) * 3); }    /* card 5 */
        .preview-card.every-8-starting-7 { margin-block-start: calc(var(--collage-drop) * 0.5); }  /* card 6 */
        .preview-card.every-8-starting-8 { margin-block-start: calc(var(--collage-drop) * 2); }    /* card 7 */
        .preview-card.every-8-starting-1 { margin-block-start: 0; }                                /* card 8 */
      }

      @container collage-field (min-width: 520px) {
        /* Pairs of 6 + 5 with one column left empty; which side it falls on alternates. */
        .preview-card.every-8-starting-2 { grid-column: 1 / span 6; }
        .preview-card.every-8-starting-3 { grid-column: 8 / span 5; }
        .preview-card.every-8-starting-4 { grid-column: 1 / span 5; }
        .preview-card.every-8-starting-5 { grid-column: 7 / span 6; }
        .preview-card.every-8-starting-6 { grid-column: 2 / span 5; }
        .preview-card.every-8-starting-7 { grid-column: 7 / span 6; }
        .preview-card.every-8-starting-8 { grid-column: 1 / span 6; }
        .preview-card.every-8-starting-1 { grid-column: 8 / span 5; }
      }

      @container collage-field (min-width: 648px) {
        .preview-card.every-3-starting-1 { grid-column: 1 / span 4; }
        .preview-card.every-3-starting-2 { grid-column: 5 / span 4; }
        .preview-card.every-3-starting-3 { grid-column: 9 / span 4; }
      }

      @container collage-field (min-width: 870px) {
        /* 140's composition: rows of three, then three, then two */
        .preview-card.every-8-starting-2 { grid-column: 1 / span 4; }
        .preview-card.every-8-starting-3 { grid-column: 5 / span 4; }
        .preview-card.every-8-starting-4 { grid-column: 10 / span 3; }
        .preview-card.every-8-starting-5 { grid-column: 2 / span 3; }
        .preview-card.every-8-starting-6 { grid-column: 6 / span 4; }
        .preview-card.every-8-starting-7 { grid-column: 10 / span 3; }
        .preview-card.every-8-starting-8 { grid-column: 1 / span 4; }
        .preview-card.every-8-starting-1 { grid-column: 6 / span 4; }
      }

      /*
        Leftovers. A last row that is not full would otherwise hang off the left edge with a hole
        beside it, so whatever is left over is centred under the rows above. Read off the classes
        the cards already carry — no :has() on the field.
      */
      @container collage-field (min-width: 432px) and (max-width: 647.98px) {
        .preview-card.last.odd { grid-column: 4 / span 6; }
      }
      @container collage-field (min-width: 648px) {
        .preview-card.last.every-3-starting-1 { grid-column: 5 / span 4; }
        .preview-card.second-last.every-3-starting-1 { grid-column: 3 / span 4; }
        .preview-card.last.every-3-starting-2 { grid-column: 7 / span 4; }
      }

      /*
        Exactly three, two across. Two-and-an-orphan is the one count where centring does not
        help: a row of two over a single picture reads as a grid with a piece missing. Instead the
        first picture takes the left column and spans both rows, the other two stack beside it —
        one cluster, the same height on both sides.

        `:nth-last-child(3 of …)` on the first card is the count test: first AND third from the
        end means there are three.
      */
      @container collage-field (min-width: 432px) and (max-width: 647.98px) {
        .preview-card.first:nth-last-child(3 of .preview-card) {
          grid-column: 1 / span 6;
          grid-row: 1 / span 2;
          margin-block-start: 0;
          /* Centred against the pair: with three portraits the stacked two are taller than the
             one, and top-aligned that left a hole under it instead of around it. */
          align-self: center;
        }
        .preview-card.second.second-last {
          grid-column: 7 / span 6;
          grid-row: 1;
          margin-block-start: var(--collage-drop);
        }
        .preview-card.last:nth-child(3 of .preview-card) {
          grid-column: 7 / span 6;
          grid-row: 2;
          margin-block-start: 0;
        }
      }
      @container collage-field (min-width: 520px) and (max-width: 647.98px) {
        .preview-card.first:nth-last-child(3 of .preview-card) { grid-column: 1 / span 7; }
        .preview-card.second.second-last,
        .preview-card.last:nth-child(3 of .preview-card) { grid-column: 8 / span 5; }
      }

      /* One or two pictures: no composition to keep, so they get the room. */
      .preview-card.first.last {
        grid-column: 1 / -1;
        margin-inline: calc(var(--collage-gap) / 2);
        margin-block-start: 0;
      }
      @container collage-field (min-width: 432px) {
        .preview-card.first.second-last { grid-column: 1 / span 6; margin-block-start: 0; }
        .preview-card.second.last { grid-column: 7 / span 6; margin-block-start: calc(var(--collage-drop) * 2); }
      }
    }

    /*
      WHEN IT SPLITS.

      Only where the collage half still fits two pictures across (a field of 432px). Below that
      the half holds a single column, and a single column of portraits beside a paragraph is
      three times as tall as the text it sits next to — so until then the block stays stacked,
      text on top, and the collage gets the whole track (where it runs two or three across).

      This is the one decision the field's container query cannot make: whether to split is a
      property of the text and the field together, and nothing spans them both. So it is keyed
      to viewport x track instead, measured on the NARROWER half (the left one - the shared
      split's empty slot sits on that side):

        flex / extended-2   432px at ~1000px viewport   -> split from 1025
        extended-1          432px at ~1150px viewport   -> split from 1200
        inner               372px at best               -> never (not offered as a width)

      The areas are custom properties so 145 and its mirror only differ in two declarations.
    */
    --text-area: 1 / var(--track-start) / 2 / var(--track-end);
    --field-area: 2 / var(--track-start) / 3 / var(--track-end);
    --text-position: static;
    --field-offset: var(--block-spacing-m);

    .text-fields {
      grid-area: var(--text-area);
      position: var(--text-position);
      top: calc(var(--dynamic-island-bottom-edge) + var(--block-spacing-m));
      align-self: start;
    }
    .entry-previews {
      grid-area: var(--field-area);
      margin-block-start: var(--field-offset);
    }

    @media (min-width: 1025px) {
      &:is(.track-flex, .track-extended-2) {
        --text-area: var(--split-text-area);
        --field-area: var(--split-field-area);
        --text-position: sticky;
        --field-offset: 0;
      }
    }
    @media (min-width: 1200px) {
      &.track-extended-1 {
        --text-area: var(--split-text-area);
        --field-area: var(--split-field-area);
        --text-position: sticky;
        --field-offset: 0;
      }
    }

    @media (max-width: 768px) {
      --collage-tilt: 0.6;
    }
  }
  /* Shared edges: --split-left-end / --split-right-start, see BlockGrid */
  .story-layout.variant-145 {
    --split-text-area: 1 / var(--track-start) / 2 / var(--split-left-end);
    --split-field-area: 1 / var(--split-right-start) / 2 / var(--track-end);
  }
  /* Mirror: text right, collage left */
  .story-layout.variant-146 {
    --split-text-area: 1 / var(--split-right-start) / 2 / var(--track-end);
    --split-field-area: 1 / var(--track-start) / 2 / var(--split-left-end);
  }

  /* 141 — flat collage grid. Every item equal in weight (no categories, no featured slot), but
     unevenly placed. Used for the artists index, so it has to stay readable at 30+ items. */
  .story-layout.variant-141 {
    --collage-drop: clamp(1rem, 3.5vw, 3.5rem);

    /* Desktop pulls the whole composition one step in, leaving room for the rotation
       overhang: a tilted card in the outermost column would otherwise reach under the
       viewport edge. Scaled to the track so that clearance survives at any width. */
    --collage-grid-inset: 0;
    @media (min-width: 1025px) {
      --collage-grid-inset: calc(var(--track-span-cols) / 10);
    }

    .text-fields {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      grid-row: 1 / 2;
      /* Text and cards share one field here, so both take the track and step in together
         on desktop. At extended-2 the inset is 4 columns, i.e. the old extended-1. */
      grid-column: calc(var(--track-start) + var(--collage-grid-inset))
                 / calc(var(--track-end) - var(--collage-grid-inset));
    }

    .entry-previews {
      grid-row: 2 / 3;
      /*
        Named track spans, never raw column numbers. The 50-column page grid is responsive and most
        of its columns are zero-width, so a literal `6 / 46` silently means a different width at
        every breakpoint. extended-1 also leaves room for the rotation overhang — a tilted card in
        the outermost column would otherwise reach under the viewport edge.
      */
      grid-column: calc(var(--track-start) + var(--collage-grid-inset))
                 / calc(var(--track-end) - var(--collage-grid-inset));
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      column-gap: calc(var(--grid-gutter) * 1.5);
      row-gap: calc(var(--grid-gutter) * 2.5);
      margin-block-start: var(--block-spacing-m);
      align-items: start;

      @media (min-width: 769px) {
        grid-template-columns: repeat(3, minmax(0, 1fr));
      }
      @media (min-width: 1281px) {
        grid-template-columns: repeat(4, minmax(0, 1fr));
        column-gap: calc(var(--grid-gutter) * 2);
      }

      /* Few items: drop the column count rather than leave one small card adrift in a wide grid.
         After the breakpoints above, so these win at every width. */
      &:not(:has(.preview-card-2)) {
        grid-template-columns: minmax(0, 1fr);
        .preview-card { width: 100%; justify-self: stretch; }
      }
      &:has(.preview-card-2):not(:has(.preview-card-3)) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
      &:has(.preview-card-3):not(:has(.preview-card-4)) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        @media (min-width: 769px) { grid-template-columns: repeat(3, minmax(0, 1fr)); }
      }

      /* Vertical offset on a cycle of 3 against 2/3/4 columns — the row baseline never
         settles into a straight line, which is the whole point. */
      .preview-card.every-3-starting-2 { margin-block-start: var(--collage-drop); }
      .preview-card.every-3-starting-3 { margin-block-start: calc(var(--collage-drop) * 2); }

      /* Cards are narrower than their cell and sit off-centre, so the gutters stay uneven.
         Size is controlled by width only — the cards use object-fit: cover, so capping height
         would crop a tall portrait into a letterbox rather than scale it. */
      .preview-card {
        width: 92%;
        z-index: 1;
        &.even { justify-self: end; }
        &.odd  { justify-self: start; }
      }
    }

    @media (max-width: 768px) {
      --collage-tilt: 0.7;
    }
  }

  /* ————————————————————————————————————————————— 142 / 143 ————————————————————————————————————————————————————— */
  /*
    Centred layouts. Both fill the screen (100svh — the *small* viewport height, so a mobile
    browser's collapsing address bar cannot make the block jump) and put the text in the middle.

    142 "Centred Halo" is a three-by-three field: the text holds the middle cell and up to eight
    images ring it clockwise. Images nine and onward continue underneath in the same three columns
    rather than being dropped — a layout that silently hides content is worse than one that gets
    long. Note that 142 is only nominally one screen: the cards are sized by width and keep their
    aspect ratio, so three rows of them are usually taller than the viewport.

    143 "Centred Statement" is the quiet sibling: text centred on its own screen, images in tidy
    rows of four below it.
  */
  .story-layout.variant-142,
  .story-layout.variant-143 {
    /*
      The screen height belongs to the BLOCK, not to any child. `1fr auto` rows made the text row
      absorb all the slack, which pushed the images to the bottom edge of the screen; `auto auto`
      plus `align-content: center` centres the text-and-images group as one thing inside the
      screen, which is what "centred" should mean.
    */
    min-height: 100svh;
    align-content: center;
    grid-template-rows: auto auto;
    row-gap: var(--block-spacing-m);

    .text-fields {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      justify-content: center;
      grid-column: var(--track-span-extended1);
      z-index: 10;
      :global(.links) { justify-content: center; }
      /*
        Centring the container is not enough: TextField carries `:where(.text) { text-align: start }`,
        and a rule on the element itself — even a zero-specificity one — beats the centre this
        container is handing down by inheritance. So a centred layout has to say it on the text,
        which is what variants 80 and 84 do too.
      */
      :global(.field:not(.links)) {
        display: flex;
        justify-content: center;
      }
      :global(.field:not(.links) .text) {
        text-align: center;
        --text-align: center;
        margin-inline: auto;
      }
    }
  }

  /* 142 — the halo */
  .story-layout.variant-142 {
    --halo-tilt: 1;
    grid-template-rows: auto auto;

    /*
      Mobile: no clearing worth speaking of, so this is an honest stack — text, then images in two
      loose columns. The overlay only makes sense once there is room for a hole in the middle.
    */
    .text-fields  {
      grid-row: 1 / 2;
      grid-column: calc(var(--track-start) + var(--track-span-cols) / 12)
                 / calc(var(--track-end) - var(--track-span-cols) / 12);
    }
    .entry-previews {
      grid-row: 2 / 3;
      grid-column: var(--track-start) / var(--track-end);
      display: grid;
      grid-template-columns: repeat(12, minmax(0, 1fr));
      gap: var(--grid-gutter);
      align-items: center;

      .preview-card {
        grid-column: span 6;
        rotate: calc(var(--card-tilt, 0deg) * var(--halo-tilt));
        z-index: 1;
      }

      /* Same deterministic tilt cycle as the collage layouts. */
      .preview-card.every-7-starting-1 { --card-tilt: -2.5deg; }
      .preview-card.every-7-starting-2 { --card-tilt:  1.75deg; }
      .preview-card.every-7-starting-3 { --card-tilt: -1deg; }
      .preview-card.every-7-starting-4 { --card-tilt:  3deg; }
      .preview-card.every-7-starting-5 { --card-tilt: -1.75deg; }
      .preview-card.every-7-starting-6 { --card-tilt:  2.25deg; }
      .preview-card.every-7-starting-7 { --card-tilt: -3deg; }
    }

    /*
      Three columns is the whole idea: eight cards clockwise around a middle cell that holds the
      text. It only holds up while a third of the screen is still a sensible measure for a
      headline — below the breakpoint the mobile stack above stays in charge.
    */
    @media (min-width: 1281px) {
      /* This layout swaps the 51-column page grid for its own 3x3, so the track anchor's
         page-grid columns mean nothing here: 2/50 clamped to the last of three lines and
         reported the block as starting a third of the way in, which put the left width
         handle inside the block and the stripes off-centre. The whole block is the track. */
      .track-anchor { grid-column: 1 / -1; }

      /* Cards are meant to read as separate pinned-up photographs, not as a contact sheet. */
      --halo-gap: calc(var(--grid-gutter) * 3);
      /* The ring sits well inside the page edge — it is a composition, not a full-bleed grid. */
      --halo-inset: max(calc(var(--page-inline-spacing) * 2), 5cqw);
      /* ONE width for every card in the ring, the overflow included. */
      --halo-card-width: 80%;

      /*
        One grid for the text AND the cards, which is what `display: contents` on the previews
        wrapper buys. The middle row is then sized by the text itself and the rows around it by
        the cards' own height. Two separate grids could only be kept in step by giving both a
        fixed height, and a fixed height is precisely what was cropping the photographs: a card
        stretched into a 300px row showed whatever part of the image `object-fit: cover` chose,
        so a set of ordinary landscape photos came out as a set of unrelated crops.
      */
      grid-template-columns: repeat(3, minmax(0, 1fr));
      grid-template-rows: auto auto auto;
      padding-inline: var(--halo-inset);
      gap: var(--halo-gap);

      .entry-previews { display: contents; }

      .text-fields {
        grid-area: 2 / 2 / 3 / 3;
        /* Centred in the middle row, which is what makes the ring a ring however tall the cards
           beside it turn out to be. */
        align-self: center;
        /* Undoes the mobile rule's track span and the full-screen box it used to centre in. */
        height: auto;
        max-width: none;
        pointer-events: none; /* clicks pass through to any card drifting behind the text */
        :global(a) { pointer-events: auto; }
      }

      /*
        Sized by width alone. Each image keeps its original ratio unless the block pins one.
        What varies between positions is where a card sits in its cell — never its width.
      */
      .preview-card {
        width: var(--halo-card-width);
        justify-self: var(--halo-card-x, center);
        align-self: start;
        translate: 0 var(--halo-card-drop, 0);
        margin-block-end: 0;
      }

      /*
        Eight positions, clockwise from the top left, around the middle cell. The drops are small
        enough to stay inside the gap, so they loosen the ring without ever reaching the text.

        nth-child rather than the .every-N-starting-K classes the cards already carry: the
        existing sets are off by one (.every-8-starting-1 lands on the *eighth* card), which makes
        a hand-placed composition very easy to get wrong. Here card 1 is :nth-child(1) and the
        rule reads as what it does.
      */
      .preview-card:nth-child(1) { grid-area: 1 / 1 / 2 / 2; --halo-card-x: start;  --halo-card-drop: -4%; }
      .preview-card:nth-child(2) { grid-area: 1 / 2 / 2 / 3; --halo-card-x: center; --halo-card-drop: 5%; }
      .preview-card:nth-child(3) { grid-area: 1 / 3 / 2 / 4; --halo-card-x: end;    --halo-card-drop: -2%; }
      .preview-card:nth-child(4) { grid-area: 2 / 3 / 3 / 4; --halo-card-x: end;    --halo-card-drop: 4%; }
      .preview-card:nth-child(5) { grid-area: 3 / 3 / 4 / 4; --halo-card-x: end;    --halo-card-drop: 2%; }
      .preview-card:nth-child(6) { grid-area: 3 / 2 / 4 / 3; --halo-card-x: center; --halo-card-drop: -5%; }
      .preview-card:nth-child(7) { grid-area: 3 / 1 / 4 / 2; --halo-card-x: start;  --halo-card-drop: 3%; }
      .preview-card:nth-child(8) { grid-area: 2 / 1 / 3 / 2; --halo-card-x: start;  --halo-card-drop: -3%; }

      /*
        Card nine onward continues underneath in the SAME three columns, in reading order — the
        three rows above are full, so it auto-places there without any help. Same columns, same
        width and ratio rules as the ring: the block gets longer rather than turning into a
        second, differently-shaped layout halfway down.
      */
      .preview-card:nth-child(n + 9) {
        grid-column: auto;
        grid-row: auto;
        --halo-card-drop: 0;
      }
      /* Same edge alignment as the column above it, so the three columns actually line up.
         Rows one to three are full, so card nine starts a fresh row and the cycle of three holds:
         9, 12, 15 … sit in the first column, 10, 13 … in the second, 11, 14 … in the third. */
      .preview-card:nth-child(n + 9):nth-child(3n)     { --halo-card-x: start; }
      .preview-card:nth-child(n + 9):nth-child(3n + 1) { --halo-card-x: center; }
      .preview-card:nth-child(n + 9):nth-child(3n + 2) { --halo-card-x: end; }
    }
  }

  /* 143 — centred text, four-column grid beneath */
  .story-layout.variant-143 {
    /* No min-height here on purpose: giving the text its own 100svh pushed the images a whole
       screen down, leaving a huge empty gap. The block already guarantees the screen height. */
    .text-fields { grid-row: 1 / 2; grid-column: var(--track-start) / var(--track-end); }

    .entry-previews {
      grid-row: 2 / 3;
      grid-column: var(--track-start) / var(--track-end);
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: var(--grid-gutter);
      padding-block-end: var(--block-spacing-l);

      @media (min-width: 769px) {
        grid-template-columns: repeat(4, minmax(0, 1fr));
      }
    }
  }

  /* ————————————————————————————————————————————— 130 —————————————————————————————————————————————————————————— */
  /* Cover Grid: first entry preview as full-bleed background, remaining previews as a card grid */
  .story-layout.group-special.variant-130 {
    position: relative;
    grid-template-rows: auto 1fr;

    .text-fields {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      /* above .entry-previews (z-index 1), which contains the absolutely positioned background card */
      z-index: 2;
      grid-row: 1 / 2;
      padding-block-start: var(--block-spacing-l);
      @media (max-width: 1024px) {
        grid-column: 6 / 46;
      }
      @media (min-width: 1025px) {
        grid-column: var(--track-span-extended1);
      }
    }

    .entry-previews {
      /* static so the absolutely positioned background card spans the whole block */
      position: static;
      z-index: 1;
      grid-row: 2 / 3;
      display: grid;
      grid-template-columns: repeat(1, minmax(0, 1fr));
      gap: var(--grid-gutter);
      margin-block-start: var(--block-spacing-m);
      padding-block-end: var(--block-spacing-l);
      @media (max-width: 1024px) {
        grid-column: 6 / 46;
      }
      @media (min-width: 769px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
      @media (min-width: 1025px) {
        grid-column: var(--track-span-extended1);
        grid-template-columns: repeat(3, minmax(0, 1fr));
      }

      .preview-card {
        z-index: 1;
        margin-block-end: 0;
      }

      /* First preview = full-bleed background behind text and cards */
      .preview-card.first {
        position: absolute;
        inset: 0;
        z-index: 0;
        pointer-events: none;
        .preview-images {
          position: absolute;
          inset: 0;
          margin-block-end: 0;
          :global(.zoomable-image),
          :global(.video-wrapper) {
            width: 100%;
            height: 100%;
          }
          :global(.media-item) {
            width: 100%;
            height: 100%;
            max-height: none;
            object-fit: cover;
          }
        }
      }
    }
  }

  /* Custom property for preview text opacity, required for passing css variables down in animation */
  @property --preview-text-opacity {
    syntax: '<number>';
    inherits: true;
    initial-value: 1;
  }

  /* Scroll-driven transition between cards */
  @keyframes scroll-card-transition {
    /* Entering from left - scaled down while scrolling */
    0%, 40% {
      scale: 0.96;
      opacity: 0.96;
    }
    /* Perfectly centered/at rest - full scale (narrow window) */
    50% {
      scale: 1;
      opacity: 1;
    }
    /* Exiting to right - scaled down while scrolling */
    55%, 100% {
      scale: 0.96;
      opacity: 0.96;
    }
  }

  /* Fade preview texts during scroll transitions */
  @keyframes preview-texts-opacity {
    0%, 30% { --preview-text-opacity: 0; }
    40% { --preview-text-opacity: 0.2; }
    45% { --preview-text-opacity: 0.6; }
    50%, 56% { --preview-text-opacity: 1; }
    62.5% { --preview-text-opacity: 0; }
    100% { --preview-text-opacity: 0; }
  }

  :global(.story-block:first-child:has(.story-layout:is(
    .variant-67,
    .variant-72,
    .variant-73,
    .variant-74,
    .variant-81,
    .variant-82,
    .variant-83,
    .variant-85,
    .variant-86,
    .variant-88,
    .variant-89
  ))) .text-fields {
    padding-block-start: calc(var(--dynamic-island-bottom-edge) + var(--block-spacing-s));
  }
  .variant-67,
  .variant-72,
  .variant-73,
  .variant-74,
  .variant-81,
  .variant-82,
  .variant-83,
  .variant-85,
  .variant-86,
  .variant-88,
  .variant-89 {
    @media (max-width: 1024px) {
      .text-fields {
        grid-row: 1 / 2;
        padding-block: var(--block-spacing-m);
      }
      .entry-previews {
        grid-area: 1 / 1 / 3 / -1;
        .preview-card {
          .preview-texts {
            padding-block-start: var(--block-spacing-s);
            padding-block-end: calc(var(--block-spacing-l) + var(--block-spacing-s));
          }
          .preview-images {
            grid-row: 1 / 3;
          }
        }
        .preview-card.first :global(.media-item) {
          height: 100vh;
          max-height: 100vh;
        }
        /* Videos are wrapped (Safari object-fit workaround): on mobile the card is sized by
           its content, so give the wrapper the viewport height explicitly (the video inside
           covers it via object-fit, see the shared cover rules). !important because the
           shared cover rule sets height: 100%, which is circular here (card sized by content). */
        .preview-card.first :global(.video-wrapper) {
          height: 100vh !important;
          max-height: 100vh;
        }
      }
    }
  }

  .variant-78,
  .variant-79,
  .variant-90,
  .variant-91,
  .variant-92,
  .variant-93,
  .variant-95,
  .variant-96,
  .variant-98,
  .variant-99 {
    @media (max-width: 1024px) {
      /* height: 100vh; */
      grid-template-rows: 1fr auto;
      .text-fields {
        height: fit-content;
        grid-row: 2 / 3;
        align-self: flex-end;
        padding-block: var(--block-spacing-s);
        margin-block-start: 0;
        margin-block-end: 0;
      }
      .entry-previews {
        grid-area: 1 / 1 / 2 / -1;
      }
    }
  }

  :global(.story-block:first-child:has(.story-layout:is(
    .variant-100,
    .variant-101,
    .variant-102,
    .variant-103,
    .variant-104,
    .variant-105,
    .variant-106,
    .variant-107,
    .variant-108,
    .variant-109
  ))) .text-fields {
    @media (max-width: 1024px) {
      padding-block-start: calc(var(--dynamic-island-bottom-edge) + var(--block-spacing-s));
    }
  }
  .variant-100,
  .variant-101,
  .variant-102,
  .variant-103,
  .variant-104,
  .variant-105,
  .variant-106,
  .variant-107,
  .variant-108,
  .variant-109 {
    @media (max-width: 1024px) {
      grid-template-rows: auto 1fr;
      .text-fields {
        height: fit-content;
        grid-row: 1 / 2;
        align-self: flex-start;
        padding-block: var(--block-spacing-m);
        /* padding-inline: var(--page-inline-spacing); */
        margin-block-start: 0;
        margin-block-end: 0;
      }
      .entry-previews {
        grid-area: 2 / 1 / 3 / -1;
      }
    }
  }

  .variant-67,
  .variant-72,
  .variant-81,
  .variant-82,
  .variant-83,
  .variant-90,
  .variant-91,
  .variant-92,
  .variant-93,
  .variant-100,
  .variant-101,
  .variant-102,
  .variant-103 {
    .text-fields {
      align-items: center;
      :global(.text) {
        text-align: center;
        --text-align: center;
      }
      :global(.links) {
        justify-content: center;
      }
    }
  }

  /* Add extra bottom spacing for text-fields when preview texts are present to avoid collisions */
  :global(.story-block:has(.entry-previews .preview-card) .story-layout:is(
    .variant-67,
    .variant-72,
    .variant-81,
    .variant-82,
    .variant-83
  )) {
    .text-fields {
      @media (min-width: 1025px) {
        padding-block-end: calc(var(--block-spacing-l) + var(--block-spacing-m));
      }
    }
  }

  /* ── Cover: one rule for what used to be ten ──────────────────────────────────────────
     Position here means WHICH HALF of the track the text occupies, not where the track sits,
     so it reads the centred pair and meets the page's centre gutter. Verified against all 18
     left/right variants: variant-104 = 2/25, 105 = 10/25, 106 = 14/25 - one formula, no
     exceptions. Below 1025px the text is stacked over the image and takes a hand-tuned side
     span instead of half the page. */
  .story-layout.group-cover {
    --cover-mobile-side-span: 33;                        /* flex */
    &.track-extended-2 { --cover-mobile-side-span: 32; } /* interpolated: no authored variant */
    &.track-extended-1 { --cover-mobile-side-span: 31; }
    &.track-inner {
      --cover-mobile-side-span: 29;
      /* Cover text sits on a full-bleed image, so it keeps the full inner width on phones
         rather than following the global narrow-screen step down to span 20. */
      @media (max-width: 768px) { --track-span-cols: 24; }
    }

    .text-fields {
      grid-column: var(--track-centred-start) / var(--track-centred-end);
    }
    &.track-align-left .text-fields {
      @media (max-width: 1024px) { grid-column: 2 / calc(2 + var(--cover-mobile-side-span)); }
      @media (min-width: 1025px) { grid-column: var(--track-centred-start) / var(--center-gutter-track-start); }
    }
    &.track-align-right .text-fields {
      @media (max-width: 1024px) { grid-column: calc(50 - var(--cover-mobile-side-span)) / 50; }
      @media (min-width: 1025px) { grid-column: var(--center-gutter-track-end) / var(--track-centred-end); }
    }
  }

  /* 67 is group-special at track:full, so it has no --track-centred-* and keeps the literal */
  .story-layout.group-special.variant-67 .text-fields {
    @media (max-width: 1024px) { grid-column: 2 / 50; }
    @media (min-width: 1025px) { grid-column: var(--track-span-flex); }
  }

  .variant-74,
  .variant-85,
  .variant-86,
  .variant-73,
  .variant-88,
  .variant-89 {
    .text-fields {
      align-items: flex-start;
      @media (min-width: 1025px) {
        margin-block: auto;
      }
    }
  }

  
  /* Left tracks - left aligned + bottom */
  .variant-79,
  .variant-95,
  .variant-96,
  .variant-78,
  .variant-98,
  .variant-99,
  .variant-104,
  .variant-105,
  .variant-106,
  .variant-107,
  .variant-108,
  .variant-109 {
    .text-fields {
      align-items: flex-start;
    }
  }

  .variant-78,
  .variant-79,
  .variant-90,
  .variant-91,
  .variant-92,
  .variant-93,
  .variant-95,
  .variant-96,
  .variant-98,
  .variant-99 {
    @media (min-width: 1025px) {
      grid-template-rows: 1fr auto;
    }
    .text-fields {
      justify-content: flex-end;
      padding-block-end: var(--block-spacing-s);
      margin-block-end: 0;
      @media (min-width: 1025px) {
        grid-row: 2 / 3;
      }
    }
  }

  .variant-100,
  .variant-101,
  .variant-102,
  .variant-103,
  .variant-104,
  .variant-105,
  .variant-106,
  .variant-107,
  .variant-108,
  .variant-109 {
    .text-fields {
      justify-content: flex-start;
    }
  }
  
  /* ———————————————————————————————————————— PREVIEW TEXTS POSITIONING (67-109) ———————————————————————————————— */
  
  /* Center-aligned layouts - preview texts centered, bottom aligned */
  .variant-100,
  .variant-101,
  .variant-102,
  .variant-103 {
    .entry-previews .preview-card .preview-texts {
      grid-column: 1 / 3;
      grid-row: 1 / 3;
      align-items: center;
      justify-content: flex-end;
      padding-block: var(--block-spacing-s);
      padding-inline: var(--page-inline-spacing);
      :global(.preview-title),
      .preview-description {
        text-align: center;
        --text-align: center;
      }
    }
  }

  /* Center-aligned layouts - preview texts bottom-right aligned on desktop */
  .variant-67,
  .variant-72,
  .variant-81,
  .variant-82,
  .variant-83 {
    .entry-previews .preview-card .preview-texts {
      grid-column: 1 / 3;
      align-items: center;
      justify-content: flex-end;
      padding-inline: var(--page-inline-spacing);
      :global(.preview-title),
      .preview-description {
        text-align: center;
        --text-align: center;
      }
      @media (max-width: 1025px) {
        grid-row: 2 / 3;
      }
      @media (min-width: 1025px) {
        grid-column: 2 / 3;
        grid-row: 2 / 3;
        align-items: flex-end;
        padding-block-end: var(--block-spacing-m);
        :global(.preview-title),
        .preview-description,
        .preview-preamble {
          text-align: right;
        }
      }
    }
  }
  
  .variant-74,
  .variant-85,
  .variant-86,
  .variant-104,
  .variant-105,
  .variant-106 {
    .entry-previews .preview-card .preview-texts {
      grid-column: 1 / 3;
      @media (min-width: 1025px) {
        grid-column: 2 / 3;
      }
      grid-row: 1 / 3;
      align-items: flex-end;
      justify-content: flex-end;
      padding-block: var(--block-spacing-m);
      padding-inline: var(--page-inline-spacing);
      :global(.preview-title),
      .preview-description,
      .preview-preamble {
        text-align: right;
      }
    }
  }
  
  .variant-73,
  .variant-88,
  .variant-89,
  .variant-107,
  .variant-108,
  .variant-109 {
    .entry-previews .preview-card .preview-texts {
      grid-column: 1 / 3;
      @media (min-width: 1025px) {
        grid-column: 1 / 2;
      }
      grid-row: 1 / 3;
      align-items: flex-start;
      justify-content: flex-end;
      padding-block: var(--block-spacing-m);
      padding-inline: var(--page-inline-spacing);
    }
  }
  
  .variant-90,
  .variant-91,
  .variant-92,
  .variant-93 {
    .entry-previews .preview-card .preview-texts {
      grid-column: 1 / 3;
      grid-row: 1 / 3;
      align-items: center;
      justify-content: flex-start;
      padding-block: var(--block-spacing-m);
      padding-inline: var(--page-inline-spacing);
      :global(.preview-title),
      .preview-description {
        text-align: center;
      }
    }
  }
  
  .variant-79,
  .variant-95,
  .variant-96 {
    .entry-previews .preview-card .preview-texts {
      grid-column: 1 / 3;
      @media (min-width: 1025px) {
        grid-column: 2 / 3;
      }
      grid-row: 1 / 3;
      align-items: flex-end;
      justify-content: flex-start;
      padding-block: var(--block-spacing-m);
      padding-inline: var(--page-inline-spacing);
      :global(.preview-title),
      .preview-description,
      .preview-preamble {
        text-align: right;
      }
    }
  }
  
  .variant-78,
  .variant-98,
  .variant-99 {
    .entry-previews .preview-card .preview-texts {
      grid-column: 1 / 3;
      @media (min-width: 1025px) {
        grid-column: 1 / 2;
      }
      grid-row: 1 / 3;
      align-items: flex-start;
      justify-content: flex-start;
      padding-block: var(--block-spacing-m);
      padding-inline: var(--page-inline-spacing);
    }
  }
  
 
  
  /* ———————————————————————————————————————— PREVIEW TEXTS SPACING ADJUSTMENTS ———————————————————————————————— */
  
  /* First-child blocks: Add dynamic-island spacing for top-aligned variants only */
  :global(.story-block:first-child:has(.story-layout:is(
    .variant-78,
    .variant-79,
    .variant-90,
    .variant-91,
    .variant-92,
    .variant-93,
    .variant-95,
    .variant-96,
    .variant-98,
    .variant-99,
    .variant-100,
    .variant-101,
    .variant-102,
    .variant-103,
    .variant-104,
    .variant-105,
    .variant-106,
    .variant-107,
    .variant-108,
    .variant-109
  ))) .entry-previews .preview-card .preview-texts {
      padding-block-start: calc(var(--dynamic-island-bottom-edge) + var(--block-spacing-m)) !important;
  }
  
  
  /* Bottom-aligned preview texts: Add extra bottom padding to avoid collision with carousel markers */
  .variant-90,
  .variant-91,
  .variant-92,
  .variant-93,
  .variant-100,
  .variant-101,
  .variant-102,
  .variant-103,
  .variant-104,
  .variant-105,
  .variant-106,
  .variant-107,
  .variant-108,
  .variant-109 {
    .entry-previews .preview-card .preview-texts {
      padding-block-end: calc(var(--block-spacing-m) + 42px);
      @media (min-width: 1025px) {
        padding-block-end: calc(var(--block-spacing-m) + 46px);
      }
    }
  }

  /* ═══════════════════════════════════════════════════════════════════════════════════════════════
     CAROUSEL SPLIT LAYOUTS (111, 113-115, 117-120)
     Two-column layouts with text on one side and carousel on the other
     ═══════════════════════════════════════════════════════════════════════════════════════════════ */
  
  .variant-111, .variant-113, .variant-114, .variant-115,
  .variant-117, .variant-118, .variant-119, .variant-120 {
    /* Height of one full-bleed panel. The first block on a page starts below the dynamic
       island's grid row (see MainPageContent), so a plain 100vh would push the panel - and
       with it the carousel markers anchored to its bottom edge - past the fold. The
       first-child rule below subtracts that offset (and any block spacing above it). */
    --carousel-split-height: 100vh;
    /* The text column makes room for the sticky island the panel slides under: its top padding
       and its height both leave this much. The first-child rule zeroes it - that panel starts
       below the island's grid row and nothing covers its top. */
    --carousel-split-island-inset: var(--dynamic-island-bottom-edge);
    min-height: var(--carousel-split-height);
    /* Full-bleed panel: block spacing has to be margin, padding would inset the images. */
    padding-block: 0;
    margin-block-start: var(--block-spacing-top);
    margin-block-end: var(--block-spacing-bottom);
    
    @media (min-width: 1025px) {
      grid-template-rows: 1fr;
    }
    
    .text-fields {
      z-index: 2;
      display: flex;
      flex-direction: column;
      padding-block: var(--block-spacing-m);
      padding-inline: var(--page-inline-spacing);
      align-items: flex-start;
      
      @media (min-width: 1025px) {
        grid-row: 1 / 2;
        padding-block-start: calc(var(--carousel-split-island-inset) + var(--block-spacing-m));
        padding-block-end: var(--block-spacing-m);
      }
    }
    
    .entry-previews {
      z-index: 1;
      display: flex;
      /* Single card: never a scroll container, or it eats the iOS swipe-back gesture.
         See the note on the cover carousel above. */
      overflow: clip;
      scrollbar-width: none;
      -ms-overflow-style: none;
      &::-webkit-scrollbar { display: none; }
      
      @media (min-width: 1025px) {
        grid-row: 1 / 2;
        height: var(--carousel-split-height);
        position: sticky;
        top: 0;
      }
      
      .preview-card {
        display: grid;
        grid-template-columns: 1fr;
        grid-template-rows: 1fr auto;
        scroll-snap-align: center;
        margin-block-end: 0 !important;
        
        @media (min-width: 1025px) {
          min-width: 50vw;
          max-width: 50vw;
        }
        
        .preview-images { grid-column: 1; grid-row: 1 / 3; }
        
        .preview-texts {
          grid-column: 1;
          grid-row: 2;
          z-index: 2;
          display: flex;
          flex-direction: column;
          gap: var(--size-2);
          padding: var(--block-spacing-s);
          pointer-events: none;
          :global(*) { pointer-events: auto; }
        }
        
        /* No object-fit here: Image.svelte covers by default and contains when the preview is
           set to fit. */
        :global(.media-item) { width: 100%; height: 100%; }
        /* The generic viewport cap (same selector, under .story-layout) is an island short of
           this panel; the strip it left under the image is what the markers used to straddle. */
        :global(.media-item:not(.fit-to-viewport)) { max-height: none; }
      }
      /* Becomes a carousel once there is a second card - see SHARED CAROUSEL. */
    }
    
    /* Left-aligned (text left, carousel right) */
    &.align-left {
      @media (min-width: 1025px) {
        .text-fields { grid-column: 1 / var(--center-gutter-track-start); }
        .entry-previews { grid-column: var(--center-gutter-track-end) / -1; }
      }
    }
    
    /* Right-aligned (text right, carousel left) */
    &.align-right {
      @media (min-width: 1025px) {
        .text-fields { grid-column: var(--center-gutter-track-end) / -1; }
        .entry-previews { grid-column: 1 / var(--center-gutter-track-start); }
      }
    }
    
    /* Track widths (text column padding) */
    &.track-flex .text-fields { @media (min-width: 1025px) { padding-inline: var(--block-spacing-m); } }
    &.track-extended-2 .text-fields { @media (min-width: 1025px) { padding-inline: var(--block-spacing-l); } }
    &.track-extended-1 .text-fields { @media (min-width: 1025px) { padding-inline: calc(var(--block-spacing-l) + var(--block-spacing-m)); } }
    &.track-inner .text-fields { @media (min-width: 1025px) { padding-inline: calc(var(--block-spacing-l) * 2); } }
    
    /* Vertical text alignment - spread: title at top, description/links at bottom */
    .text-fields {
      justify-content: flex-start;
      @media (min-width: 1025px) {
        justify-content: space-between;
        height: calc(var(--carousel-split-height) - var(--carousel-split-island-inset) - var(--block-spacing-m) - var(--block-spacing-m));
      }
      :global(.field.description) { @media (min-width: 1025px) { margin-block-start: auto; } }
      :global(.field.links) { @media (min-width: 1025px) { margin-block-start: auto; } }
      :global(.field.description + .field.links) { margin-block-start: 0; }
    }
    
    /* Mobile */
    @media (max-width: 1024px) {
      min-height: auto;
      grid-template-rows: auto 1fr;
      
      .text-fields { grid-row: 1 / 2; grid-column: 1 / -1; }
      .entry-previews {
        grid-row: 2 / 3;
        grid-column: 1 / -1;
        height: 70vh;
        .preview-card { min-width: 100vw; max-width: 100vw; }
      }
    }
  }
  
  /* First-child blocks: Add dynamic-island spacing on mobile */
  :global(.story-block:first-child:has(.story-layout:is(
    .variant-111, .variant-113, .variant-114, .variant-115,
    .variant-117, .variant-118, .variant-119, .variant-120
  ))) .text-fields {
    @media (max-width: 1024px) {
      padding-block-start: calc(var(--dynamic-island-bottom-edge) + var(--block-spacing-m));
    }
  }

  /* First-child blocks: the panel starts below the dynamic island's grid row, so it only has
     `100vh - island` of viewport left. Without this the block overhangs the fold by exactly
     that offset - which is the gap under the carousel and the reason its scroll markers,
     anchored to the bottom edge of .entry-previews, sit off screen. The island is sticky and
     opaque, so shrinking the panel is right here; sliding under it (as the cover layouts do)
     would just hide the top of the image. */
  :global(.story-block:first-child) > .story-layout:is(
    .variant-111, .variant-113, .variant-114, .variant-115,
    .variant-117, .variant-118, .variant-119, .variant-120
  ) {
    --carousel-split-height: calc(100vh - var(--dynamic-island-bottom-edge) - var(--block-spacing-top));
    --carousel-split-island-inset: 0px;
  }

  /* ═══════════════════════════════════════════════════════════════════════════════════════════════
     SHARED CAROUSEL (cover, carousel split, two column carousel, expanding carousel)
     ═══════════════════════════════════════════════════════════════════════════════════════════════ */

  /* Turn .entry-previews into a scroll-snap carousel with scroll markers - only once there is a
     second card to reach; each layout's own `overflow: clip` covers the single-card case.
     The .story-layout prefix is for specificity: cover's base rule is nested under a selector
     list that weighs (0,3,0), and this has to outrank its `overflow: clip`. */
  .story-layout:is(.group-cover, .variant-67, .variant-111, .variant-113, .variant-114, .variant-115, .variant-117, .variant-118, .variant-119, .variant-120, .variant-144, .inset-carousel) .entry-previews:has(.preview-card-2) {
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;
    scroll-behavior: smooth;
    overscroll-behavior-x: contain;
    anchor-name: --carousel-anchor;
    scroll-marker-group: after;

    --marker-size: 42px;
    
    &::scroll-marker-group {
      --carousel-controls-hovered: 0;
      position-visibility: always;
      position: absolute;
      position-anchor: --carousel-anchor;
      bottom: anchor(bottom);
      left: anchor(center);
      z-index: 1000;
      /* Glass, for markers that sit on an image. Layouts that place them on the flat block
         background swap both values - see "Markers below the carousel". */
      backdrop-filter: var(--marker-group-backdrop, blur(20px) brightness(1.2) saturate(1.2));
      background-color: var(--marker-group-background, oklch(from var(--color-background, var(--color-default-background)) l c h / 0.05));
      border-radius: var(--round);
      padding: 2px;
      translate: -50% calc(-1 * var(--block-spacing-xs));
      @media (min-width: 1025px) {
        padding: 4px;
      }
      display: flex;
      align-items: center;
      justify-content: center;
      height: var(--marker-size);
      opacity: 0;
      animation: fade-in 0.3s ease-in-out 0.5s forwards;
    }
    
    .preview-card::scroll-marker {
      --carousel-controls-hovered: 0;
      content: "";
      --marker-visual-size: 12px;
      --marker-width: var(--marker-visual-size);
      --marker-padding: calc((var(--marker-size) - var(--marker-visual-size)) / 2);
      --marker-radius: calc(var(--marker-size) / 2);
      --marker-color: oklch(from var(--color-foreground, var(--color-default-foreground)) l c h / 0.7);
      height: var(--marker-visual-size);
      width: var(--marker-width);
      display: block;
      z-index: 2000;
      padding-inline: var(--marker-padding);
      padding-block: var(--marker-padding);
      box-sizing: content-box;
      background: var(--marker-color) content-box;
      border-radius: var(--marker-radius);
      transition: width 0.3s ease-in-out;
    }
    
    .preview-card::scroll-marker:target-current {
      --marker-width: calc(var(--marker-visual-size) * 3);
    }

    &::scroll-marker-group:hover,
    .preview-card::scroll-marker:hover {
      --carousel-controls-hovered: 1;
    }
  }

  /* Markers below the carousel instead of over it: these cards are not full-bleed images, so
     there is no image edge to sit inside - the carousel reserves the room underneath. */
  .story-layout:is(.inset-carousel, .variant-144) .entry-previews:has(.preview-card-2) {
    /* On the flat background the glass has nothing to blur, and its brightness/saturate boost
       just repaints the background in a different colour. A faint foreground tint is the
       background's own colour, a step darker (or lighter), whatever that background is. */
    --marker-group-backdrop: none;
    --marker-group-background: oklch(from var(--color-foreground, var(--color-default-foreground)) l c h / 0.06);
    @supports selector(::scroll-marker-group) {
      margin-block-end: calc(var(--marker-size) + 8px + 2 * var(--block-spacing-xs));
    }
    &::scroll-marker-group {
      top: anchor(bottom);
      bottom: auto;
      translate: -50% var(--block-spacing-xs);
    }
  }

  /* Expanding carousel: the existing block is the runway, and its existing
     StoryBlock grid is the sticky stage. All geometry comes from CSS. */
  @property --expanding-page-y {
    syntax: '<length>';
    inherits: true;
    initial-value: 0px;
  }
  @property --expanding-block-y {
    syntax: '<length>';
    inherits: true;
    initial-value: 0px;
  }
  @property --expanding-growth {
    syntax: '<number>';
    inherits: true;
    initial-value: 0;
  }
  @property --expanding-arc-x1 {
    syntax: '<number>';
    inherits: true;
    initial-value: .1;
  }

  :global(.story-block:has(> .variant-144)) {
    --expanding-island: var(--dynamic-island-bottom-edge, 0px);
    background-color: var(--color-background, var(--color-default-background));
  }
  :global(.main-page-content:has(.dynamic-island .row-2:not(:empty)) .story-block:has(> .variant-144)) {
    --expanding-island: calc(var(--dynamic-island-bottom-edge, 0px) + var(--dynamic-island-height, 0px));
  }

  .story-layout.variant-144 {
    --expanding-image-height: min(calc(100vw / (var(--common-aspect-ratio, 3 / 2))), calc(100svh - var(--expanding-island)));
    display: grid;
    grid-template-rows: auto auto;
    min-width: 0;
    padding-block: 0;
    margin-block: var(--block-spacing-top) var(--block-spacing-bottom);

    .entry-previews {
      grid-area: 1 / 1 / 2 / -1;
      display: flex;
      align-items: stretch;
      width: 100%;
      min-width: 0;
      overflow: clip;
      scrollbar-width: none;
      &::-webkit-scrollbar { display: none; }

      /* Carousel activation and the marker placement below it are shared - see SHARED CAROUSEL */
      &:has(.preview-card-2) {
        &::scroll-marker-group {
          animation: none;
          opacity: 1;
          visibility: visible;
        }
      }

      .preview-card {
        flex: 0 0 100%;
        position: relative;
        min-width: 0;
        display: flex;
        flex-direction: column;
        scroll-snap-align: center;
        margin-block-end: 0;
      }
      .preview-card .preview-images {
        flex: 0 0 auto;
        width: 100%;
        height: var(--expanding-image-height);
        aspect-ratio: var(--common-aspect-ratio, 3 / 2);
        margin-block-end: 0;
        overflow: clip;
        transform-origin: top center;
        :global(.zoomable-image) { width: 100%; height: 100%; min-height: 0; }
        :global(.media-item) { width: 100%; height: 100%; max-height: none; max-width: none; }
        :global(.video-wrapper) { width: 100%; height: 100%; aspect-ratio: auto !important; max-height: none; }
        :global(.video-wrapper > .media-item) {
          width: 100% !important;
          height: 100% !important;
          max-width: none !important;
          max-height: none !important;
          object-fit: cover !important;
        }
        :global(.video-wrapper > .media-item.fit-to-frame) { object-fit: contain !important; }
      }
      .preview-texts {
        display: flex;
        flex-direction: column;
        align-self: center;
        align-items: center;
        opacity: var(--preview-text-opacity, 1);
        gap: var(--size-2);
        width: 100%;
        box-sizing: border-box;
        padding: var(--block-spacing-s) var(--page-inline-spacing);
        text-align: center;
        :is(.preview-preamble, .preview-title, .preview-description) {
          display: block;
          -webkit-line-clamp: unset;
          line-clamp: none;
          max-height: none;
          overflow: visible;
        }
        :global(.preview-button) { width: fit-content; }
      }
    }
    .text-fields {
      grid-area: 2 / 1 / 3 / -1;
      display: flex;
      flex-direction: column;
      align-items: center;
      box-sizing: border-box;
      min-width: 0;
      width: 100%;
      padding: var(--block-spacing-s) var(--page-inline-spacing);
      --text-align: center;
      :global(.text) { text-align: center; }
      :global(.links) { justify-content: center; }
    }

    @media (min-width: 1025px) {
      &:has(.preview-images) {
      grid-template-rows: auto;
      .entry-previews:has(.preview-card-2) {
        margin-block-end: 0;
        /* Back on the image here, so back to the glass */
        --marker-group-backdrop: initial;
        --marker-group-background: initial;
        &::scroll-marker-group {
          top: auto;
          bottom: anchor(bottom);
          translate: -50% calc(-1 * var(--block-spacing-xs));
        }
      }
      .preview-card:has(.preview-images) .preview-texts {
        position: absolute;
        inset: 0 0 auto;
        justify-content: flex-start;
        padding-block: var(--block-spacing-m);
        z-index: 2;
        pointer-events: none;
        :global(*) { pointer-events: auto; }
        .preview-preamble { --line-clamp: 1; }
        .preview-title, .preview-description { --line-clamp: 2; }
        :is(.preview-preamble, .preview-title, .preview-description) {
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: var(--line-clamp);
          line-clamp: var(--line-clamp);
          overflow: hidden;
        }
      }
      .text-fields {
        grid-row: 1;
        grid-column: var(--track-span-extended1);
        align-self: end;
        justify-content: flex-end;
        z-index: 2;
        max-height: var(--expanding-image-height);
        overflow-y: auto;
        padding: 0 0 var(--block-spacing-s);
        margin-block: 0;
        :global(.title:not(.last) .text) { margin-block-end: calc(.5 * var(--font-size)); }
        :global(.title.last .text) { margin-block-end: 0; }
      }
      &:has(.preview-card-2) .text-fields {
        @supports selector(::scroll-marker-group) {
          padding-block-end: max(var(--block-spacing-s), calc(42px + 2 * var(--block-spacing-xs) + 8px));
        }
      }
      }
    }
  }

  @supports (animation-timeline: --expanding) and (animation-range: contain 0px contain 1px) and (opacity: calc(1px / 1px)) {
    @media (prefers-reduced-motion: no-preference) {
      :global(.story-block:not(.is_editing):has(> .variant-144 .preview-images)) {
        --expanding-responsive: clamp(0, calc((100vw - 390px) / 890px), 1);
        --expanding-desired-scale: calc(.55 + .09 * var(--expanding-responsive));
        --expanding-natural-gap: max(0px, calc(var(--expanding-page-y) - var(--expanding-block-y)));
        --expanding-inset: 34svh;
        --expanding-travel: calc(100svh * (1 - .28 * var(--expanding-responsive)));
        --expanding-hold: 15svh;
        --expanding-range-start: calc(-1 * var(--expanding-inset));
        --expanding-range-end: calc(var(--expanding-travel) - var(--expanding-inset));
        --expanding-reveal-start: calc(.25 * var(--expanding-travel) - var(--expanding-inset));
        --expanding-reveal-end: calc(.55 * var(--expanding-travel) - var(--expanding-inset));
        --expanding-arc-x1: calc(var(--expanding-inset) / var(--expanding-travel) / 4);
        margin-block-end: calc(-1 * var(--expanding-inset));
        min-height: calc(100svh - var(--expanding-island) + 1px);
        view-timeline: --expanding block;
        view-timeline-inset: var(--expanding-island) 0px;
        /* Two linear CSS timelines cancel the scroll offset, leaving the block's
           natural distance below the island. The wide ranges keep this value
           unclamped throughout the intro, including restored scroll positions. */
        animation: expanding-page-y auto both linear, expanding-block-y auto both linear, expanding-growth auto both cubic-bezier(.42, 0, .58, 1);
        animation-timeline: scroll(root block), --expanding, --expanding;
        animation-range: 0px 100000px, contain -100000px contain 100000px, contain var(--expanding-range-start) contain var(--expanding-range-end);
      }
      :global(.story-block:not(.is_editing):has(> .variant-144 .preview-images)::after) {
        content: '';
        grid-column: 1 / -1;
        grid-row: 2;
        height: calc(var(--expanding-travel) + var(--expanding-hold));
        pointer-events: none;
      }
      :global(.story-block:not(.is_editing):first-child:has(> .variant-144 .preview-images)) {
        --expanding-inset: 0px;
      }
      :global(.story-block:not(.is_editing)) > .story-layout.variant-144:has(.preview-images) {
        /* A carousel further down the page fits its local arrival, rather than
           shrinking according to its distance from the top of the document. */
        --expanding-below-fold: clamp(0, calc((var(--expanding-natural-gap) - (100svh - var(--expanding-island)) + 1px) / 1px), 1);
        --expanding-initial-gap: calc(var(--expanding-natural-gap) * (1 - var(--expanding-below-fold)) + var(--expanding-inset) * var(--expanding-below-fold));
        --expanding-start: min(var(--expanding-desired-scale), max(.001, calc((100svh - var(--expanding-island) - var(--expanding-initial-gap) - 40px) / var(--expanding-image-height))));
        --expanding-scale: calc(var(--expanding-start) + (1 - var(--expanding-start)) * var(--expanding-growth));
        --expanding-below-shift: calc((var(--expanding-scale) - 1) * var(--expanding-image-height));
        position: sticky;
        top: calc(var(--expanding-island) + var(--expanding-inset));
        align-self: start;
        animation: expanding-arrive auto both cubic-bezier(var(--expanding-arc-x1), .25, .65, 1);
        animation-timeline: --expanding;
        animation-range: contain var(--expanding-range-start) contain var(--expanding-range-end);

        .preview-images { transform: scale(var(--expanding-scale)); }
        .preview-images :global(img.media-item) { object-fit: contain; }
        @supports (width: attr(width px)) {
          .preview-images :global(img.media-item[width][height]:not([width="0"]):not([height="0"]):not(.fit-to-frame)) {
            /* Start with the whole source image. Resize its inner box into the
               existing cover frame; the carousel's own geometry stays fixed. */
            --expanding-source-ratio: calc(attr(width type(<number>)) / attr(height type(<number>)));
            --expanding-contained-width: min(100vw, calc(var(--expanding-image-height) * var(--expanding-source-ratio)));
            --expanding-contained-height: min(var(--expanding-image-height), calc(100vw / var(--expanding-source-ratio)));
            --expanding-crop-progress: clamp(0, calc((var(--expanding-growth) - .25) / .75), 1);
            width: calc(var(--expanding-contained-width) + (100vw - var(--expanding-contained-width)) * var(--expanding-crop-progress));
            height: calc(var(--expanding-contained-height) + (var(--expanding-image-height) - var(--expanding-contained-height)) * var(--expanding-crop-progress));
            margin-inline: auto;
            align-self: start;
            object-fit: cover;
          }
        }
        .text-fields, .preview-texts {
          translate: 0 var(--expanding-below-shift);
          opacity: 0;
          visibility: hidden;
          animation: expanding-reveal auto both cubic-bezier(.25, 0, .35, 1), expanding-expose auto both step-end;
          animation-timeline: --expanding;
          animation-range: contain var(--expanding-reveal-start) contain var(--expanding-reveal-end), contain var(--expanding-range-start) contain var(--expanding-reveal-start);
        }
        .entry-previews:has(.preview-card-2)::scroll-marker-group {
          transform: translateY(var(--expanding-below-shift));
          opacity: 0;
          visibility: hidden;
          animation: expanding-reveal auto both cubic-bezier(.25, 0, .35, 1), expanding-expose auto both step-end;
          animation-timeline: --expanding;
          animation-range: contain var(--expanding-reveal-start) contain var(--expanding-reveal-end), contain var(--expanding-range-start) contain var(--expanding-reveal-start);
        }
        @media (min-width: 1025px) {
          .preview-texts { translate: none; }
        }
      }
    }
  }
  @keyframes expanding-page-y { from { --expanding-page-y: 0px; } to { --expanding-page-y: 100000px; } }
  @keyframes expanding-block-y { from { --expanding-block-y: -100000px; } to { --expanding-block-y: 100000px; } }
  @keyframes expanding-growth { from { --expanding-growth: 0; } to { --expanding-growth: 1; } }
  @keyframes expanding-arrive { from { transform: translateY(0); } to { transform: translateY(calc(-1 * var(--expanding-inset))); } }
  @keyframes expanding-reveal { from { opacity: 0; } to { opacity: var(--preview-text-opacity, 1); } }
  @keyframes expanding-expose { from { visibility: hidden; } to { visibility: visible; } }

  /* ═══════════════════════════════════════════════════════════════════════════════════════════════
     FULL-SCREEN SCROLL LAYOUTS (301, 311)

     Base: vertical gallery fallback (Firefox and browsers without scroll-driven animations).
     Cards flow naturally at 100svh each; all content visible; no pagination.

     Enhanced (@supports animation-timeline): scroll-driven cross-fade with sticky stacking,
     view-timeline segments, and animated pagination dots.

     Heights that take up space in the page flow use --screen-height, never dvh: dvh changes
     every time a mobile browser shows or hides its toolbar (on iOS Safari that happens when the
     scroll direction changes), and this block is many viewports tall, so everything below it
     jumped by a multiple of the toolbar height. Plain svh is not enough either: in-app browsers
     (Instagram …) resize the whole web view with their toolbars, svh included - see
     screen_height.js. Only boxes that don't affect the flow (the sticky card, the pagination)
     use dvh.
     ═══════════════════════════════════════════════════════════════════════════════════════════════ */

  .story-layout:is(.variant-301, .variant-311) {
    grid-template-rows: auto auto;
    padding-block: 0;
    margin-block-start: var(--block-spacing-top);
    margin-block-end: var(--block-spacing-bottom);

    .text-fields {
      display: flex;
      flex-direction: column;
      grid-row: 1;
      grid-column: var(--dynamic-island-track-start) / var(--dynamic-island-track-end);
      padding-block: var(--block-spacing-m);
    }

    .entry-previews {
      grid-column: 1 / -1;
      grid-row: 2;
      display: grid;
      grid-template-columns: 1fr;
      min-height: var(--screen-height, 100svh);

      .preview-card {
        height: var(--screen-height, 100svh);
        display: grid;
        grid-template-columns: 1fr;
        grid-template-rows: 1fr;
        margin-block-end: 0 !important;
        overflow: hidden;

        .preview-images {
          grid-area: 1 / 1;
          z-index: 0;
          margin-block-end: 0;
          min-height: 0;
          overflow: hidden;

          :global(.media-item) {
            width: 100%;
            height: 100%;
            object-fit: cover;
            max-height: none;
          }
        }

        .preview-texts {
          grid-area: 1 / 1;
          z-index: 1;
          display: flex;
          flex-direction: column;
          gap: var(--size-2);
          pointer-events: none;
          align-items: center;
          justify-content: end;
          text-align: center;
          padding: var(--size-4);
          padding-block-start: 50dvh;
          padding-block-end: max(var(--size-14), env(safe-area-inset-bottom, 0px) + var(--size-10));
          max-height: min(100%, 100dvh);

          :global(*) { pointer-events: auto; }
          .preview-preamble { --line-clamp: 2; }
          .preview-title { --line-clamp: 2; }
          .preview-description { --line-clamp: 3; }
          :global(.preview-button) { width: fit-content; }
        }
      }

      .fss-pagination { display: none; }
    }

    :global(.story-block.is_editing) & {
      grid-template-rows: auto auto;

      .entry-previews {
        grid-column: 1 / -1;
        grid-row: 2;
        height: auto;
        grid-template-columns: 1fr;
        grid-template-rows: auto;

        .preview-card {
          position: relative;
          width: 100%;
          height: auto;
          max-height: 100dvh;
          aspect-ratio: 3/4;

          .preview-images,
          .preview-texts {
            animation: none;
            opacity: 1;
            padding-block-start: var(--size-4);
          }
        }
      }
    }

    @media (min-width: 1024px) {
      .entry-previews .preview-card .preview-texts {
        justify-content: center;
        padding-block-end: var(--size-10);
      }
    }

    @media (min-width: 1024px) and (min-aspect-ratio: 16/10) {
      .entry-previews .preview-card {
        grid-template-columns: 1fr 1fr;

        .preview-images {
          grid-column: 1 / 2;
          grid-row: 1;
        }
        .preview-texts {
          grid-column: 2 / 3;
          grid-row: 1;
          justify-content: center;
          align-items: start;
          text-align: left;
          padding-block-start: var(--size-6);
          padding-inline: var(--block-spacing-m);
          padding-block-end: var(--size-6);
          .preview-description { --line-clamp: 12; }
        }
      }
    }
  }

  .story-layout.variant-311 {
    @media (min-width: 1024px) and (min-aspect-ratio: 16/10) {
      .entry-previews .preview-card {
        .preview-images {
          grid-column: 2 / 3;
          grid-row: 1;
        }
        .preview-texts {
          grid-column: 1 / 2;
          grid-row: 1;
        }
      }
    }
  }

  /* ---------------------------------------------------------------------------
     Scroll-driven enhancement (Chromium 115+, Safari 18+)
     Browsers without animation-timeline get the vertical gallery above.
     --------------------------------------------------------------------------- */
  @supports (animation-timeline: view()) {
    .story-layout:is(.variant-301, .variant-311) {
      --fss-speed: 1.2;

      @media (min-width: 1024px) {
        --fss-speed: 2;
      }

      .entry-previews {
        grid-template-rows: 1fr;
        height: calc((var(--entry-previews-count) / var(--fss-speed) + 1) * var(--screen-height, 100svh));
        view-timeline: --fss-timeline block;

        .preview-card {
          grid-area: 1 / 1;
          position: sticky;
          top: 0;
          height: 100dvh;
          align-self: start;
          z-index: 0;
          animation: fss-card-z linear both;
          animation-timeline: --fss-timeline;
          animation-range: contain var(--segment-start) contain var(--segment-end);

          --segment-start: calc((var(--preview-card-index) - 1) / var(--preview-card-count) * 100%);
          --segment-end: calc(var(--preview-card-index) / var(--preview-card-count) * 100%);

          .preview-images {
            animation: fss-image-fade linear both;
            animation-timeline: --fss-timeline;
            animation-range: contain var(--segment-start) contain var(--segment-end);
          }

          .preview-texts {
            animation: fss-text-fade linear both;
            animation-timeline: --fss-timeline;
            animation-range: contain var(--segment-start) contain var(--segment-end);
          }

          &.first {
            --segment-end: calc(1.5 / var(--preview-card-count) * 100%);
            animation-name: fss-card-z-first;
            animation-range: entry 0% contain var(--segment-end);

            .preview-images {
              animation: fss-image-first linear both;
              animation-timeline: --fss-timeline;
              animation-range: entry 0% contain var(--segment-end);
            }
            .preview-texts {
              animation: fss-text-first linear both;
              animation-timeline: --fss-timeline;
              animation-range: entry 0% contain var(--segment-end);
            }
          }

          &.last {
            animation-name: fss-card-z-last;

            .preview-images {
              animation: fss-image-last linear both;
              animation-timeline: --fss-timeline;
              animation-range: contain var(--segment-start) contain var(--segment-end);
            }
            .preview-texts {
              animation: fss-text-last linear both;
              animation-timeline: --fss-timeline;
              animation-range: contain var(--segment-start) contain var(--segment-end);
            }
          }

          &:only-child {
            animation: none;
            z-index: auto;

            .preview-images,
            .preview-texts {
              animation: none;
              opacity: 1;
              visibility: visible;
            }
          }
        }

        /* --- Pagination dots --- */
        .fss-pagination {
          --dot-size: 8px;
          --dot-pill-height: 22px;
          --dot-gap: 12px;
          --dot-color-inactive: oklch(from var(--color-foreground, var(--color-default-foreground)) l c h / 0.35);
          --dot-color-active: oklch(from var(--color-foreground, var(--color-default-foreground)) l c h / 0.9);
          --dot-step: calc(var(--dot-size) + var(--dot-gap));

          display: flex;
          grid-area: 1 / 1;
          position: sticky;
          top: 0;
          height: 100dvh;
          align-self: start;
          pointer-events: none;
          z-index: 20;
          overflow: hidden;

          flex-direction: column;
          align-items: flex-end;
          padding-inline-end: var(--size-6);
          padding-top: calc(50dvh - var(--dot-step) / 2);

          mask-image: linear-gradient(
            to bottom,
            transparent calc(50% - 2.5 * var(--dot-step)),
            black calc(50% - 1.5 * var(--dot-step)),
            black calc(50% + 1.5 * var(--dot-step)),
            transparent calc(50% + 2.5 * var(--dot-step))
          );

          .fss-dot {
            width: var(--dot-size);
            height: var(--dot-size);
            margin-block: calc(var(--dot-gap) / 2);
            flex-shrink: 0;
            border-radius: 999px;
            background: var(--dot-color-inactive);

            --segment-start: calc((var(--dot-index) - 1) / var(--entry-previews-count) * 100%);
            --segment-end: calc(var(--dot-index) / var(--entry-previews-count) * 100%);
            --pill-margin: calc((var(--dot-size) + var(--dot-gap) - var(--dot-pill-height)) / 2);

            animation-name: fss-dot-wheel, fss-dot-active;
            animation-duration: auto, auto;
            animation-timing-function: linear, linear;
            animation-fill-mode: both, both;
            animation-timeline: --fss-timeline, --fss-timeline;
            animation-range:
              contain 0% contain 100%,
              contain var(--segment-start) contain var(--segment-end);

            &:first-child {
              animation-name: fss-dot-wheel, fss-dot-active-first;
            }
            &:last-child {
              animation-name: fss-dot-wheel, fss-dot-active-last;
            }
          }
        }
      }

      :global(.story-block.is_editing) & {
        .entry-previews {
          height: auto;
          view-timeline: none;

          .preview-card {
            grid-area: auto;
            position: relative;
            z-index: auto;
            animation: none;

            .preview-images,
            .preview-texts {
              animation: none;
              opacity: 1;
              visibility: visible;
            }
          }
        }
      }
    }

    @keyframes fss-image-fade {
      0% { opacity: 0; visibility: hidden; }
      15% { opacity: 1; visibility: visible; }
      85% { opacity: 1; visibility: visible; }
      100% { opacity: 0; visibility: hidden; }
    }
    @keyframes fss-image-first {
      0%   { opacity: 1; visibility: visible; }
      85%  { opacity: 1; visibility: visible; }
      100% { opacity: 0; visibility: hidden; }
    }
    @keyframes fss-image-last {
      0%   { opacity: 0; visibility: hidden; }
      30%  { opacity: 1; visibility: visible; }
      100% { opacity: 1; visibility: visible; }
    }

    @keyframes fss-text-fade {
      0%   { opacity: 0; visibility: hidden; transform: translateY(var(--size-6)); }
      15%  { opacity: 0; visibility: hidden; transform: translateY(var(--size-4)); }
      25%  { opacity: 1; visibility: visible; transform: translateY(0); }
      75%  { opacity: 1; visibility: visible; transform: translateY(0); }
      85%  { opacity: 0; visibility: hidden; transform: translateY(calc(-1 * var(--size-4))); }
      100% { opacity: 0; visibility: hidden; transform: translateY(calc(-1 * var(--size-6))); }
    }
    @keyframes fss-text-first {
      0%       { opacity: 1; visibility: visible; transform: translateY(0); }
      75%      { opacity: 1; visibility: visible; transform: translateY(0); }
      85%      { opacity: 0; visibility: hidden; transform: translateY(calc(-1 * var(--size-4))); }
      100%     { opacity: 0; visibility: hidden; transform: translateY(calc(-1 * var(--size-6))); }
    }
    @keyframes fss-text-last {
      0%   { opacity: 0; visibility: hidden; transform: translateY(var(--size-6)); }
      15%  { opacity: 0; visibility: hidden; transform: translateY(var(--size-4)); }
      30%  { opacity: 1; visibility: visible; transform: translateY(0); }
      100% { opacity: 1; visibility: visible; transform: translateY(0); }
    }

    /* Active card gets z-index: 1 so it's on top of the stack and receives events.
       Inactive cards stay at z-index: 0 behind the active one. */
    @keyframes fss-card-z {
      0%, 100% { z-index: 0; }
      10%, 90% { z-index: 1; }
    }
    @keyframes fss-card-z-first {
      0%, 90% { z-index: 1; }
      100% { z-index: 0; }
    }
    @keyframes fss-card-z-last {
      0% { z-index: 0; }
      10%, 100% { z-index: 1; }
    }

    @keyframes fss-dot-wheel {
      from { translate: 0 0; }
      to { translate: 0 calc(-1 * (var(--entry-previews-count) - 1) * var(--dot-step)); }
    }
    @keyframes fss-dot-active {
      0% {
        height: var(--dot-size);
        background: var(--dot-color-inactive);
        transform: translateY(0);
      }
      20%, 80% {
        height: var(--dot-pill-height);
        background: var(--dot-color-active);
        transform: translateY(var(--pill-offset));
      }
      100% {
        height: var(--dot-size);
        background: var(--dot-color-inactive);
        transform: translateY(0);
      }
    }
    @keyframes fss-dot-active-first {
      0%, 80% {
        height: var(--dot-pill-height);
        background: var(--dot-color-active);
        transform: translateY(var(--pill-offset));
      }
      100% {
        height: var(--dot-size);
        background: var(--dot-color-inactive);
        transform: translateY(0);
      }
    }
    @keyframes fss-dot-active-last {
      0% {
        height: var(--dot-size);
        background: var(--dot-color-inactive);
        transform: translateY(0);
      }
      20%, 100% {
        height: var(--dot-pill-height);
        background: var(--dot-color-active);
        transform: translateY(var(--pill-offset));
      }
    }
  }

  /* ——— File-drop target + in-flight upload placeholders ——— */
  .story-layout.file-drop-active {
    outline: 2px dashed var(--editing);
    outline-offset: -2px;
    border-radius: var(--radius-md);
  }

  .media-upload-placeholder {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    min-height: 120px;
    aspect-ratio: var(--common-aspect-ratio, 3 / 2);
    border: 1px dashed var(--color-default-stroke);
    border-radius: var(--radius-s);
    overflow: hidden;
    background: var(--color-default-background-100, var(--color-default-background));
    /* Gentle enter: the placeholder appearing IS the drop feedback */
    transition-property: opacity, translate;
    transition-duration: 200ms;
    transition-timing-function: cubic-bezier(0.2, 0, 0, 1);

    @starting-style {
      opacity: 0;
      translate: 0 4px;
    }

    &.failed {
      border-color: var(--color-system-destructive, #b3382f);
    }

    .placeholder-preview {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      opacity: 0.75;
    }

    .placeholder-status {
      position: relative;
      display: flex;
      flex-direction: column;
      gap: var(--size-1);
      padding: var(--size-2);
      background: color-mix(in oklch, var(--color-default-background) 82%, transparent);
      backdrop-filter: blur(4px);

      .status-text {
        font-variant-numeric: tabular-nums; /* live percentages must not jitter */
      }

      .status-text.optimizing {
        animation: placeholder-shimmer 1.6s ease-in-out infinite;
      }

      .placeholder-progress {
        width: 100%;
        accent-color: var(--editing);
      }

      .placeholder-actions {
        display: flex;
        gap: var(--size-2);
      }
    }
  }

  @keyframes placeholder-shimmer {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.45; }
  }

  @media (prefers-reduced-motion: reduce) {
    .media-upload-placeholder .placeholder-status .status-text.optimizing {
      animation: none;
    }
  }
</style>
